import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stage, Layer, Image, Rect, Group, Line as KonvaLine } from 'react-konva';
import useImage from 'use-image';
import { getCanvasImageCrossOriginMode } from '../../../utils/canvasImageLoader';
import { buildMergedCombatStageUpdate } from '../../../utils/combat/CombatStageUpdateBuilder';
import { fetchDefaultBuiltinRulesetSelection } from '../../../rules/rulesetSelections';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import Toolbar from './Toolbar';
import FogControls from './FogControls';
import Grid from './Grid';
import Token from './Token';
import CoLocatedTokenBadge from './CoLocatedTokenBadge';
import SceneAssetNode from './SceneAssetNode';
import SceneAssetInspector from './SceneAssetInspector';
import LayerContextMenu from '../../LayerContextMenu/LayerContextMenu';
import TokenPropertiesPopup from '../../TokenPropertiesPopup/TokenPropertiesPopup';
import MultiattackTargetingPopup from '../../MultiattackTargetingPopup/MultiattackTargetingPopup';
import AssetBrowser from '../../AssetBrowser/AssetBrowser';
import AoETargetingOverlay from './AoETargetingOverlay';
import FogOfWarMask from './FogOfWarMask'; // date_of_change: 2025-09-24 - Fog of war overlay
import SharedPingLayer from './SharedPingLayer'; // Shared, expiring map attention marker — date_of_change: 2026-08-11
import SceneLifecycleDialog from './SceneLifecycleDialog';
import RealtimeStatusNotice from './RealtimeStatusNotice';
import VisionBarrierLayer from './VisionBarrierLayer';
import VisibilityMaskLayer from './VisibilityMaskLayer';
import VisionBarrierControls from './VisionBarrierControls';
import BattleRoomInterruptions from './BattleRoomInterruptions';
import UserInfoTopbar from './UserInfoTopbar'; // @date_of_change: 2025-10-01 - User info and sign out
import MeasureOverlay from './MeasureOverlay';
import DrawingCanvas from './DrawingCanvas'; // GAP-002: drawing layer overlay — date_of_change: 2026-05-20
import Minimap from './Minimap'; // GAP-004: minimap navigation — date_of_change: 2026-05-20
import StatusBar from './StatusBar'; // GAP-011: status bar — date_of_change: 2026-05-20
import useBattleRoom from '../../../hooks/battle-room-v2/useBattleRoom';
import './battleroom.css';
import '../../../styles/notifications.css'; // Import notification styles
import NotificationManager from '../../../utils/NotificationManager';
import UnifiedDataManager from '../../../services/UnifiedDataManager';
import { SpellHandler } from '../../../handlers/SpellHandler';
import CombatManager from '../../../utils/CombatManager';
import RangeCalculator from '../../../utils/RangeCalculator';
import CombatLogManager from '../../../utils/CombatLogManager';
import ActionEconomyManager from '../../../utils/ActionEconomyManager';
import {
  buildFamiliarOriginAttacker,
  distanceFeetBetweenTokens,
  findFamiliarReappearancePosition,
  isFamiliarPresent,
} from '../../../utils/FamiliarActions';
import { normalizeAreaOfEffect } from '../../../utils/areaOfEffectUtils';
import { movementTracker, canEnterTokenSpace } from '../../../utils/MovementUtils';
import ErrorBoundary from '../ErrorBoundary';
import { requestChoice } from '../../../utils/DiceProvider';
// date_of_change: 2025-09-04 - New UI Layout System
import { BattleRoomLayout, useKeyboardShortcuts as useLayoutKeyboardShortcuts } from '../../../ui/layout';
import useBattleRoomCompactViewport from '../../../ui/layout/useBattleRoomCompactViewport';
// date_of_change: 2026-05-24 - Import battle room keyboard shortcuts for tool undo/shortcuts
import useBattleKeyboardShortcuts from '../../../hooks/battle-room-v2/modules/useKeyboardShortcuts';
// @date_of_change: 2025-09-07 - Import centralized constants and utilities
 
// @date_of_change: 2025-09-07 - Import state management and attack handling utilities
import { useBattleRoomLayers, useBattleRoomUIState } from '../../../utils/BattleRoomStateManager';
import { AttackHandler } from '../../../utils/combat/AttackHandler';
// @date_of_change: 2025-09-10 - PHASE ONE: Import ActionEconomyProcessor for complete combat integration
import ActionEconomyProcessor from '../../../utils/combat/ActionEconomyProcessor';
import { buildCombatActionFailureFeedback, formatCombatActionError } from '../../../utils/combat/CombatActionError';
import { buildCombatActionCatalog } from '../../../utils/combat/CombatActionCatalog';
import {
  buildCanonicalMovementTokenUpdate,
  preserveLocalCombatRuntime,
  resetTurnScopedCombatFlags,
  selectCanonicalCombatantToken
} from '../../../utils/battleRoom/BattleRoomCombatantSelectors';
import { executeCombatActionRequest } from '../../../utils/combat/CombatExecutionRouter';
import { normalizeCombatActionRequest } from '../../../utils/combat/CombatActionRequestNormalizer';
import {
  buildObjectInteractionAction,
  isSceneObjectToken,
  isUseObjectAction
} from '../../../utils/combat/ObjectInteractionTargeting';
import {
  buildCombatExecutionBundle,
  extractPrimaryProcessorOutcome
} from '../../../utils/combat/CombatExecutionRequestBuilder';
import {
  resolveCombatantSpellData,
  resolveSpellLevel
} from '../../../utils/combat/CombatantNormalizer';
import { Grid as GridIcon, Magnet, ZoomIn, ZoomOut, RefreshCw, Ruler, FolderOpen, Download, MapPin, Layers3, Tag, EyeOff, CloudFog } from 'lucide-react';
// @date_of_change: 2025-09-27 - Import single source of truth for role
import { useCombatRole, useWebSocket } from '../../../contexts/WebSocketContext';
import ManualRollModal from './ManualRollModal';
import ChoiceModal from './ChoiceModal';
import InteractionIntentModal from './InteractionIntentModal';
import MapArea from './MapArea';
import { setRequestD20, setRequestChoice, setRequestDice, getDiceAuto, setDiceAuto } from '../../../utils/DiceProvider';
import { resolveUploadAssetUrl } from '../../../utils/uploadAssetUrlResolver';
import { uploadAssetToS3, validateAssetFile, createAssetFromS3Upload } from '../../../services/S3AssetUploader';
import { buildSceneAssetCreatePayload } from '../../../utils/battleRoom/SceneAssetPlacement';
import { restoreSnapshotTokenCollection } from '../../../utils/battleRoom/BattleRoomSnapshotRehydrator';
import { applyGmHitPointAdjustment } from '../../../utils/battleRoom/GmHitPointAdjustment';
import { getRealtimeMutationFeedback } from '../../../utils/battleRoom/RealtimeMutationFeedback';
import { buildBattleRoomSessionInitiativeSnapshot } from '../../../utils/battleRoom/BattleRoomSessionLoadPolicy';
import {
  authoritativeInitiativeSignature,
  buildAuthoritativeInitiativeHydration,
} from '../../../utils/battleRoom/AuthoritativeInitiativeHydration';
import { buildCoLocatedTokenLayout, resolveTokenGridCell } from '../../../utils/battleRoom/CoLocatedTokenLayout';
import {
  normalizeBattleLayerId,
  getTokenLayerId,
  canViewerSeeToken,
  getViewerVisibleTokens,
  getTokensForBattleLayer,
  isTokenOnBattleLayer
} from '../../../utils/battleRoomLayerUtils';
import { DEBUG_ENABLED } from '../../../utils/DebugLogger';
import { apiFetch } from '../../../utils/apiFetch';

const createBattleRoomOperationId = (operation) => {
  const id = typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `${operation}:${id}`;
};

const readBattleRoomApiResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error?.message || payload?.error || fallbackMessage);
    error.code = payload?.error?.code;
    error.details = payload?.error?.details;
    throw error;
  }
  return payload;
};

const readBattleRoomLocalEntityMap = (entityType) => {
  try {
    const storageKey = UnifiedDataManager?.storageKey || 'ascendant_unified_entities';
    const rawData = localStorage.getItem(storageKey);
    if (!rawData) return {};
    const parsed = JSON.parse(rawData);
    return parsed?.[`${entityType}s`] || {};
  } catch (_) {
    return {};
  }
};

const rehydrateIncomingBattleRoomTokens = (tokens, templateMap = {}) => (
  restoreSnapshotTokenCollection({
    tokens: Array.isArray(tokens) ? tokens : [],
    templateMap: templateMap && typeof templateMap === 'object' ? templateMap : {},
    canonicalCharacterEntities: readBattleRoomLocalEntityMap('character'),
    canonicalMonsterEntities: readBattleRoomLocalEntityMap('monster')
  })
);

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

const TOKEN_LABEL_VISIBILITY_STORAGE_KEY = 'battleRoomShowTokenLabels';

const readStoredTokenLabelVisibility = () => {
  try {
    return typeof window === 'undefined'
      || window.localStorage.getItem(TOKEN_LABEL_VISIBILITY_STORAGE_KEY) !== 'false';
  } catch (_) {
    return true;
  }
};

const TOUCH_CONTEXT_MENU_DELAY_MS = 500;
const TOUCH_CONTEXT_MENU_MOVE_TOLERANCE_PX = 12;
const MAP_PING_HOLD_DELAY_MS = 500;
const MAP_TOUCH_FOCUS_HOLD_DELAY_MS = 1250;
const MAP_ATTENTION_MOVE_TOLERANCE_PX = 12;
const MAP_ATTENTION_CLICK_SUPPRESSION_MS = 900;

const createAuthorizedGmOverride = (overrideIntent, isGameMaster) => {
  if (!isGameMaster || (!overrideIntent?.enabled && !overrideIntent?.ignoreActionEconomy)) return null;
  return ActionEconomyProcessor.createGMOverride({
    note: String(overrideIntent.note || overrideIntent.gmNote || '').trim() || 'GM action-economy override',
    ignoreActionEconomy: true,
    ignoreDataValidation: false,
    ignoreHandlerValidation: false
  });
};

const buildDashboardSessionLoadRequestKey = (locationState) => {
  if (!locationState?.fromDashboard) {
    return null;
  }

  if (locationState.loadSessionId) {
    return `session-id:${locationState.loadSessionId}`;
  }

  if (locationState.loadSessionFile) {
    const file = locationState.loadSessionFile;
    return `session-file:${file?.name || 'unnamed'}:${file?.size || 0}:${file?.lastModified || 0}`;
  }

  if (locationState.loadSessionData) {
    const sessionData = locationState.loadSessionData;
    const battleState = sessionData?.battleState || sessionData?.data?.battleState || {};
    const tokenCount = Array.isArray(battleState.tokens) ? battleState.tokens.length : 0;
    return `session-data:${sessionData?.version || 'legacy'}:${sessionData?.timestamp || sessionData?.savedAt || 'no-timestamp'}:${tokenCount}`;
  }

  return null;
};

const stripDashboardSessionLoadState = (locationState) => {
  if (!locationState || typeof locationState !== 'object') {
    return null;
  }

  const {
    loadSessionData,
    loadSessionFile,
    loadSessionId,
    expectedFileName,
    fromDashboard,
    sessionName,
    forceGM,
    ...restState
  } = locationState;

  return Object.keys(restState).length > 0 ? restState : null;
};

const extractSessionLoadFile = (fileOrEvent) => {
  if (!fileOrEvent) {
    return null;
  }

  if (typeof File !== 'undefined' && fileOrEvent instanceof File) {
    return fileOrEvent;
  }

  const candidateFile = fileOrEvent?.target?.files?.[0] || fileOrEvent?.dataTransfer?.files?.[0] || null;
  if (typeof File !== 'undefined' && candidateFile instanceof File) {
    return candidateFile;
  }

  return null;
};

const withoutHitPointPatch = (props) => {
  if (!props || typeof props !== 'object') return props;
  const { hpObj, hp, maxHp, currentHp, hitPoints, tempHp, ...remaining } = props;
  ['entity', 'entityPayload', 'monsterData', 'characterData', 'canonicalData', 'data'].forEach((key) => {
    if (!remaining[key] || typeof remaining[key] !== 'object' || Array.isArray(remaining[key])) return;
    const {
      hpObj: _hpObj,
      hp: _hp,
      maxHp: _maxHp,
      currentHp: _currentHp,
      hitPoints: _hitPoints,
      tempHp: _tempHp,
      ...safeNested
    } = remaining[key];
    remaining[key] = safeNested;
  });
  return remaining;
};

const structuredDamageComponents = (outcome, prefix = 'damage') => {
  const damage = outcome?.damage;
  if (!damage) return [];
  const details = Array.isArray(damage?.details) ? damage.details : [];
  if (details.length > 0) {
    return details.map((detail, index) => ({
      componentId: `${prefix}:${index}`,
      damageType: String(detail?.damageType || detail?.type || damage?.type || 'untyped').toLowerCase(),
      rolledAmount: Math.max(0, Number(detail?.originalTotal ?? detail?.rolledAmount ?? detail?.total ?? 0) || 0),
      ...(detail?.formula || detail?.dice ? { formula: detail.formula || detail.dice } : {}),
      ...(Array.isArray(detail?.rolls) ? { rolls: detail.rolls } : {}),
      ...([0, 0.5, 1].includes(detail?.saveMultiplier) ? { saveMultiplier: detail.saveMultiplier } : {}),
    })).filter((component) => component.rolledAmount > 0);
  }
  const amount = Number(damage?.originalTotal ?? damage?.rolledAmount ?? damage?.total ?? damage);
  return Number.isFinite(amount) && amount > 0 ? [{
    componentId: `${prefix}:0`,
    damageType: String(damage?.type || outcome?.damageType || 'untyped').toLowerCase(),
    rolledAmount: amount,
    ...(damage?.formula ? { formula: damage.formula } : {}),
    ...(Array.isArray(damage?.rolls) ? { rolls: damage.rolls } : {}),
    ...([0, 0.5, 1].includes(damage?.saveMultiplier) ? { saveMultiplier: damage.saveMultiplier } : {}),
  }] : [];
};

const STAGE_VIEWPORT_PADDING = 24;
const MIN_STAGE_ZOOM = 0.05;
const MAX_STAGE_ZOOM = 6;
const DEFAULT_STAGE_SIZE = { width: 0, height: 0 };

const clampStageZoom = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 1;
  }
  return Math.min(Math.max(numericValue, MIN_STAGE_ZOOM), MAX_STAGE_ZOOM);
};

const getPinchViewportMetrics = (touches, containerRect) => {
  if (!touches || touches.length !== 2 || !containerRect) {
    return null;
  }

  const firstTouch = touches[0];
  const secondTouch = touches[1];
  const firstX = firstTouch.clientX - containerRect.left;
  const firstY = firstTouch.clientY - containerRect.top;
  const secondX = secondTouch.clientX - containerRect.left;
  const secondY = secondTouch.clientY - containerRect.top;
  const center = {
    x: (firstX + secondX) / 2,
    y: (firstY + secondY) / 2
  };

  return {
    center,
    distance: Math.hypot(secondX - firstX, secondY - firstY)
  };
};

const getMapImageDimensions = (image) => {
  const width = Number(image?.naturalWidth || image?.width || 0);
  const height = Number(image?.naturalHeight || image?.height || 0);
  if (width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
};

const loadSceneAssetImageSize = (source) => new Promise((resolve, reject) => {
  const image = new window.Image();
  const crossOrigin = getCanvasImageCrossOriginMode(source);
  if (crossOrigin) image.crossOrigin = crossOrigin;
  image.onload = () => {
    const size = getMapImageDimensions(image);
    if (size) resolve(size);
    else reject(new Error('The selected image has no usable dimensions.'));
  };
  image.onerror = () => reject(new Error('The selected image could not be decoded.'));
  image.src = source;
});

const isPlaceholderMapAsset = (value) => (
  typeof value === 'string' && value.includes('/assets/placeholders/default-grid.svg')
);

const getGridPixelSize = ({ gridSize, gridWidth, gridHeight }) => {
  const safeGridSize = Math.max(Number(gridSize) || 0, 1);
  const safeGridWidth = Math.max(parseInt(gridWidth, 10) || 0, 10);
  const safeGridHeight = Math.max(parseInt(gridHeight, 10) || 0, 10);
  return {
    width: safeGridWidth * safeGridSize,
    height: safeGridHeight * safeGridSize
  };
};

const getPreferredMapSize = ({ imageSize, fallbackSize = DEFAULT_STAGE_SIZE }) => {
  const width = Number(imageSize?.width) || Number(fallbackSize?.width) || 0;
  const height = Number(imageSize?.height) || Number(fallbackSize?.height) || 0;
  if (width <= 0 || height <= 0) {
    return null;
  }
  return {
    width: Math.max(Math.round(width), 1),
    height: Math.max(Math.round(height), 1)
  };
};

const getMinimumGridCoverage = ({ mapSize, gridSize }) => {
  const safeGridSize = Math.max(Number(gridSize) || 0, 1);
  const mapWidth = Math.max(Number(mapSize?.width) || 0, 0);
  const mapHeight = Math.max(Number(mapSize?.height) || 0, 0);

  return {
    columns: Math.max(Math.ceil(mapWidth / safeGridSize), 10),
    rows: Math.max(Math.ceil(mapHeight / safeGridSize), 10)
  };
};

const buildFittedStageViewport = ({
  stageSize,
  mapState,
  padding = STAGE_VIEWPORT_PADDING,
  preferReadableMap = false
}) => {
  const stageWidth = Number(stageSize?.width) || 0;
  const stageHeight = Number(stageSize?.height) || 0;
  const mapWidth = Number(mapState?.size?.width) || 0;
  const mapHeight = Number(mapState?.size?.height) || 0;
  const mapX = Number(mapState?.position?.x) || 0;
  const mapY = Number(mapState?.position?.y) || 0;

  if (stageWidth <= 0 || stageHeight <= 0 || mapWidth <= 0 || mapHeight <= 0) {
    return null;
  }

  const availableWidth = Math.max(stageWidth - (padding * 2), 1);
  const availableHeight = Math.max(stageHeight - (padding * 2), 1);
  const fitZoom = Math.min(availableWidth / mapWidth, availableHeight / mapHeight);
  // A full-map fit makes a square arena unreadable in a short landscape viewport.
  // Compact devices begin width-focused; players can still fit, pinch, or pan freely.
  const readableZoom = availableWidth / mapWidth;
  const zoom = clampStageZoom(preferReadableMap ? Math.max(fitZoom, readableZoom) : fitZoom);

  return {
    zoom,
    position: {
      x: ((stageWidth - (mapWidth * zoom)) / 2) - (mapX * zoom),
      y: ((stageHeight - (mapHeight * zoom)) / 2) - (mapY * zoom)
    }
  };
};

const shouldRepairMapSize = ({ mapSize, imageSize, stageSize, gridSize, gridWidth, gridHeight }) => {
  const currentWidth = Number(mapSize?.width) || 0;
  const currentHeight = Number(mapSize?.height) || 0;
  const imageWidth = Number(imageSize?.width) || 0;
  const imageHeight = Number(imageSize?.height) || 0;
  const stageWidth = Number(stageSize?.width) || 0;
  const stageHeight = Number(stageSize?.height) || 0;

  if (currentWidth <= 0 || currentHeight <= 0) {
    return true;
  }

  if (imageWidth <= 0 || imageHeight <= 0 || stageWidth <= 0 || stageHeight <= 0) {
    return false;
  }

  const currentAspect = currentWidth / currentHeight;
  const imageAspect = imageWidth / imageHeight;
  const aspectDelta = Math.abs(currentAspect - imageAspect) / imageAspect;
  const currentArea = currentWidth * currentHeight;
  const stageArea = Math.max(stageWidth * stageHeight, 1);
  const tinyArea = currentArea < (stageArea * 0.2);
  const suspiciouslySmall = currentWidth < 160 || currentHeight < 160;
  const shrunkFarBelowImage = currentWidth < (imageWidth * 0.25) || currentHeight < (imageHeight * 0.25);
  const gridPixelWidth = Math.max((Number(gridWidth) || 0) * (Number(gridSize) || 0), 0);
  const gridPixelHeight = Math.max((Number(gridHeight) || 0) * (Number(gridSize) || 0), 0);
  const matchesGridBounds = gridPixelWidth > 0 && gridPixelHeight > 0 &&
    Math.abs(currentWidth - gridPixelWidth) < 2 &&
    Math.abs(currentHeight - gridPixelHeight) < 2;
  const matchesViewportBounds = Math.abs(currentWidth - stageWidth) < 2 &&
    Math.abs(currentHeight - stageHeight) < 2;

  if (matchesViewportBounds && (aspectDelta > 0.08 || shrunkFarBelowImage)) {
    return true;
  }

  if (matchesGridBounds && (tinyArea || suspiciouslySmall || shrunkFarBelowImage)) {
    return true;
  }

  return (tinyArea || suspiciouslySmall) && (shrunkFarBelowImage || aspectDelta > 0.12);
};

/**
 * BattleRoom Component - Main Virtual Tabletop Interface
 * Updated with ResizablePanel components, fixed top bar alignment, and restored toolbar functionality
 * 
 * This is the core component that provides a complete virtual tabletop experience
 * similar to Roll20 or Foundry VTT. It manages everything needed for online D&D sessions:
 * 
 * MAIN FEATURES:
 * - Interactive battle map with zoom, pan, and grid controls
 * - Token management (characters, monsters, NPCs) with drag/drop and resizing
 * - Layer system (Map Layer for backgrounds, Token Layer for characters, GM Layer for hidden items)
 * - Initiative tracking for turn-based combat
 * - Character/monster property management
 * - Session save/load functionality for preserving battle states
 * - Asset deletion and management
 * 
 * LAYOUT STRUCTURE:
 * - Left Sidebar: Action Panel (combat actions), Drawing Tools
 * - Center: Interactive map canvas with tokens and grid
 * - Right Sidebar: Character Sheet, Initiative Tracker, Token Properties, Chat
 * - Top Toolbar: Layer controls, grid settings, zoom, save/load, asset upload
 * 
 * DATA FLOW:
 * - Uses useBattleRoom hook for all state management
 * - Integrates with DataService for D&D 5e character/monster templates
 * - Supports session persistence via JSON file download/upload
 * - Real-time updates between all panels when tokens are selected or modified
 * 
 * CRITICAL LAYERING ORDER (DO NOT CHANGE):
 * Layer contents must be in this exact order for proper rendering:
 * 1. MapArea (background map image)
 * 2. Grid (grid lines visible on top of map)
 * 3. Tokens (interactive tokens on top of grid)
 * 
 * WORKING FEATURES:
 * - Map upload and display
 * - Token upload, drag, resize with grid snapping
 * - Grid display with size adjustment and visibility toggle
 * - Zoom controls with proper grid scaling
 * - Left/right panel layout with all controls
 * - Responsive canvas sizing
 * 
 * INTEGRATION POINTS:
 * - useBattleRoom hook provides all state and handlers
 * - Toolbar receives grid/zoom props for controls
 * - Stage scaling affects both canvas and grid calculations
 * - Drop handling for token placement from external sources
 */
const buildRulesetExecutionState = (authoritativeState, serviceSessionData) => {
  const source = authoritativeState || {};
  const serviceState = serviceSessionData?.state || serviceSessionData?.combatState || {};
  return {
    ...(source.ruleset || serviceState.ruleset ? { ruleset: source.ruleset || serviceState.ruleset } : {}),
    round: source.round ?? serviceState.round ?? CombatLogManager.currentRound,
    turn: source.turn ?? serviceState.turn ?? CombatLogManager.currentTurn,
    random: source.random ?? serviceState.random,
    resolutionRevision: source._stateRevision,
    ...(source.rulesetBinding || serviceState.rulesetBinding ? { rulesetBinding: source.rulesetBinding || serviceState.rulesetBinding } : {}),
    ...(source.rulesetRuntime || serviceSessionData?.rulesetRuntime ? { rulesetRuntime: source.rulesetRuntime || serviceSessionData.rulesetRuntime } : {})
  };
};

export const BattleRoomBase = ({
  sessionId,
  activeCampaignId = null,
  renderUserInfoTopbar = null,
  onTestStateChange = null,
  testMode = false,
  serviceSessionData = null
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // date_of_change: 2025-09-04 - Enable keyboard shortcuts for new layout system
  useLayoutKeyboardShortcuts();

  // @date_of_change: 2025-09-27 - Use WebSocket role as single source of truth
  const { isGM: isGameMasterFromWS, role } = useCombatRole();

  // @date_of_change: 2025-10-04 - Get current user ID to prevent applying own token updates
  const { currentUser } = useWebSocket();
  
  // @date_of_change: 2025-09-30 - Add WebSocket for multiplayer map synchronization
  // Also pull currentSessionId so BattleRoom doesn't rely on a missing prop
  // @date_of_change: 2025-10-05 - Added sendInitiativeUpdate for initiative tracker sync
  // @date_of_change: 2025-10-05 - Added sendWebSocketMessage for CombatLogManager integration
  const {
    sendInitiativeUpdate,
    sendWebSocketMessage,
    isConnected: isWebSocketConnected,
    currentSessionId: wsSessionId,
    sessionStatus: wsSessionStatus,
    updateRole,
    clearSessionState,
    addEventListener,
    sendBattleRoomMutation,
    requestBattleRoom,
    stateRevision,
    activeSceneId,
    pendingMutationCount,
    combatState: authoritativeCombatState,
    waitForBattleRoomMutationQueue
  } = useWebSocket();
  const isLocalBattleRoom = !sessionId && !wsSessionId;
  const rulesetExecutionState = useMemo(
    () => buildRulesetExecutionState(authoritativeCombatState, serviceSessionData),
    [authoritativeCombatState, serviceSessionData]
  );
  const authoritativeCombatStateRef = useRef(authoritativeCombatState);
  authoritativeCombatStateRef.current = authoritativeCombatState;
  
  // @date_of_change 2025-09-06 - Lint fix: alias ActionEconomyManager.useAction to avoid React Hook linting error
  // Use bracket notation so eslint react-hooks doesn't misidentify this as a Hook call
  const consumeActionEconomy = useCallback((...args) => ActionEconomyManager['useAction'](...args), []);
  
  const [stageSize, setStageSize] = useState(DEFAULT_STAGE_SIZE);
  // These controls outlive compact/mobile drawer mounts. Their state belongs
  // to the room, not to a transient panel. date_of_change: 2026-07-27
  const [gmActionOverride, setGmActionOverride] = useState({ enabled: false, note: '' });
  const [combatLogEditMode, setCombatLogEditMode] = useState(false);
  // Token labels are a viewer preference, not authoritative combat state.
  // Persisting locally lets each participant choose their own map density.
  // date_of_change: 2026-08-25
  const [showTokenLabels, setShowTokenLabels] = useState(readStoredTokenLabelVisibility);
  const handleToggleTokenLabels = useCallback(() => {
    setShowTokenLabels((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(TOKEN_LABEL_VISIBILITY_STORAGE_KEY, String(next));
      } catch (_) {}
      return next;
    });
  }, []);

  // Stage position state for panning/scrolling
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const stageContainerRef = useRef(null);
  const stagePositionRef = useRef({ x: 0, y: 0 });
  const stageZoomRef = useRef(1);
  const pinchSessionRef = useRef(null);
  const viewportAdjustedRef = useRef(false);
  const lastViewportMapKeyRef = useRef('');
  const repairedMapSignatureRef = useRef('');
  const panSessionRef = useRef(null);
  const importedSessionProtectionRef = useRef({ expiresAt: 0 });
  const touchContextMenuHoldRef = useRef(null);
  const suppressNextTokenTapRef = useRef(false);
  const suppressNextTokenTapTimerRef = useRef(null);
  const mapAttentionHoldRef = useRef(null);
  const mapAttentionSuppressUntilRef = useRef(0);
  const selectionDragRef = useRef(null);

  const measureStageContainer = useCallback(() => {
    const container = stageContainerRef.current;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = Math.max(Math.floor(rect.width), 0);
    const height = Math.max(Math.floor(rect.height), 0);

    if (width <= 0 || height <= 0) {
      return;
    }

    setStageSize((prev) => (
      prev.width === width && prev.height === height
        ? prev
        : { width, height }
    ));
  }, []);
  
  
  
  // date_of_change: 2025-10-06 - Use middle mouse (button=1) for panning to avoid interfering with left-drag actions
  // date_of_change: 2026-04-07 - Manual camera panning replaces Konva stage dragging to prevent jitter/refocus loops
  const [isPanning, setIsPanning] = useState(false);

  // Add hoveredToken state for tracking which token the cursor is over
  const [hoveredToken, setHoveredToken] = useState(null);

  // Session loading ref to prevent double triggering in React Strict Mode
  const sessionLoadingHandledRef = useRef(false);
  const sessionLoadRequestKeyRef = useRef(null);
  
  // Handlers for tracking token hover state
  const handleTokenMouseEnter = useCallback((id) => {
    setHoveredToken(id);
    debugLog(`[BattleRoom] Token hover: ${id}`);
  }, []);

  const handleTokenMouseLeave = useCallback(() => {
    setHoveredToken(null);
    debugLog('[BattleRoom] Token hover cleared');
  }, []);

  // Asset Browser state
  const [isAssetBrowserOpen, setIsAssetBrowserOpen] = useState(false);
  const [sceneManagerOpen, setSceneManagerOpen] = useState(false);
  const [authoritativeRewindPoints, setAuthoritativeRewindPoints] = useState(null);
  const [authoritativeRewindRevision, setAuthoritativeRewindRevision] = useState(0);
  const [rewindPending, setRewindPending] = useState(false);
  const [rewindError, setRewindError] = useState('');
  const [realtimeNotice, setRealtimeNotice] = useState(null);
  const [movementFeedback, setMovementFeedback] = useState('');
  const [movementMode, setMovementMode] = useState('walk');
  const [movementIntent, setMovementIntent] = useState('auto');
  // Selecting a path is not resolution. The server spends the bonus action
  // with the movement commit. date_of_change: 2026-09-05
  const creatureMovementRef = useRef(null);
  const [visionControlsOpen, setVisionControlsOpen] = useState(false);
  const [visionEditing, setVisionEditing] = useState(false);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [selectedSceneAssetId, setSelectedSceneAssetId] = useState(null);
  const [visionPreview, setVisionPreview] = useState(null);
  const [playerVisionPreviewEnabled, setPlayerVisionPreviewEnabled] = useState(false);
  const [playerVisionPreviewPending, setPlayerVisionPreviewPending] = useState(false);

  // @date_of_change 2025-09-20 - Global drag-and-drop state for Roll20-style image dropping
  const [dragOverlay, setDragOverlay] = useState(false);

  // @date_of_change: 2025-10-08 - MOVED BEFORE handleTokenDragEnd to fix hoisting error
  // Combat state management stores only the initiative reference. Actor mechanics
  // always resolve from the canonical token collection below; keeping a second
  // mutable combatant object here caused turn effects and equipment to diverge.
  // date_of_change: 2026-07-15
  const [localCombatantId, setCurrentCombatantId] = useState(null);
  const currentCombatantId = wsSessionId ? authoritativeCombatState?.activeCombatant ?? null : localCombatantId;
  const setCurrentCombatant = useCallback((reference) => {
    const nextId = reference && typeof reference === 'object'
      ? (reference.id ?? reference.tokenId ?? reference.combatantId ?? null)
      : reference;
    setCurrentCombatantId(nextId === undefined || nextId === null ? null : String(nextId));
  }, []);
  // Duplicate initiative notifications can arrive from both button handlers and effects.
  // This ref makes turn side effects idempotent without blocking GM token inspection.
  // date_of_change: 2026-06-23
  const lastProcessedTurnRef = useRef({ key: null, at: 0 });
  const lastSelectedAuthoritativeTurnRef = useRef(null);
  const lastAuthoritativeInitiativeHydrationRef = useRef(null);
  const lastAuthoritativeCombatLogHistoryRef = useRef('');

  // Handler for adding tokens from Asset Browser
  const handleOpenAssetBrowser = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setIsAssetBrowserOpen(true);
  }, []);

  // Get all state and handlers from the useBattleRoom hook
  const {
    tokens,
    setTokens,
    selectedTokenId,
    setSelectedTokenId,
    mapImage: hookMapImage,
    setMapImage,
    mapImages,
    setMapImages,
    sceneAssets,
    setSceneAssets,
    mapState,
    setMapState,
    stageRef,
    // Grid state and handlers
    showGrid,
    snapToGrid,
    gridSize,
    // @date_of_change: 2025-10-13 - Use gridType/feetPerCell from hook (no legacy fallbacks)
    gridType,
    feetPerCell,
    gridOpacity,
    gridWidth,
    setGridWidth,
    gridHeight,
    setGridHeight,
    zoom,
    setZoom,
    handleGridToggle,
    handleSnapToggle,
    handleGridSizeChange,
    handleGridOpacityChange,
    handleGridWidthChange,
    handleGridHeightChange,
    handleSaveSession,
    handleLoadSession,
    loadSessionFromData,
    // Layer management utilities
    activeLayer: hookActiveLayer,
    setActiveLayer: setHookActiveLayer,
    layers: hookLayers,
    setLayers: setHookLayers,
    // Map interaction handlers
    // handleDeselectAll, - Now using memoized version
    handleTokenSelect,
    handleTokenUpdate,
    handleBatchTokenUpdates,
    handleTokenDelete,
    handleMapDelete,
    // Token creation utilities
    createTokenFromDashboardData,
    // Token autoscaling utilities
    handleTokenSizeChange,
    // Layer change handler for maintaining token data consistency
    handleTokenLayerChange,
    // Multiplayer functions - date_of_change: 2025-09-24
    isMultiplayerMode,
    multiplayerSessionId,
    enableMultiplayerMode,
    disableMultiplayerMode,
    seedCurrentStateToMultiplayer,
    seedSessionDataToMultiplayer,
    waitForAuthoritativeRevision,
    pendingAuthoritativeProjectionCount,
    connectedPlayers,
    tokenOwnership,
    canControlToken,
    assignTokenControl,
    unassignTokenControl,
    getTokenControlInfo,
    // date_of_change: 2025-09-24 - Fog-of-war state and controls
    fogOfWar,
    toggleFogEnabled,
    setFogOpacity,
    toggleFogIgnoreGM,
    revealAllFog,
    coverAllFog,
    addFogOperationRectNormalized,
    addFogOperationPolygon,   // GAP-003: polygon/lasso fog ops — date_of_change: 2026-05-20
    undoLastFogOperation,     // GAP-003: undo last fog op — date_of_change: 2026-05-20
    // GAP-002: Drawing layer \u2014 date_of_change: 2026-05-20
    drawings,
    drawingEnabled,
    setDrawingEnabled,
    drawingTool,
    setDrawingTool,
    drawingColor,
    setDrawingColor,
    drawingLineWidth,
    setDrawingLineWidth,
    addDrawing,
    undoDrawing,
    clearDrawings
  } = useBattleRoom();

  const authoritativeTokensRef = useRef(tokens);
  authoritativeTokensRef.current = tokens;

  /**
   * Creature resolution is deterministic only when its token snapshot and
   * server revision describe the same board. Drain earlier writes, then wait
   * for React to apply that authoritative token projection before rolling.
   * date_of_change: 2026-09-08
   */
  const prepareCreatureResolution = useCallback(async () => {
    if (!wsSessionId) {
      return { tokens: authoritativeTokensRef.current, rulesetExecutionState };
    }

    try {
      const settledRevision = await waitForBattleRoomMutationQueue();
      await waitForAuthoritativeRevision(settledRevision);
      const currentState = authoritativeCombatStateRef.current;
      const currentRulesetState = buildRulesetExecutionState(currentState, serviceSessionData);
      const currentRevision = Number(currentRulesetState.resolutionRevision);
      if (!Number.isSafeInteger(currentRevision) || currentRevision < settledRevision) {
        const error = new Error('The authoritative BattleRoom snapshot is not ready for action resolution.');
        error.code = 'BATTLE_ROOM_NOT_READY';
        throw error;
      }
      return {
        tokens: authoritativeTokensRef.current,
        rulesetExecutionState: currentRulesetState,
      };
    } catch (cause) {
      console.warn('[BattleRoom] Creature action waited for authoritative synchronization:', {
        code: cause?.code,
        message: cause?.message,
      });
      setRealtimeNotice(getRealtimeMutationFeedback(cause));
      return null;
    }
  }, [rulesetExecutionState, serviceSessionData, waitForAuthoritativeRevision, waitForBattleRoomMutationQueue, wsSessionId]);

  // @date_of_change: 2025-09-27 - Override useBattleRoom's isGameMaster with WebSocket role
  // date_of_change: 2026-07-03 - Local, non-live rooms have no server role, so the creator is GM.
  const isGameMaster = isLocalBattleRoom || isGameMasterFromWS;
  const isCompactBattleViewport = useBattleRoomCompactViewport();
  const authoritativeMap = authoritativeCombatState?.map || authoritativeCombatState || {};
  useEffect(() => {
    if (Array.isArray(authoritativeMap?.assets)) setSceneAssets(authoritativeMap.assets);
  }, [authoritativeMap?.assets, setSceneAssets]);
  const visionWalls = Array.isArray(authoritativeMap?.walls) ? authoritativeMap.walls : [];
  const visionConfig = authoritativeMap?.vision || { mode: 'off', geometryRevision: 0 };
  const visibilityProjection = authoritativeMap?.visibility || { mode: 'off', polygons: [] };
  const selectedVisionWall = visionWalls.find((wall) => wall.wallId === selectedWallId) || null;
  const isPlayerVisionPreview = isGameMaster && playerVisionPreviewEnabled;
  const isGameMasterPresentation = isGameMaster && !isPlayerVisionPreview;

  const updateVisionConfig = useCallback(async (vision) => {
    try {
      await sendBattleRoomMutation('vision:config:update', { vision });
      if (vision.mode !== 'basic_los') {
        setPlayerVisionPreviewEnabled(false);
        setVisionPreview(null);
      }
      NotificationManager.success('Vision updated', vision.mode === 'basic_los' ? 'Basic line of sight is active.' : 'Dynamic vision is off; static Fog is unchanged.');
    } catch (cause) {
      NotificationManager.error('Vision not updated', cause?.message || 'The vision policy could not be saved.');
    }
  }, [sendBattleRoomMutation]);

  const upsertVisionWall = useCallback(async (wall) => {
    try {
      const wallId = wall.wallId || `wall:${typeof window !== 'undefined' && window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`}`;
      await sendBattleRoomMutation('vision:wall:upsert', { wall: { blocksMovement: true, blocksVision: true, visibility: 'public', locked: false, revision: 0, ...wall, wallId } });
      setSelectedWallId(wallId);
    } catch (cause) {
      NotificationManager.error('Barrier not saved', cause?.message || 'The barrier could not be saved.');
    }
  }, [sendBattleRoomMutation]);

  const deleteVisionWall = useCallback(async (wallId) => {
    if (!window.confirm('Delete this movement/vision barrier?')) return;
    try {
      await sendBattleRoomMutation('vision:wall:delete', { wallId });
      setSelectedWallId(null);
    } catch (cause) {
      NotificationManager.error('Barrier not deleted', cause?.message || 'The barrier could not be deleted.');
    }
  }, [sendBattleRoomMutation]);

  const handlePlayerVisionPreviewChange = useCallback((enabled) => {
    const nextEnabled = enabled === true;
    setPlayerVisionPreviewEnabled(nextEnabled);
    if (nextEnabled) {
      setVisionEditing(false);
      // Fail closed while the authoritative shared mask is loading so secret
      // map content never flashes into a player preview. date_of_change: 2026-09-10
      setVisionPreview((current) => current || {
        visibility: {
          mode: visionConfig.mode,
          geometryRevision: Number(visionConfig.geometryRevision) || 0,
          polygons: [],
        },
      });
    } else {
      setVisionPreview(null);
    }
  }, [visionConfig.geometryRevision, visionConfig.mode]);

  useEffect(() => {
    if (!playerVisionPreviewEnabled) {
      setPlayerVisionPreviewPending(false);
      return undefined;
    }
    if (!isGameMaster || visionConfig.mode !== 'basic_los' || !wsSessionId || typeof requestBattleRoom !== 'function') {
      setPlayerVisionPreviewEnabled(false);
      setVisionPreview(null);
      setPlayerVisionPreviewPending(false);
      return undefined;
    }

    let cancelled = false;
    setPlayerVisionPreviewPending(true);
    requestBattleRoom('vision:preview')
      .then((response) => {
        if (cancelled) return;
        setVisionPreview({ visibility: response.visibility });
      })
      .catch((cause) => {
        if (cancelled) return;
        setPlayerVisionPreviewEnabled(false);
        setVisionPreview(null);
        NotificationManager.error('Player preview unavailable', cause?.message || 'The shared player view could not be loaded. Try again.');
      })
      .finally(() => {
        if (!cancelled) setPlayerVisionPreviewPending(false);
      });

    return () => { cancelled = true; };
  }, [activeSceneId, isGameMaster, playerVisionPreviewEnabled, requestBattleRoom, stateRevision, visionConfig.mode, wsSessionId]);

  const applyAuthoritativeDamageOutcomes = useCallback(async (sourceTokenId, outcomes, operationLabel) => {
    if (!wsSessionId) return true;
    const damageOutcomes = (Array.isArray(outcomes) ? outcomes : []).map((outcome, index) => ({
      targetId: outcome?.targetId || outcome?.target?.id,
      critical: outcome?.attackRoll?.isCritical === true,
      creatureSaves: outcome?.creatureSaves,
      components: structuredDamageComponents(outcome, `${String(operationLabel || 'damage').replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 40)}:${index}`),
    })).filter((entry) => entry.targetId && entry.components.length > 0);
    for (const damage of damageOutcomes) {
      const acknowledgement = await sendBattleRoomMutation('damage:apply', {
        sourceTokenId,
        targetId: damage.targetId,
        critical: damage.critical,
        creatureSaves: damage.creatureSaves,
        components: damage.components
      });
      await waitForAuthoritativeRevision(acknowledgement?.revision);
    }
    return true;
  }, [sendBattleRoomMutation, waitForAuthoritativeRevision, wsSessionId]);

  // One queued mutation retains its ID and dice across ACK-loss retries.
  // Nothing is logged as resolved until the full revision is acknowledged.
  // date_of_change: 2026-09-05
  const commitCreatureResolution = useCallback(async (attacker, action, processor, updates, outcomes, executionState, gmOverride = null) => {
    try {
      const acknowledgement = await sendBattleRoomMutation('creature:action:commit', {
        sourceTokenId: attacker.id, actionId: action.id,
        multiattackParentId: action.multiattackParentId, multiattackIndex: action.multiattackIndex,
        round: executionState.round, turn: executionState.turn,
        resolutionRevision: executionState.resolutionRevision,
        creatureRandom: processor.creatureRandom,
        gmOverride,
        updates: updates.map(update => ({ id: update.id, props: update.props })),
        outcomes: (outcomes || []).map((outcome, index) => ({
          targetId: outcome.targetId || outcome.target?.id,
          hit: outcome.attackRoll?.isHit === true,
          critical: outcome.attackRoll?.isCritical === true,
          attackRoll: outcome.attackRoll,
          components: structuredDamageComponents(outcome, `${action.id}:${index}`),
          creatureSaves: outcome.creatureSaves,
          creatureEffects: outcome.creatureEffects,
          uncannyDodge: outcome.uncannyDodge
        }))
      });
      await waitForAuthoritativeRevision(acknowledgement.revision);
      return true;
    } catch (cause) {
      ActionEconomyManager.synchronizeCombatFlags(attacker.id, attacker.combatFlags || {}, ActionEconomyProcessor.parseSpeedFeet(attacker.speed));
      console.warn('[BattleRoom] Creature action commit rejected:', {
        code: cause?.code,
        message: cause?.message,
        actorId: attacker.id,
        actionId: action.id,
      });
      setRealtimeNotice(getRealtimeMutationFeedback(cause, { actionName: action.name }));
      return false;
    }
  }, [sendBattleRoomMutation, waitForAuthoritativeRevision]);

  const applyAuthoritativeHealingOutcomes = useCallback(async (sourceTokenId, outcomes) => {
    if (!wsSessionId) return true;
    const healingOutcomes = (Array.isArray(outcomes) ? outcomes : []).map((outcome) => ({
      targetId: outcome?.targetId || outcome?.target?.id || outcome?.originalTarget?.id,
      amount: Number(outcome?.healing?.total ?? outcome?.healing?.amount ?? outcome?.healing ?? 0),
    })).filter((entry) => entry.targetId && Number.isFinite(entry.amount) && entry.amount > 0);
    for (const healing of healingOutcomes) {
      const acknowledgement = await sendBattleRoomMutation('healing:apply', {
        sourceTokenId,
        targetId: healing.targetId,
        amount: Math.floor(healing.amount),
      });
      await waitForAuthoritativeRevision(acknowledgement?.revision);
    }
    return true;
  }, [sendBattleRoomMutation, waitForAuthoritativeRevision, wsSessionId]);

  const handleGmHitPointAdjustment = useCallback(async ({ tokenId, tokenName, type, amount }) => {
    // GM HP changes use the same server-owned mutation/revision barrier as
    // attacks and spells. A local token patch would be reverted by the next
    // initiative projection. rule_reference: PHB 2024 Damage and Healing;
    // AGENTS.md authoritative audit trail. date_of_change: 2026-09-01
    return applyGmHitPointAdjustment({
      sessionId: wsSessionId,
      sendBattleRoomMutation,
      waitForAuthoritativeRevision,
      tokenId,
      tokenName,
      type,
      amount,
    });
  }, [sendBattleRoomMutation, waitForAuthoritativeRevision, wsSessionId]);

  useEffect(() => {
    if (isLocalBattleRoom || (location.state?.forceGM && location.state?.fromDashboard && !sessionId)) {
      updateRole('gm');
    }
  }, [isLocalBattleRoom, location.state?.forceGM, location.state?.fromDashboard, sessionId, updateRole]);

  const handleToggleFogControls = useCallback(() => {
    if (!isGameMaster) {
      return;
    }
    setFogControlsOpen((prev) => !prev);
  }, [isGameMaster]);

  const handleCloseFogControls = useCallback(() => {
    setFogControlsOpen(false);
  }, []);

  // Debug GM status when component renders
  // date_of_change: 2025-09-25 - Debug GM role for UI visibility
  useEffect(() => {
    if (role) {
      debugLog('[BattleRoom] GM status changed:', {
        isGameMaster,
        role,
        isMultiplayerMode
      });
    }
  }, [isGameMaster, role, isMultiplayerMode]);

  // Use zoom level for stage scaling
  const stageScale = clampStageZoom(zoom || 1);

  useEffect(() => {
    stagePositionRef.current = stagePosition;
  }, [stagePosition]);

  useEffect(() => {
    stageZoomRef.current = stageScale;
  }, [stageScale]);

  // date_of_change: 2026-07-25
  // Wheel, toolbar, minimap, and pinch gestures share one camera commit path.
  // This keeps the visible map position and persisted zoom value in lockstep.
  const commitStageViewport = useCallback(({ nextZoom, nextPosition }) => {
    viewportAdjustedRef.current = true;

    if (Number.isFinite(nextZoom)) {
      const clampedZoom = clampStageZoom(nextZoom);
      stageZoomRef.current = clampedZoom;
      setZoom(clampedZoom);
    }

    if (nextPosition && Number.isFinite(nextPosition.x) && Number.isFinite(nextPosition.y)) {
      stagePositionRef.current = nextPosition;
      setStagePosition(nextPosition);
    }
  }, [setZoom]);

  // GAP-002: Auto-set eraser width inversely proportional to zoom when switching to eraser tool.
  // Formula: eraserWidth = 50 / stageScale  (zoom 1.0 → 50, zoom 2.0 → 25, etc.)
  // User can still override width manually. Restores previous non-eraser width on tool switch.
  // date_of_change: 2026-05-25
  const prevNonEraserWidthRef = useRef(drawingLineWidth);
  useEffect(() => {
    if (drawingTool === 'eraser') {
      const autoWidth = Math.max(1, Math.round(50 / stageScale));
      setDrawingLineWidth(autoWidth);
    } else {
      setDrawingLineWidth(prevNonEraserWidthRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire on tool switch or zoom change, not on drawingLineWidth change (avoid loop)
  }, [drawingTool, stageScale]);

  // Track user's non-eraser width whenever they change it while not on the eraser tool
  // date_of_change: 2026-05-25
  useEffect(() => {
    if (drawingTool !== 'eraser') {
      prevNonEraserWidthRef.current = drawingLineWidth;
    }
  }, [drawingLineWidth, drawingTool]);

  const getViewportPlacementHint = useCallback(() => {
    if (
      stageScale <= 0 ||
      Number(stageSize?.width) <= 0 ||
      Number(stageSize?.height) <= 0
    ) {
      return null;
    }

    return {
      x: Math.max(((stageSize.width / 2) - stagePosition.x) / stageScale, 0),
      y: Math.max(((stageSize.height / 2) - stagePosition.y) / stageScale, 0),
      // Placement uses the current rendered scale to reserve fixed-screen
      // nameplate space without changing any map or token controls.
      // date_of_change: 2026-08-25
      viewScale: stageScale
    };
  }, [stagePosition.x, stagePosition.y, stageScale, stageSize?.height, stageSize?.width]);

  const handleAssetBrowserAddToken = useCallback(async (entityData) => {
    try {
      const newToken = await createTokenFromDashboardData(entityData, 'token', {
        placementHint: getViewportPlacementHint()
      });
      debugLog(`[BattleRoom] Successfully added ${entityData.name} from Asset Browser`);
      setSelectedTokenId(newToken?.id || null);

      if (CombatLogManager.combatStarted) {
        CombatLogManager.updateCombatant(newToken);
      }
      
      const entityType = entityData.type === 'Monster' ? 'Monster' : 'Character';
      NotificationManager.success(
        `${entityType} Added!`,
        `${entityData.name} has been placed on the Token Layer. You can now drag, resize, and manage this token.`
      );
    } catch (error) {
      console.error(`[BattleRoom] Failed to add ${entityData.name} from Asset Browser:`, error);
      NotificationManager.error(
        'Token Addition Failed', 
        `Failed to add ${entityData.name} to battle. Please try again.`
      );
    }
  }, [createTokenFromDashboardData, getViewportPlacementHint, setSelectedTokenId]);

  const fitMapInViewport = useCallback(() => {
    const fittedViewport = buildFittedStageViewport({
      stageSize,
      mapState,
      preferReadableMap: isCompactBattleViewport
    });

    if (!fittedViewport) {
      return;
    }

    viewportAdjustedRef.current = true;
    commitStageViewport({
      nextZoom: fittedViewport.zoom,
      nextPosition: fittedViewport.position
    });
  }, [commitStageViewport, isCompactBattleViewport, mapState, stageSize]);

  const applyStagePan = useCallback((clientX, clientY) => {
    if (!panSessionRef.current) {
      return;
    }

    const deltaX = clientX - panSessionRef.current.clientX;
    const deltaY = clientY - panSessionRef.current.clientY;

    if (!deltaX && !deltaY) {
      return;
    }

    panSessionRef.current = { clientX, clientY };
    const currentPosition = stagePositionRef.current;
    commitStageViewport({
      nextPosition: {
        x: currentPosition.x + deltaX,
        y: currentPosition.y + deltaY
      }
    });
  }, [commitStageViewport]);

  useEffect(() => {
    if (!isPanning) {
      return undefined;
    }

    const handleWindowMouseMove = (event) => {
      if (typeof event?.preventDefault === 'function') {
        event.preventDefault();
      }
      applyStagePan(event.clientX, event.clientY);
    };

    const handleWindowMouseUp = () => {
      panSessionRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener('mousemove', handleWindowMouseMove, { passive: false });
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('blur', handleWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('blur', handleWindowMouseUp);
    };
  }, [applyStagePan, isPanning]);

  const zoomStageAroundPoint = useCallback((targetZoom, anchorPoint = null) => {
    const stageWidth = Number(stageSize?.width) || 0;
    const stageHeight = Number(stageSize?.height) || 0;
    if (stageWidth <= 0 || stageHeight <= 0) {
      return;
    }

    const currentZoom = clampStageZoom(stageZoomRef.current);
    const nextZoom = clampStageZoom(targetZoom);
    const anchor = anchorPoint || {
      x: stageWidth / 2,
      y: stageHeight / 2
    };

    const nextPosition = {
      x: anchor.x - (((anchor.x - stagePositionRef.current.x) / currentZoom) * nextZoom),
      y: anchor.y - (((anchor.y - stagePositionRef.current.y) / currentZoom) * nextZoom)
    };

    commitStageViewport({ nextZoom, nextPosition });
  }, [commitStageViewport, stageSize]);

  const handleViewportZoomIn = useCallback(() => {
    zoomStageAroundPoint(stageZoomRef.current * 1.2);
  }, [zoomStageAroundPoint]);

  const handleViewportZoomOut = useCallback(() => {
    zoomStageAroundPoint(stageZoomRef.current / 1.2);
  }, [zoomStageAroundPoint]);

  const handleViewportZoomReset = useCallback(() => {
    fitMapInViewport();
  }, [fitMapInViewport]);

  useEffect(() => {
    const container = stageContainerRef.current;
    if (!container) {
      return undefined;
    }

    const beginPinch = (event) => {
      const metrics = getPinchViewportMetrics(event.touches, container.getBoundingClientRect());
      if (!metrics || metrics.distance <= 0) {
        pinchSessionRef.current = null;
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      pinchSessionRef.current = metrics;
    };

    const updatePinch = (event) => {
      const metrics = getPinchViewportMetrics(event.touches, container.getBoundingClientRect());
      const previous = pinchSessionRef.current;
      if (!metrics || metrics.distance <= 0) {
        pinchSessionRef.current = null;
        return;
      }

      if (!previous) {
        pinchSessionRef.current = metrics;
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const currentZoom = clampStageZoom(stageZoomRef.current);
      const nextZoom = clampStageZoom(currentZoom * (metrics.distance / previous.distance));
      const currentPosition = stagePositionRef.current;
      const zoomedPosition = {
        x: previous.center.x - (((previous.center.x - currentPosition.x) / currentZoom) * nextZoom),
        y: previous.center.y - (((previous.center.y - currentPosition.y) / currentZoom) * nextZoom)
      };

      commitStageViewport({
        nextZoom,
        nextPosition: {
          x: zoomedPosition.x + (metrics.center.x - previous.center.x),
          y: zoomedPosition.y + (metrics.center.y - previous.center.y)
        }
      });
      pinchSessionRef.current = metrics;
    };

    const endPinch = () => {
      pinchSessionRef.current = null;
    };

    container.addEventListener('touchstart', beginPinch, { capture: true, passive: false });
    container.addEventListener('touchmove', updatePinch, { capture: true, passive: false });
    container.addEventListener('touchend', endPinch, { capture: true });
    container.addEventListener('touchcancel', endPinch, { capture: true });

    return () => {
      container.removeEventListener('touchstart', beginPinch, true);
      container.removeEventListener('touchmove', updatePinch, true);
      container.removeEventListener('touchend', endPinch, true);
      container.removeEventListener('touchcancel', endPinch, true);
    };
  }, [commitStageViewport]);

  // Fog-of-war interactive rectangle tool (GM only)
  // date_of_change: 2025-09-24 - Local UI state for fog draw tool
  // GAP-004: minimap visibility toggle — date_of_change: 2026-05-20
  const [minimapVisible, setMinimapVisible] = useState(true);

  useEffect(() => {
    if (isCompactBattleViewport) {
      setMinimapVisible(false);
    }
  }, [isCompactBattleViewport]);

  // GAP-011: cursor position for status bar — date_of_change: 2026-05-20
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const [fogEditMode, setFogEditMode] = useState('none'); // 'none' | 'reveal' | 'cover'
  const fogDragRef = useRef(null); // { start: {x,y}, current: {x,y} }

  // date_of_change: 2026-05-24 - Mutual exclusivity: fog and drawing tools compete
  // When fog edit mode becomes active, disable drawing; when drawing enabled, exit fog edit mode
  useEffect(() => {
    if (fogEditMode !== 'none' && drawingEnabled) {
      setDrawingEnabled(false);
    }
  }, [fogEditMode, drawingEnabled, setDrawingEnabled]);

  useEffect(() => {
    if (drawingEnabled && fogEditMode !== 'none') {
      setFogEditMode('none');
    }
  }, [drawingEnabled, fogEditMode, setFogEditMode]);

  // date_of_change: 2026-05-24 - Wire up battle tool keyboard shortcuts with context-aware undo
  // Must be after fogEditMode, drawingEnabled, and useBattleRoom state are defined
  useBattleKeyboardShortcuts({
    enabled: isGameMaster,
    onUndo: useCallback(() => {
      // Context-aware undo: fog operations take precedence when fogEditMode is active
      if (fogEditMode !== 'none' && fogOfWar?.operations?.length > 0) {
        undoLastFogOperation();
      } else if (drawingEnabled && drawings.length > 0) {
        undoDrawing();
      }
    }, [fogEditMode, fogOfWar?.operations?.length, drawingEnabled, drawings.length, undoLastFogOperation, undoDrawing]),
    onToolChange: useCallback((tool) => {
      // When switching to a drawing tool, disable fog edit mode
      if (fogEditMode !== 'none') {
        setFogEditMode('none');
      }
      setDrawingTool(tool);
      if (!drawingEnabled) {
        setDrawingEnabled(true);
      }
    }, [fogEditMode, setDrawingTool, setDrawingEnabled, drawingEnabled]),
    onClearDrawings: clearDrawings
  });

  // date_of_change: 2026-04-17 - Fog controls now render as a compact toolbar slide-out
  const [fogControlsOpen, setFogControlsOpen] = useState(false);
  const compactFogButtonRef = useRef(null);
  const [compactFogAnchorRect, setCompactFogAnchorRect] = useState(null);

  // date_of_change: 2025-09-25 - Visual feedback for fog area selection
  const [fogDragPreview, setFogDragPreview] = useState(null); // { x, y, width, height }

  // GAP-003: Fog shape palette and polygon/lasso draw state — date_of_change: 2026-05-20
  // Rule reference: GAP-003 spec — rect (default), polygon (click vertices), lasso (drag freehand)
  const [fogShape, setFogShape] = useState('rect'); // 'rect' | 'polygon' | 'lasso'
  // Polygon tool: accumulated vertices (normalized {xPct, yPct}) until double-click closes the shape
  const fogPolygonPointsRef = useRef([]); // { xPct, yPct }[]
  // Lasso tool: points accumulated during mouse-drag (normalized), committed on mouse-up
  const fogLassoPointsRef = useRef([]); // { xPct, yPct }[]
  const fogLassoDragging = useRef(false);
  // Live polygon/lasso preview overlay (raw stage coords for display, not normalized)
  const [fogPolyPreview, setFogPolyPreview] = useState(null); // { points: [x,y,...], closingPoint: {x,y}|null }

  // -----------------------------
  // Dice automation settings (GM panel)
  // -----------------------------
  const [diceSettings, setDiceSettings] = useState(() => {
    try {
      return getDiceAuto ? getDiceAuto() : { gmAutomaticRolls: true, playerAutomaticRolls: true };
    } catch (err) {
      console.warn('[BattleRoom] Failed to read initial dice settings, using defaults.', err);
      return { gmAutomaticRolls: true, playerAutomaticRolls: true };
    }
  });

  const handleDiceSettingsChange = useCallback((updates) => {
    setDiceSettings((prev) => {
      const next = { ...prev, ...updates };
      try {
        setDiceAuto(next);
      } catch (err) {
        console.warn('[BattleRoom] Failed to persist dice settings to DiceProvider.', err);
      }
      return next;
    });
  }, []);

  // -----------------------------
  // Global modals (manual dice, GM choice)
  // -----------------------------
  const manualQueueRef = useRef([]);
  const manualCurrentRef = useRef(null);
  const [manualModal, setManualModal] = useState({ visible: false, title: '', prompt: '', numDice: 1, dieSize: 20 });

  const choiceQueueRef = useRef([]);
  const choiceCurrentRef = useRef(null);
  const [choiceModal, setChoiceModal] = useState({ visible: false, title: '', prompt: '', options: [] });

  const interactionIntentCurrentRef = useRef(null);
  const [interactionIntentModal, setInteractionIntentModal] = useState({
    visible: false,
    title: '',
    description: '',
    textLabel: '',
    placeholder: '',
    confirmLabel: '',
    footerNote: '',
    untargetedLabel: '',
    targets: [],
    initialTargetId: '__audible__'
  });

  const requestInteractionIntent = useCallback((options) => (
    new Promise((resolve) => {
      if (interactionIntentCurrentRef.current?.resolve) interactionIntentCurrentRef.current.resolve(null);
      interactionIntentCurrentRef.current = { resolve };
      setInteractionIntentModal({
        visible: true,
        title: options.title || 'Describe interaction',
        description: options.description || 'Record the exact intent GMA should resolve.',
        textLabel: options.textLabel || 'What are you trying to do?',
        placeholder: options.placeholder || '',
        confirmLabel: options.confirmLabel || 'Record intent',
        footerNote: options.footerNote || 'This intent will be included in the GMA narration handoff.',
        untargetedLabel: options.untargetedLabel || 'No specific target',
        targets: options.targets || [],
        initialTargetId: options.initialTargetId || '__audible__'
      });
    })
  ), []);

  const resolveInteractionIntent = useCallback((value) => {
    const current = interactionIntentCurrentRef.current;
    interactionIntentCurrentRef.current = null;
    setInteractionIntentModal((previous) => ({ ...previous, visible: false }));
    current?.resolve?.(value);
  }, []);

  const showNextManual = useCallback(() => {
    if (manualModal.visible) return;
    const next = manualQueueRef.current.shift();
    if (!next) return;
    manualCurrentRef.current = next;
    setManualModal({ visible: true, title: next.title, prompt: next.prompt, numDice: next.numDice || 1, dieSize: next.dieSize || 20 });
  }, [manualModal.visible]);

  const showNextChoice = useCallback(() => {
    if (choiceModal.visible) return;
    const next = choiceQueueRef.current.shift();
    if (!next) return;
    choiceCurrentRef.current = next;
    setChoiceModal({ visible: true, title: next.title, prompt: next.prompt, options: Array.isArray(next.options) ? next.options : [] });
  }, [choiceModal.visible]);

  // Bind engine prompts to UI modals
  useEffect(() => {
    try {
      setRequestD20(({ numDice = 1, title = 'Enter Roll', prompt = 'Enter d20 roll value' }) => {
        return new Promise((resolve) => {
          manualQueueRef.current.push({ numDice, title, prompt, dieSize: 20, resolve });
          showNextManual();
        });
      });
    } catch (_) {}
    try {
      // Generic dice (e.g., damage dice)
      if (typeof setRequestDice === 'function') {
        setRequestDice(({ numDice = 1, dieSize = 20, title = 'Enter Rolls', prompt = 'Enter dice results' }) => {
          return new Promise((resolve) => {
            manualQueueRef.current.push({ numDice, title, prompt, dieSize, resolve });
            showNextManual();
          });
        });
      }
    } catch (_) {}
    try {
      setRequestChoice(({ title = 'Select', prompt = 'Choose one', options = [] }) => {
        return new Promise((resolve) => {
          choiceQueueRef.current.push({ title, prompt, options, resolve });
          showNextChoice();
        });
      });
    } catch (_) {}
    // no cleanup necessary; BattleRoom persists as long as session is active
  }, [showNextManual, showNextChoice]);

  // Pointer-centered mouse wheel zoom for Stage
  // @date_of_change: 2026-04-07 - Keep zoom anchored to the pointer using controlled viewport state
  const handleStageWheel = useCallback((e) => {
    try {
      if (e?.evt?.preventDefault) e.evt.preventDefault();
      const stage = stageRef?.current;
      const scaleBy = 1.08;
      const deltaY = e?.evt?.deltaY ?? 0;
      const currentZoom = clampStageZoom(stageZoomRef.current);
      let direction = deltaY > 0 ? -1 : 1;
      if (e?.evt?.ctrlKey) {
        direction = -direction;
      }
      const nextZoom = direction > 0 ? (currentZoom * scaleBy) : (currentZoom / scaleBy);
      const pointer = stage?.getPointerPosition?.() || {
        x: (Number(stageSize?.width) || 0) / 2,
        y: (Number(stageSize?.height) || 0) / 2
      };

      zoomStageAroundPoint(nextZoom, pointer);
    } catch (err) {
      console.error('[BattleRoom] Wheel zoom error:', err);
    }
  }, [stageRef, stageSize, zoomStageAroundPoint]);

  // Convert stage pixel coordinates to normalized rectPct relative to map area
  const toNormalizedFogRect = useCallback((start, end) => {
    if (!mapState?.size?.width || !mapState?.size?.height) return null;
    const mapX = mapState.position.x;
    const mapY = mapState.position.y;
    const mapW = mapState.size.width;
    const mapH = mapState.size.height;

    const x1 = Math.min(start.x, end.x);
    const y1 = Math.min(start.y, end.y);
    const x2 = Math.max(start.x, end.x);
    const y2 = Math.max(start.y, end.y);

    // Clamp to map bounds
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const cx1 = clamp(x1, mapX, mapX + mapW);
    const cy1 = clamp(y1, mapY, mapY + mapH);
    const cx2 = clamp(x2, mapX, mapX + mapW);
    const cy2 = clamp(y2, mapY, mapY + mapH);

    const w = Math.max(0, cx2 - cx1);
    const h = Math.max(0, cy2 - cy1);
    if (w < 2 || h < 2) return null; // ignore tiny selections

    return {
      xPct: (cx1 - mapX) / mapW,
      yPct: (cy1 - mapY) / mapH,
      wPct: w / mapW,
      hPct: h / mapH,
    };
  }, [mapState]);

  /**
   * GAP-003: Convert a single stage-local pixel point to normalized fog coords.
   * Returns null if map dimensions not ready or point outside map.
   * Rule reference: GAP-003 spec — geometry stored as normalized (0..1) relative to map area.
   * date_of_change: 2026-05-20
   */
  const toNormalizedFogPoint = useCallback((pt) => {
    if (!mapState?.size?.width || !mapState?.size?.height) return null;
    const mapX = mapState.position.x;
    const mapY = mapState.position.y;
    const mapW = mapState.size.width;
    const mapH = mapState.size.height;
    const clamp = (val, lo, hi) => Math.max(lo, Math.min(hi, val));
    return {
      xPct: (clamp(pt.x, mapX, mapX + mapW) - mapX) / mapW,
      yPct: (clamp(pt.y, mapY, mapY + mapH) - mapY) / mapH,
    };
  }, [mapState]);

  // Enable multiplayer mode when we have an effective session id
  // @date_of_change: 2025-09-30 - Use WebSocketContext session id if prop is missing
  const effectiveSessionId = useMemo(() => sessionId || wsSessionId, [sessionId, wsSessionId]);
  // @date_of_change: 2025-09-24 - Auto-enable multiplayer for session invitations
  useEffect(() => {
    if (effectiveSessionId && !isMultiplayerMode && (!sessionId || wsSessionId === sessionId)) {
      debugLog('[BattleRoom] Enabling multiplayer mode for session:', effectiveSessionId, {
        source: sessionId ? 'prop' : 'websocket', wsSessionStatus
      });
      // Pass the session id to ensure joinSession gets a value
      enableMultiplayerMode(effectiveSessionId);
      // @date_of_change: 2025-09-27 - Removed setIsGameMaster override
      // GM role now comes from WebSocket server response, not local assumption
    } else if (!effectiveSessionId && isMultiplayerMode) {
      debugLog('[BattleRoom] Disabling multiplayer mode (no session ID)');
      disableMultiplayerMode();
    }
  }, [effectiveSessionId, isMultiplayerMode, enableMultiplayerMode, disableMultiplayerMode, wsSessionStatus, sessionId, wsSessionId]);

  // @date_of_change: 2025-10-05 - Configure CombatLogManager with WebSocket sender for multiplayer combat log sync
  useEffect(() => {
    if (effectiveSessionId && sendWebSocketMessage) {
      CombatLogManager.setWebSocketSender(effectiveSessionId, sendWebSocketMessage);
    }
  }, [effectiveSessionId, sendWebSocketMessage]);

  // Listen for WebSocket map updates from GM
  // @date_of_change: 2025-09-30 - Receive and apply map updates from other players
  useEffect(() => {
    debugLog('[BattleRoom] Setting up websocket-map-update listener');
    
    const handleMapUpdate = (event) => {
      const { mapState: incomingMapState } = event.detail;
      const localSceneExists = tokens.length > 0 || !!(mapImages.map || hookMapImage);
      const incomingTokens = Array.isArray(incomingMapState?.tokens) ? incomingMapState.tokens : [];
      const incomingSceneExists = !!(
        incomingTokens.length > 0 ||
        incomingMapState?.imageUrl ||
        (Number(incomingMapState?.width) > 0 && Number(incomingMapState?.height) > 0)
      );
      const protectionActive = importedSessionProtectionRef.current.expiresAt > Date.now();

      if (protectionActive && localSceneExists && !incomingSceneExists) {
        debugLog('[BattleRoom] Ignoring empty remote map state while loaded session is being promoted into live room');
        return;
      }

      if (protectionActive && incomingSceneExists) {
        importedSessionProtectionRef.current = { expiresAt: 0 };
      }

      // @date_of_change: 2025-10-03 - Convert backend structure to frontend structure
      // @date_of_change: 2025-10-04 - Backend now uses socket.broadcast to prevent echo
      // No need to filter - we won't receive our own updates back from the server
      if (incomingMapState.tokens && Array.isArray(incomingMapState.tokens)) {
        const incomingTemplateMap = incomingMapState.snapshotTemplates || incomingMapState.characterTemplates || {};
        const rehydratedTokens = rehydrateIncomingBattleRoomTokens(
          incomingMapState.tokens,
          incomingTemplateMap
        );
        setTokens(rehydratedTokens);
      }

      // Note: gridSize and fogOfWar are handled by useBattleRoom hook's WebSocket processor
      // (they don't have setters exposed to BattleRoom)

      // Update map image if present
      if (incomingMapState.imageUrl) {
        setMapImage(incomingMapState.imageUrl);
        setMapImages(prev => ({
          ...prev,
          map: incomingMapState.imageUrl
        }));
        setHookLayers(prev => ({
          ...prev,
          map: {
            ...prev.map,
            mapImage: incomingMapState.imageUrl
          }
        }));
      }

      // Convert backend structure (width/height) to frontend structure (size.width/size.height)
      if (incomingMapState.width && incomingMapState.height) {
        setMapState(prev => ({
          ...prev,
          size: {
            width: incomingMapState.width,
            height: incomingMapState.height
          }
        }));
      }

      debugLog('[BattleRoom] Map state updated from remote');
    };

    window.addEventListener('websocket-map-update', handleMapUpdate);
    
    return () => {
      window.removeEventListener('websocket-map-update', handleMapUpdate);
    };
  }, [hookMapImage, mapImages.map, setMapImage, setMapImages, setHookLayers, setMapState, setTokens, tokens.length]);

  // Stage-targeting restore value until UI state setters are available
  const pendingTargetingEnabledRef = useRef(null);

  // Refs for handler functions to avoid no-use-before-define lint
  const handleAttackActionRef = useRef(null);
  const handleSpellActionRef = useRef(null);
  const handleAbilityActionRef = useRef(null);

  // Helper function to restore BattleRoom state
  const restoreBattleRoomState = useCallback((result) => {
    if (result.battleRoomState) {
      debugLog('[BattleRoom] 🔄 Restoring BattleRoom state from session:', result.battleRoomState);

      // Restore BattleRoom-specific combat state
      if (result.battleRoomState.actionsUsed) {
        setActionsUsed(result.battleRoomState.actionsUsed);
      }
      if (result.battleRoomState.armedAction) {
        setArmedAction(result.battleRoomState.armedAction);
      }
      if (result.battleRoomState.selectedTargets) {
        setSelectedTargets(result.battleRoomState.selectedTargets);
      }
      if (result.battleRoomState.multiattackSequence) {
        setMultiattackSequence(result.battleRoomState.multiattackSequence);
      }
      if (result.battleRoomState.isInMultiattackMode !== undefined) {
        setIsInMultiattackMode(result.battleRoomState.isInMultiattackMode);
      }
      if (result.battleRoomState.multiattackTarget) {
        setMultiattackTarget(result.battleRoomState.multiattackTarget);
      }
      if (result.battleRoomState.targetingEnabled !== undefined) {
        pendingTargetingEnabledRef.current = result.battleRoomState.targetingEnabled;
      }
      const restoredCombatantReference = result.battleRoomState.currentCombatantId
        ?? result.battleRoomState.currentCombatant
        ?? null;
      if (restoredCombatantReference) {
        setCurrentCombatant(restoredCombatantReference);
      }

      debugLog('[BattleRoom] ✅ BattleRoom state restoration complete');
    }
  }, [setCurrentCombatant]);

  // Helper function to show session load success
  const showSessionLoadSuccess = useCallback((sessionData) => {
    NotificationManager.success(
      'Battle Room Loaded!',
      `Restored:<br>• ${sessionData.battleState?.tokens?.length || 0} tokens<br>• Map and grid settings<br>• Layer configuration<br>• Combat state<br>• Zoom level: ${Math.round((sessionData.battleState?.zoom || 1) * 100)}%`
    );
  }, []);

  // Helper function to show session load error
  const showSessionLoadError = useCallback(() => {
    NotificationManager.error(
      'Room Load Failed',
      'Failed to load the selected room file.'
    );
  }, []);

  const refreshLoadedSessionPresentation = useCallback((sessionData) => {
    const initiativeData = buildBattleRoomSessionInitiativeSnapshot(sessionData);

    // React token replacement and singleton imports are synchronous requests,
    // but the initiative tracker and latest-result card own local projections.
    // Notify them after the restored token collection commits.
    // date_of_change: 2026-08-20
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('websocket-initiative-update', {
        detail: {
          isRemoteUpdate: true,
          initiativeData
        }
      }));
      window.dispatchEvent(new CustomEvent('combat-log-updated', {
        detail: { source: 'saved-room-load', timestamp: Date.now() }
      }));
    }, 100);
  }, []);

  const clearDashboardSessionLoadState = useCallback(() => {
    if (!location.state?.fromDashboard) {
      return;
    }

    const nextState = stripDashboardSessionLoadState(location.state);
    navigate(location.pathname, {
      replace: true,
      state: nextState
    });
  }, [location.pathname, location.state, navigate]);

  const syncLoadedSessionIntoLiveRoom = useCallback((sessionData) => {
    if (!sessionData || !(isMultiplayerMode && (multiplayerSessionId || effectiveSessionId))) {
      return;
    }

    importedSessionProtectionRef.current = { expiresAt: Date.now() + 8000 };
    const targetSessionId = multiplayerSessionId || effectiveSessionId;
    const seeded = seedSessionDataToMultiplayer(sessionData, targetSessionId);

    if (!seeded) {
      importedSessionProtectionRef.current = { expiresAt: 0 };
      console.warn('[BattleRoom] Failed to synchronize loaded session into current live room');
    }
  }, [effectiveSessionId, isMultiplayerMode, multiplayerSessionId, seedSessionDataToMultiplayer]);

  const handleLoadSessionIntoRoom = useCallback(async (sessionId = null, fileOrEvent = null) => {
    const sessionFile = extractSessionLoadFile(fileOrEvent);
    const shouldDetachFromLiveRoom = !!(
      (sessionId || sessionFile) &&
      (effectiveSessionId || wsSessionId || isMultiplayerMode || wsSessionStatus === 'joining' || wsSessionStatus === 'joined')
    );

    if (shouldDetachFromLiveRoom) {
      debugLog('[BattleRoom] Redirecting session load into local room before applying saved state:', {
        effectiveSessionId,
        wsSessionId,
        isMultiplayerMode,
        wsSessionStatus,
        hasSessionId: !!sessionId,
        hasSessionFile: !!sessionFile
      });

      clearSessionState();
      disableMultiplayerMode();

      navigate('/battle', {
        replace: true,
        state: sessionId
          ? {
              fromDashboard: true,
              forceGM: true,
              loadSessionId: sessionId
            }
          : sessionFile
            ? {
                fromDashboard: true,
                forceGM: true,
                loadSessionFile: sessionFile
              }
            : null
      });

      return {
        success: true,
        redirectedToLocalRoom: true
      };
    }

    const result = await handleLoadSession(sessionId, fileOrEvent);

    if (result?.success) {
      restoreBattleRoomState(result);
      refreshLoadedSessionPresentation(result.sessionData);
      syncLoadedSessionIntoLiveRoom(result.sessionData);
    }

    return result;
  }, [
    clearSessionState,
    disableMultiplayerMode,
    effectiveSessionId,
    handleLoadSession,
    isMultiplayerMode,
    navigate,
    refreshLoadedSessionPresentation,
    restoreBattleRoomState,
    syncLoadedSessionIntoLiveRoom,
    wsSessionId,
    wsSessionStatus
  ]);

  // Handle session loading from dashboard
  // @date_of_change 2025-09-15 - FILE-BASED SESSION STORAGE
  // date_of_change: 2026-04-17 - When dashboard passes forceGM:true, set role to gm immediately
  // so the user who loads their own session gets full GM controls without a WS round-trip.
  useEffect(() => {
    const requestKey = buildDashboardSessionLoadRequestKey(location.state);

    if (!requestKey) {
      sessionLoadingHandledRef.current = false;
      sessionLoadRequestKeyRef.current = null;
      return;
    }

    if (sessionLoadRequestKeyRef.current === requestKey) {
      debugLog('[BattleRoom] Skipping duplicate dashboard session load request:', requestKey);
      return;
    }

    if (location.state?.forceGM && location.state?.fromDashboard) {
      updateRole('gm');
    }
    debugLog('[BattleRoom] useEffect triggered with location.state:', {
      hasLocationState: !!location.state,
      hasLoadSessionData: !!(location.state?.loadSessionData),
      hasLoadSessionFile: !!(location.state?.loadSessionFile),
      hasLoadSessionId: !!(location.state?.loadSessionId),
      fromDashboard: location.state?.fromDashboard,
      sessionName: location.state?.sessionName,
      sessionLoadingHandled: sessionLoadingHandledRef.current,
      requestKey
    });
    // @date_of_change 2025-09-16 - Handle direct session data loading
    if (location.state?.loadSessionData && location.state?.fromDashboard) {
      sessionLoadRequestKeyRef.current = requestKey;
      const sessionData = location.state.loadSessionData;
      debugLog('[BattleRoom] 🔄 Loading session data directly from localStorage');

      const result = loadSessionFromData(sessionData);
      if (result.success) {
        debugLog('[BattleRoom] ✅ Session loaded from localStorage successfully');
        showSessionLoadSuccess(sessionData);
        restoreBattleRoomState(result);
        refreshLoadedSessionPresentation(sessionData);
        syncLoadedSessionIntoLiveRoom(sessionData);
      } else {
        console.error('[BattleRoom] ❌ Failed to load session from localStorage:', result.error);
        showSessionLoadError();
      }
      clearDashboardSessionLoadState();
      return;
    }

    // Handle legacy loadSessionFile (direct file from dashboard)
    if (location.state?.loadSessionFile && location.state?.fromDashboard) {
      sessionLoadRequestKeyRef.current = requestKey;
      const file = location.state.loadSessionFile;
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const sessionData = JSON.parse(event.target.result);
          debugLog('[BattleRoom] 🔍 DEBUGGING: Loaded session file structure:', {
            hasDirectBattleState: !!sessionData.battleState,
            hasDataBattleState: !!(sessionData.data && sessionData.data.battleState),
            topLevelKeys: Object.keys(sessionData),
            dataKeys: sessionData.data ? Object.keys(sessionData.data) : null
          });

          // @date_of_change 2025-09-17 - HANDLE MULTIPLE SESSION FILE FORMATS
          // Check for battleState in multiple possible locations
          let battleStateData = null;

          if (sessionData.battleState) {
            // Direct battleState (legacy format)
            debugLog('[BattleRoom] Using direct battleState format');
            battleStateData = sessionData;
          } else if (sessionData.data && sessionData.data.battleState) {
            // Nested under data (new format)
            debugLog('[BattleRoom] Using nested data.battleState format');
            battleStateData = sessionData.data;
          } else {
            console.error('[BattleRoom] No battleState found in session file');
            alert('Invalid room file: No battle data found.');
            return;
          }

          const result = loadSessionFromData(battleStateData);
          if (result.success) {
            debugLog('[BattleRoom] ✅ Session loaded from file successfully');
            restoreBattleRoomState(result);
            refreshLoadedSessionPresentation(battleStateData);
            showSessionLoadSuccess(battleStateData);
            syncLoadedSessionIntoLiveRoom(battleStateData);
          } else {
            console.error('[BattleRoom] ❌ Failed to load session from file:', result);
            showSessionLoadError();
          }
          clearDashboardSessionLoadState();
        } catch (error) {
          console.error('[BattleRoom] ❌ Failed to parse session file:', error);
          alert('Failed to load the selected room file. Please check that it is a valid battle room file.');
          clearDashboardSessionLoadState();
        }
      };

      reader.readAsText(file);
    }
    // Handle new file-based session loading (loadSessionId)
    else if (location.state?.loadSessionId && location.state?.fromDashboard && !sessionLoadingHandledRef.current) {
      const sessionId = location.state.loadSessionId;
      const expectedFileName = location.state.expectedFileName;

      // Mark as handled to prevent double triggering
      sessionLoadingHandledRef.current = true;
      sessionLoadRequestKeyRef.current = requestKey;

      debugLog('[BattleRoom] 🔍 DEBUGGING: Session loading triggered with location.state:', {
        loadSessionId: sessionId,
        expectedFileName: expectedFileName,
        fromDashboard: location.state.fromDashboard,
        sessionName: location.state.sessionName
      });
      debugLog('[BattleRoom] 🔄 Auto-loading file-based session for:', sessionId);

      // @date_of_change 2025-09-17 - AUTO-LOAD: Load session directly without user interaction
      // No confirmation dialog - automatically load from production storage
      handleLoadSession(sessionId)
        .then(result => {
          if (result && result.success) {
            debugLog('[BattleRoom] ✅ File-based session loaded successfully');
            restoreBattleRoomState(result);
            refreshLoadedSessionPresentation(result.sessionData || {});
            showSessionLoadSuccess(result.sessionData || {});
            syncLoadedSessionIntoLiveRoom(result.sessionData);
            } else {
              showSessionLoadError();
            }
          })
          .catch(error => {
            console.error('[BattleRoom] ❌ Failed to load file-based session:', error);
            NotificationManager.error(
              'Session Load Failed',
              error.message || 'Failed to load the selected session.'
            );
          })
          .finally(() => {
            clearDashboardSessionLoadState();
          });
    }
  }, [location.state, loadSessionFromData, handleLoadSession, refreshLoadedSessionPresentation, restoreBattleRoomState, showSessionLoadError, showSessionLoadSuccess, updateRole, clearDashboardSessionLoadState, syncLoadedSessionIntoLiveRoom]);

  const handleGoLive = useCallback(async () => {
    if (effectiveSessionId && (wsSessionStatus === 'joining' || wsSessionStatus === 'joined')) {
      debugLog('[BattleRoom] Go Live skipped - already attached to live session:', effectiveSessionId);
      return;
    }

    try {
      const { apiFetch } = await import('../../../utils/apiFetch.js');
      const rulesetSelection = await fetchDefaultBuiltinRulesetSelection(apiFetch);
      const response = await apiFetch('/api/combat', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Battle Room',
          rulesetSelection
        })
      });

      if (!response.ok) {
        throw new Error(`Create failed: ${response.status}`);
      }

      const sessionData = await response.json();
      const newSessionId = sessionData?._id;
      if (!newSessionId) {
        throw new Error('Live session was created without an ID');
      }

      const joinResponse = await apiFetch(`/api/combat/${newSessionId}/join`, { method: 'POST' });
      if (!joinResponse.ok) {
        throw new Error(`Backend join failed: ${joinResponse.status}`);
      }

      await enableMultiplayerMode(newSessionId);

      const seeded = seedCurrentStateToMultiplayer(newSessionId);
      if (!seeded) {
        throw new Error('Live session seed failed');
      }

      navigate(`/battle/${newSessionId}`, { replace: true });
    } catch (error) {
      console.error('[BattleRoom] Go Live failed:', error);
      NotificationManager.error(
        'Go Live Failed',
        error?.message || 'Failed to open a live battle room. Please try again.'
      );
    }
  }, [effectiveSessionId, enableMultiplayerMode, navigate, seedCurrentStateToMultiplayer, wsSessionStatus]);



  // Use layer state from useBattleRoom hook and enhance it with UI properties
  // @date_of_change: 2025-09-07 - Replace complex state sync with simplified state manager
  const {
    layers,
    activeLayer,
    setActiveLayer,
    updateLayerVisibility,
    updateLayerLocked,
    getAssetDraggableInLayer
  } = useBattleRoomLayers(hookLayers, hookActiveLayer, setHookActiveLayer, setHookLayers);


  // @date_of_change: 2025-09-07 - Removed duplicate getAssetDraggableInLayer - using state manager version

  // @date_of_change: 2025-09-07 - Replace individual UI state with consolidated state manager
  const {
    contextMenu,
    setContextMenu,
    tokenPropertiesPopup,
    setTokenPropertiesPopup,
    multiattackTargetingPopup,
    setMultiattackTargetingPopup,
    selectedTokenIds,
    setSelectedTokenIds,
    setDraggedTokenId,
    targetingEnabled,
    setTargetingEnabled,
    // @date_of_change: 2025-10-08 - Add marquee selection state from UI manager
    selectionRect,
    setSelectionRect,
    isSelecting,
    setIsSelecting,
    
  } = useBattleRoomUIState();
  // @date_of_change: 2025-09-07 - Removed complex state synchronization logic
  // State synchronization is now handled by useBattleRoomLayers hook with single source of truth
  // Drag-start is declared before armedAction in this legacy component, so keep the latest
  // targeting drag lock in a ref rather than referencing later state directly. date_of_change: 2026-06-23
  const actionTargetingDragLockRef = useRef(false);

  const normalizedActiveLayer = normalizeBattleLayerId(activeLayer, 'map');
  const viewerVisibleTokens = useMemo(
    () => getViewerVisibleTokens(tokens, isGameMasterPresentation),
    [tokens, isGameMasterPresentation]
  );
  const tokenLayerTokens = useMemo(
    () => getTokensForBattleLayer(tokens, 'token', isGameMasterPresentation),
    [tokens, isGameMasterPresentation]
  );
  const gameMasterLayerTokens = useMemo(
    () => getTokensForBattleLayer(tokens, 'gamemaster', isGameMasterPresentation),
    [tokens, isGameMasterPresentation]
  );
  const tokenLayerPresentation = useMemo(
    () => buildCoLocatedTokenLayout(tokenLayerTokens, gridSize, selectedTokenId),
    [tokenLayerTokens, gridSize, selectedTokenId]
  );
  const gameMasterLayerPresentation = useMemo(
    () => buildCoLocatedTokenLayout(gameMasterLayerTokens, gridSize, selectedTokenId),
    [gameMasterLayerTokens, gridSize, selectedTokenId]
  );
  const selectedVisibleToken = useMemo(
    () => viewerVisibleTokens.find((token) => token.id === selectedTokenId) || null,
    [viewerVisibleTokens, selectedTokenId]
  );
  const currentCombatant = useMemo(
    () => selectCanonicalCombatantToken(tokens, currentCombatantId),
    [tokens, currentCombatantId]
  );
  const visibleCurrentCombatant = useMemo(
    () => selectCanonicalCombatantToken(viewerVisibleTokens, currentCombatantId),
    [viewerVisibleTokens, currentCombatantId]
  );
  const visibleCurrentCombatantActions = useMemo(
    () => buildCombatActionCatalog(visibleCurrentCombatant),
    [visibleCurrentCombatant]
  );

  // Apply any staged targetingEnabled value after UI state is available
  useEffect(() => {
    if (pendingTargetingEnabledRef.current !== null) {
      try {
        setTargetingEnabled(!!pendingTargetingEnabledRef.current);
      } finally {
        pendingTargetingEnabledRef.current = null;
      }
    }
  }, [setTargetingEnabled]);

  useEffect(() => {
    measureStageContainer();

    const container = stageContainerRef.current;
    if (!container) {
      return undefined;
    }

    const requestFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (callback) => window.setTimeout(callback, 0);
    const cancelFrame = typeof window.cancelAnimationFrame === 'function'
      ? window.cancelAnimationFrame.bind(window)
      : (frameId) => window.clearTimeout(frameId);
    let rafId = requestFrame(measureStageContainer);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        measureStageContainer();
      });
      observer.observe(container);
      return () => {
        cancelFrame(rafId);
        try {
          observer.disconnect();
        } catch (_) {}
      };
    }

    window.addEventListener('resize', measureStageContainer);
    return () => {
      cancelFrame(rafId);
      window.removeEventListener('resize', measureStageContainer);
    };
  }, [measureStageContainer]);

  // @date_of_change: 2025-09-07 - Fix Image component runtime error by using useImage hook
  // Determine the map image URL to load
  const mapImageUrl = useMemo(() => {
    // Live scenes own their background. An older layer placeholder must not
    // hide the persisted map or leak the previous scene's image on transition.
    // App UI Standard, BattleRoom table. date_of_change: 2026-11-04
    if (wsSessionId && authoritativeCombatState?.map) {
      const url = authoritativeCombatState.map.imageUrl;
      return url && !isPlaceholderMapAsset(url) ? resolveUploadAssetUrl(url) : null;
    }
    return [hookLayers?.map?.mapImage, mapImages?.map, hookMapImage]
      .find((url) => url && !isPlaceholderMapAsset(url)) || null;
  }, [authoritativeCombatState?.map, hookMapImage, hookLayers, mapImages, wsSessionId]);

  // Load the image properly using useImage hook for react-konva
  const [mapImage] = useImage(mapImageUrl, getCanvasImageCrossOriginMode(mapImageUrl));

  // @date_of_change 2025-09-18 - FIX: Load token and gamemaster layer images at top level to avoid hooks violations
  const [tokenLayerMapImage] = useImage(
    mapImages?.token || '',
    getCanvasImageCrossOriginMode(mapImages?.token)
  );
  const [gamemasterLayerMapImage] = useImage(
    mapImages?.gamemaster || '',
    getCanvasImageCrossOriginMode(mapImages?.gamemaster)
  );

  useEffect(() => {
    if (mapImageUrl) {
      return;
    }

    const gridSizedBoard = getGridPixelSize({
      gridSize,
      gridWidth,
      gridHeight
    });

    if (
      (Number(mapState?.size?.width) || 0) === gridSizedBoard.width &&
      (Number(mapState?.size?.height) || 0) === gridSizedBoard.height &&
      (Number(mapState?.position?.x) || 0) === 0 &&
      (Number(mapState?.position?.y) || 0) === 0
    ) {
      return;
    }

    viewportAdjustedRef.current = false;
    setMapState((prev) => ({
      ...prev,
      position: { x: 0, y: 0 },
      size: gridSizedBoard
    }));
  }, [gridHeight, gridSize, gridWidth, mapImageUrl, mapState?.position?.x, mapState?.position?.y, mapState?.size?.height, mapState?.size?.width, setMapState]);

  useEffect(() => {
    const mapViewportKey = `${mapImageUrl || 'none'}:${mapState?.size?.width || 0}x${mapState?.size?.height || 0}`;
    if (lastViewportMapKeyRef.current !== mapViewportKey) {
      lastViewportMapKeyRef.current = mapViewportKey;
      viewportAdjustedRef.current = false;
    }
  }, [mapImageUrl, mapState?.size?.height, mapState?.size?.width]);

  useEffect(() => {
    if (!viewportAdjustedRef.current) {
      fitMapInViewport();
    }
  }, [
    fitMapInViewport,
    mapImageUrl,
    mapState?.size?.height,
    mapState?.size?.width,
    stageSize?.height,
    stageSize?.width
  ]);

  useEffect(() => {
    const imageSize = getMapImageDimensions(mapImage);
    if (!mapImageUrl || !imageSize) {
      return;
    }

    if (!shouldRepairMapSize({
      mapSize: mapState?.size,
      imageSize,
      stageSize,
      gridSize,
      gridWidth,
      gridHeight
    })) {
      return;
    }

    const repairedSize = getPreferredMapSize({
      imageSize,
      fallbackSize: stageSize
    });

    if (!repairedSize) {
      return;
    }

    const repairSignature = `${mapImageUrl}:${repairedSize.width}x${repairedSize.height}`;
    if (
      repairedMapSignatureRef.current === repairSignature &&
      mapState?.size?.width === repairedSize.width &&
      mapState?.size?.height === repairedSize.height
    ) {
      return;
    }

    repairedMapSignatureRef.current = repairSignature;
    viewportAdjustedRef.current = false;
    setMapState((prev) => ({
      ...prev,
      size: repairedSize
    }));

    const minimumGridCoverage = getMinimumGridCoverage({
      mapSize: repairedSize,
      gridSize
    });

    if (minimumGridCoverage.columns > gridWidth) {
      setGridWidth(minimumGridCoverage.columns);
      localStorage.setItem('battleRoomGridWidth', String(minimumGridCoverage.columns));
    }

    if (minimumGridCoverage.rows > gridHeight) {
      setGridHeight(minimumGridCoverage.rows);
      localStorage.setItem('battleRoomGridHeight', String(minimumGridCoverage.rows));
    }
  }, [
    gridHeight,
    gridSize,
    gridWidth,
    mapImage,
    mapImageUrl,
    mapState?.size,
    setGridHeight,
    setGridWidth,
    setMapState,
    stageSize
  ]);

  

  // @date_of_change: 2025-09-08 - Add map image selection handler
  const handleMapClick = useCallback((e) => {
    debugLog('[BattleRoom] Map image clicked - selecting map asset');
    // Create a synthetic map asset ID for selection consistency
    const mapAssetId = 'map-background';
    setSelectedTokenId(mapAssetId);
    
    // Stop event propagation to prevent stage deselection
    e.cancelBubble = true;
    if (e.stopPropagation) e.stopPropagation();
  }, [setSelectedTokenId]);

  

  // (moved below to avoid TDZ with setCurrentCombatant)

  // Handle Token Drag Start
  const handleTokenDragStart = useCallback((e) => {
    if (actionTargetingDragLockRef.current) {
      // Target picking is not movement. Keep armed-action clicks from entering drag state.
      // date_of_change: 2026-06-23
      e?.target?.stopDrag?.();
      return;
    }
    debugLog('Token drag start');
    const id = e.target.id();
    const draggedToken = tokens.find(token => token.id === id);

    if (!draggedToken) {
      return;
    }

    setDraggedTokenId(id);
    // @date_of_change: 2025-10-08 - Prepare group drag context for multi-selected tokens
    try {
      // @date_of_change: 2025-10-09 - Disable group movement during combat
      const combatActive = !!CombatLogManager.combatStarted;
      debugLog('[BattleRoom] [GROUP] DragStart base=', id, 'combat=', combatActive, 'selectedIds=', Array.isArray(selectedTokenIds) ? selectedTokenIds : []);
      if (combatActive) {
        // @date_of_change: 2025-10-09 - UX: Inform user group movement is disabled during combat
        if (Array.isArray(selectedTokenIds) && selectedTokenIds.length > 1 && selectedTokenIds.includes(id)) {
          try {
            NotificationManager.info('Group Movement Disabled', 'Combat is active. Move tokens individually on their turns.');
          } catch (_) {}
        }
        groupDragContextRef.current = null;
        return; // single-token drag only when combat is active
      }
      if (Array.isArray(selectedTokenIds) && selectedTokenIds.length > 1 && selectedTokenIds.includes(id)) {
        const baseStart = e.target.position();
        const baseLayer = getTokenLayerId(draggedToken);
        const eligible = selectedTokenIds.filter(selId => {
          if (selId === id) return true;
          const t = tokens.find(tok => tok.id === selId);
          // Only tokens on the same layer as the base dragged token (not UI activeLayer)
          // @date_of_change 2025-10-09 - Prevent layer UI selection from breaking group drag
          const sameLayer = getTokenLayerId(t) === baseLayer;
          // Permission gate (inline logic to avoid TDZ on canDragTokenGuard)
          // @date_of_change 2025-10-09 - Inline ownership/turn gating
          const permitted = (() => {
            if (!t) return false;
            if (!isMultiplayerMode) return true;
            if (isGameMaster) return true;
            const owns = typeof canControlToken === 'function' ? !!canControlToken(t.id) : true;
            const isCombatActive = !!CombatLogManager.combatStarted;
            if (!isCombatActive) return owns; // Outside combat, ownership is sufficient
            if (!currentCombatant) {
              debugLog('[BattleRoom][DRAG-GUARD] Combat active but no currentCombatant yet; allowing owned drag for token', t.id);
              return owns;
            }
            const isUsersTurnForToken = currentCombatant?.id === t.id;
            return owns && isUsersTurnForToken;
          })();
          return sameLayer && permitted;
        });
        debugLog('[BattleRoom] [GROUP] EligibleIds=', eligible);
        const offsets = new Map();
        for (const sid of eligible) {
          if (sid === id) continue;
          const t = tokens.find(tok => tok.id === sid);
          if (!t) continue;
          offsets.set(sid, { dx: (t.x || 0) - baseStart.x, dy: (t.y || 0) - baseStart.y });
        }
        // Store on ref for drag move/end usage
        (groupDragContextRef.current = {
          baseId: id,
          baseStartPos: { x: baseStart.x, y: baseStart.y },
          eligibleIds: eligible,
          offsets
        });
      } else {
        groupDragContextRef.current = null;
      }
    } catch (_) {
      groupDragContextRef.current = null;
    }
  }, [tokens, selectedTokenIds, isMultiplayerMode, isGameMaster, canControlToken, currentCombatant, setDraggedTokenId]);

  // (handler refs assigned after handler definitions below)

  // Removed local updateTokenPosition to centralize updates in useBattleRoom's handleTokenUpdate. 2025-08-22T07:57:04-04:00

  // Utility function to snap coordinates to grid
  const snapToGridUtil = useCallback((pos, gridSize) => {
    if (!gridSize || gridSize <= 0) return pos;
    
    return {
      x: Math.round(pos.x / gridSize) * gridSize,
      y: Math.round(pos.y / gridSize) * gridSize
    };
  }, []);

  // 2025-08-27 — Target + range validation + consume action [DEBUG-TASK:BATTLESYS]
  // --- helpers (place near other helpers) ---
  // @date_of_change: 2025-10-13 - Use hook-provided feetPerCell/gridType from useBattleRoom; removed legacy constants

  

  // Measurement overlay state and helpers
  const [measurementActive, setMeasurementActive] = useState(false);
  const [measureStartCell, setMeasureStartCell] = useState(null);
  const [measureEndCell, setMeasureEndCell] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [sharedPingActive, setSharedPingActive] = useState(false);
  const [sharedPings, setSharedPings] = useState([]);
  const [mapAttentionAnnouncement, setMapAttentionAnnouncement] = useState('');

  // Map resize state and refs
  // @date_of_change 2025-09-18 - FIX: Use mapState.isSelected from hook instead of local state
  // const [mapSelected, setMapSelected] = useState(false);
  

  // Pointer position in unscaled/untranslated stage coordinates
  const getStageLocalPointer = useCallback(() => {
    const stage = stageRef?.current;
    if (!stage) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(pointer);
  }, [stageRef]);

  const getStageLocalPointFromClient = useCallback((clientX, clientY) => {
    const stage = stageRef?.current;
    const container = stageContainerRef?.current;

    if (!stage || !container) {
      return null;
    }

    const rect = container.getBoundingClientRect();
    const pointer = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };

    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(pointer);
  }, [stageRef]);

  const getGridPositionFromStagePoint = useCallback((stagePoint) => {
    if (!stagePoint || gridSize <= 0) {
      return null;
    }

    const mapRelativeX = stagePoint.x - mapState.position.x;
    const mapRelativeY = stagePoint.y - mapState.position.y;

    const { pixelsToGridTarget } = require('../../../utils/GridTargeting');
    return pixelsToGridTarget(mapRelativeX, mapRelativeY, gridSize);
  }, [gridSize, mapState.position.x, mapState.position.y]);

  const getGridPositionFromClientPoint = useCallback((clientX, clientY) => {
    const stagePoint = getStageLocalPointFromClient(clientX, clientY);
    return getGridPositionFromStagePoint(stagePoint);
  }, [getGridPositionFromStagePoint, getStageLocalPointFromClient]);

  const handleMeasurementPointerDown = useCallback((event) => {
    if (!measurementActive || fogEditMode !== 'none' || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const gridInfo = getGridPositionFromClientPoint(event.clientX, event.clientY);
    if (!gridInfo || typeof gridInfo.x !== 'number' || typeof gridInfo.y !== 'number') {
      return;
    }

    const nextCell = { x: gridInfo.x, y: gridInfo.y };
    setMeasureStartCell(nextCell);
    setMeasureEndCell(nextCell);
    setIsMeasuring(true);
  }, [measurementActive, fogEditMode, getGridPositionFromClientPoint]);

  const handleMeasurementPointerMove = useCallback((event) => {
    if (!measurementActive || !isMeasuring || fogEditMode !== 'none') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const gridInfo = getGridPositionFromClientPoint(event.clientX, event.clientY);
    if (!gridInfo || typeof gridInfo.x !== 'number' || typeof gridInfo.y !== 'number') {
      return;
    }

    setMeasureEndCell({ x: gridInfo.x, y: gridInfo.y });
  }, [measurementActive, isMeasuring, fogEditMode, getGridPositionFromClientPoint]);

  const handleMeasurementPointerUp = useCallback((event) => {
    if (!measurementActive || fogEditMode !== 'none') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isMeasuring) {
      const gridInfo = getGridPositionFromClientPoint(event.clientX, event.clientY);
      if (gridInfo && typeof gridInfo.x === 'number' && typeof gridInfo.y === 'number') {
        setMeasureEndCell({ x: gridInfo.x, y: gridInfo.y });
      }
    }

    setIsMeasuring(false);
  }, [measurementActive, fogEditMode, isMeasuring, getGridPositionFromClientPoint]);

  

  const handleToggleMeasurement = useCallback(() => {
    setSharedPingActive(false);
    setMeasurementActive(prev => {
      const next = !prev;
      if (!next) {
        setIsMeasuring(false);
        setMeasureStartCell(null);
        setMeasureEndCell(null);
      } else {
        // fresh start when enabling
        setMeasureStartCell(null);
        setMeasureEndCell(null);
      }
      return next;
    });
  }, []);

  const sharedPingAvailable = isWebSocketConnected && Boolean(wsSessionId);

  const handleToggleSharedPing = useCallback(() => {
    if (!sharedPingAvailable) return;

    const activating = !sharedPingActive;
    setSharedPingActive(activating);
    if (activating) {
      setMeasurementActive(false);
      setIsMeasuring(false);
      setMeasureStartCell(null);
      setMeasureEndCell(null);
      setFogEditMode('none');
      setDrawingEnabled(false);
    }
  }, [sharedPingActive, sharedPingAvailable, setDrawingEnabled]);

  const focusSharedMapPoint = useCallback((ping) => {
    const mapX = Number(ping?.x);
    const mapY = Number(ping?.y);
    const viewportWidth = Number(stageSize?.width);
    const viewportHeight = Number(stageSize?.height);
    const zoomAtReceipt = clampStageZoom(stageZoomRef.current);
    if (
      !Number.isFinite(mapX)
      || !Number.isFinite(mapY)
      || viewportWidth <= 0
      || viewportHeight <= 0
    ) {
      return;
    }

    // Shared focus preserves each participant's chosen zoom while placing the
    // same map-relative location at the center of their own viewport.
    // date_of_change: 2026-08-13
    commitStageViewport({
      nextPosition: {
        x: (viewportWidth / 2) - ((Number(mapState.position?.x || 0) + mapX) * zoomAtReceipt),
        y: (viewportHeight / 2) - ((Number(mapState.position?.y || 0) + mapY) * zoomAtReceipt),
      }
    });
  }, [commitStageViewport, mapState.position, stageSize?.height, stageSize?.width]);

  // Shared pings are transient server events, not saved BattleRoom state. Keeping
  // them out of map persistence also prevents an old session from reviving a ping.
  // date_of_change: 2026-08-11
  useEffect(() => {
    if (!sharedPingAvailable) {
      setSharedPingActive(false);
      setSharedPings([]);
      return undefined;
    }

    return addEventListener('mapPing', (ping) => {
      const receivedAt = Date.now();
      if (
        String(ping?.sessionId || '') !== String(wsSessionId)
        || !ping?.pingId
        || !Number.isFinite(Number(ping?.x))
        || !Number.isFinite(Number(ping?.y))
        || Number(ping?.expiresAt) <= receivedAt
      ) {
        return;
      }

      setSharedPings((current) => [
        ...current.filter((candidate) => {
          if (candidate.pingId === ping.pingId || Number(candidate.expiresAt) <= receivedAt) return false;
          // A continued touch hold upgrades its first ping into one focus marker
          // at the same point instead of stacking two labels/rings.
          // date_of_change: 2026-08-13
          if (
            ping.focusCamera === true
            && candidate.focusCamera !== true
            && candidate.ownerId === ping.ownerId
            && Number(candidate.x) === Number(ping.x)
            && Number(candidate.y) === Number(ping.y)
            && Math.abs(Number(ping.createdAt) - Number(candidate.createdAt)) <= 2000
          ) {
            return false;
          }
          return true;
        }),
        ping
      ]);
      setMapAttentionAnnouncement(
        ping.focusCamera === true
          ? `${String(ping.ownerName || 'A player')} focused everyone's camera on a map location.`
          : `${String(ping.ownerName || 'A player')} pinged a map location.`
      );
      if (ping.focusCamera === true) {
        focusSharedMapPoint(ping);
      }
    });
  }, [addEventListener, focusSharedMapPoint, sharedPingAvailable, wsSessionId]);

  useEffect(() => {
    if (sharedPings.length === 0) return undefined;

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now();
      setSharedPings((current) => {
        const active = current.filter((ping) => Number(ping.expiresAt) > nextNow);
        return active.length === current.length ? current : active;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [sharedPings.length]);

  const loadAuthoritativeRewindPoints = useCallback(async () => {
    if (!wsSessionId || !isGameMaster) return [];
    setRewindPending(true);
    setRewindError('');
    try {
      const response = await apiFetch(`/api/combat/${wsSessionId}/checkpoints`, {
        method: 'GET',
        requireAuth: true,
      });
      const payload = await readBattleRoomApiResponse(response, 'Failed to load authoritative rewind points.');
      const checkpoints = Array.isArray(payload.checkpoints) ? payload.checkpoints : [];
      setAuthoritativeRewindPoints(checkpoints);
      if (Number.isSafeInteger(Number(payload.revision))) setAuthoritativeRewindRevision(Number(payload.revision));
      return checkpoints;
    } catch (cause) {
      setRewindError(cause?.message || 'Failed to load authoritative rewind points.');
      return [];
    } finally {
      setRewindPending(false);
    }
  }, [isGameMaster, wsSessionId]);

  const previewAuthoritativeRewind = useCallback(async (checkpoint) => {
    if (!wsSessionId || !isGameMaster || !checkpoint?.checkpointId) throw new Error('Choose a valid rewind point.');
    const response = await apiFetch(`/api/combat/${wsSessionId}/checkpoints/${encodeURIComponent(checkpoint.checkpointId)}/preview`, {
      method: 'GET',
      requireAuth: true,
    });
    const payload = await readBattleRoomApiResponse(response, 'The rewind preview could not be loaded.');
    return payload.preview;
  }, [isGameMaster, wsSessionId]);

  const handleAuthoritativeRewind = useCallback(async (checkpoint, options = {}) => {
    if (!wsSessionId || !isGameMaster || !checkpoint?.checkpointId) {
      throw new Error('Join a live BattleRoom as Game Master before rewinding.');
    }
    setRewindPending(true);
    setRewindError('');
    try {
      const response = await apiFetch(`/api/combat/${wsSessionId}/rewind`, {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({
          checkpointId: checkpoint.checkpointId,
          expectedRevision: Math.max(Number(authoritativeRewindRevision) || 0, Number(stateRevision) || 0),
          mutationId: createBattleRoomOperationId('rewind'),
          reason: options.reason,
          previewRevision: options.preview?.computedAtRevision,
        }),
      });
      const payload = await readBattleRoomApiResponse(response, 'The BattleRoom could not be rewound.');
      // Rewind now returns a compact durable acknowledgement; the complete
      // recipient-projected room arrives through the existing authoritative
      // socket broadcast. date_of_change: 2026-08-28
      const round = Number(payload?.restoredRound ?? payload?.state?.round ?? checkpoint.round ?? 1);
      const turn = Number(payload?.restoredTurn ?? payload?.state?.turn ?? checkpoint.turn ?? 0);
      CombatLogManager.rewindToAuthoritativePosition(round, turn);
      window.dispatchEvent(new CustomEvent('combat-log-updated', {
        detail: { source: 'authoritative-rewind', timestamp: Date.now() },
      }));
      NotificationManager.success('BattleRoom rewound', `Everyone was restored to Round ${round}, turn ${turn}.`);
      await loadAuthoritativeRewindPoints();
      return { ok: true };
    } catch (cause) {
      const message = cause?.message || 'The BattleRoom could not be rewound.';
      setRewindError(message);
      if (cause?.code === 'REVISION_CONFLICT') await loadAuthoritativeRewindPoints();
      throw new Error(message);
    } finally {
      setRewindPending(false);
    }
  }, [authoritativeRewindRevision, isGameMaster, loadAuthoritativeRewindPoints, stateRevision, wsSessionId]);

  useEffect(() => {
    const handleRecoveredState = () => {
      setRealtimeNotice(getRealtimeMutationFeedback({ code: 'REVISION_CONFLICT' }));
    };
    const handleRejectedMutation = (event) => {
      setRealtimeNotice(getRealtimeMutationFeedback(event?.detail));
    };
    window.addEventListener('battleroom-realtime-recovered', handleRecoveredState);
    window.addEventListener('realtime-mutation-rejected', handleRejectedMutation);
    return () => {
      window.removeEventListener('battleroom-realtime-recovered', handleRecoveredState);
      window.removeEventListener('realtime-mutation-rejected', handleRejectedMutation);
    };
  }, []);

  useEffect(() => {
    if (!wsSessionId || !isGameMaster) {
      setSceneManagerOpen(false);
      setAuthoritativeRewindPoints(null);
    }
  }, [isGameMaster, wsSessionId]);

  

  // mark the acting token's main action as used for this turn
  const [actionsUsed, setActionsUsed] = useState({}); // { tokenId: { main:true } }

  // === Click-to-Target Flow State ===
  // Armed action awaiting explicit target selection and confirmation
  const [armedAction, setArmedAction] = useState(null); // { attackerId, action, multiTarget?, requiredTargets? }
  const [selectedTargets, setSelectedTargets] = useState([]); // array of tokenIds

  // @date_of_change 2025-09-15 - AOE TARGETING SYSTEM
  // AoE targeting state for spells with area effects
  const [aoeTargeting, setAoeTargeting] = useState({
    enabled: false,
    targetPoint: null, // {x, y} grid coordinates
    areaOfEffect: null, // {shape, size} from spell
    direction: 0, // For cones and lines
    targetMode: 'center', // 'center' or 'corner'
    previewPoint: null // {x, y} grid coordinates for mouse hover preview
  });
  
  // === Multiattack Sequence State ===
  // @date_of_change 2025-09-11 - CRITICAL FIX: Stable multiattack state using ref to prevent clearing by hover/selection events
  const multiattackRef = useRef({
    isActive: false,         // true while any multiattack component is armed
    targetId: null,          // sticky target across the sequence
    sequence: null           // { parentAction, componentActions, currentIndex, attackerId, totalAttacks }
  });
  
  // @date_of_change 2025-09-04 - Added for sequential multiattack handling
  const [multiattackSequence, setMultiattackSequence] = useState(null); // { attackerId, componentActions, currentIndex, parentAction }
  const [isInMultiattackMode, setIsInMultiattackMode] = useState(false); // Track if we're in dedicated multiattack mode
  const [multiattackTarget, setMultiattackTarget] = useState(null); // currently selected target for current attack
  actionTargetingDragLockRef.current = !!((targetingEnabled && armedAction) || multiattackSequence);

  // @date_of_change 2025-09-11 - CRITICAL FIX: Proper multiattack sequence ending function
  const endMultiattackSequence = useCallback(() => {
    debugLog('[BattleRoom] 🏁 Ending multiattack sequence completely');
    multiattackRef.current = { isActive: false, targetId: null, sequence: null };
    setIsInMultiattackMode(false);
    setMultiattackTarget(null);
    setMultiattackSequence(null);
    setSelectedTargets([]); // UI reset is fine here
    setArmedAction(null);
  }, []);

  // (removed unused handleArmAction)

  // Toggle a token as target while targeting is armed or during multiattack sequence
  const handleToggleTarget = useCallback((tokenId, evt) => {
    // Handle multiattack sequence target selection
    if (multiattackSequence) {
      if (!tokenId) return;
      // Prevent self-targeting unless explicitly allowed
      if (tokenId === multiattackSequence.attackerId) {
        NotificationManager.warning('Invalid target', 'Cannot target the acting token.');
        return;
      }
      // Set the target for multiattack sequence using existing state management
      const targetToken = tokens.find(t => t.id === tokenId);
      if (targetToken) {
        debugLog('[BattleRoom] 🎯 MULTIATTACK_TARGET_SET: Setting multiattackTarget to:', targetToken.name, targetToken.id);
        // Store in stable ref (durable across re-renders)
        multiattackRef.current.targetId = targetToken.id;
        // Also update reactive state for UI highlights
        setMultiattackTarget(targetToken);
        // Keep selectedTargets UI array in sync
        setSelectedTargets([targetToken.id]);
      }
      return;
    }
    
    // Handle regular armed action target selection
    if (!armedAction) return;
    if (!tokenId) return;
    const targetToken = tokens.find((token) => token.id === tokenId);
    if (armedAction.targetType === 'object' && !isSceneObjectToken(targetToken)) {
      NotificationManager.warning('Select an object', 'Use an Object can target object tokens only.');
      return;
    }
    // Prevent self-targeting unless explicitly allowed for this armed action
    if (tokenId === armedAction.attackerId) {
      const allowSelf = !!(armedAction.allowSelf);
      if (!allowSelf) {
        NotificationManager.warning('Invalid target', 'Cannot target the acting token.');
        return;
      }
    }
    const action = armedAction.action || armedAction;
    if (targetToken && RangeCalculator.isTargetedCreatureSpell(action) && !selectedTargets.includes(tokenId)) {
      const caster = tokens.find((token) => token.id === armedAction.attackerId);
      if (caster) {
        const range = RangeCalculator.validateCreatureSpellTarget(caster, targetToken, action, { gridSize, gridType, feetPerCell });
        if (!range.valid) {
          NotificationManager.warning('Target unavailable', `${targetToken.name} cannot be targeted by ${action.name} from here. ${range.reasons.join('. ')}.`);
          return;
        }
      }
    }
    const isMultiKey = !!(evt?.ctrlKey || evt?.metaKey || evt?.shiftKey);
    setSelectedTargets(prev => {
      const exists = prev.includes(tokenId);
      if (armedAction.multiTarget || isMultiKey) {
        const max = Number.isFinite(armedAction.allowedTargets) ? armedAction.allowedTargets : Infinity;
        if (!exists && prev.length >= max) {
          NotificationManager.warning('Target limit reached', `You can select up to ${max} target(s).`);
          return prev;
        }
        return exists ? prev.filter(id => id !== tokenId) : [...prev, tokenId];
      }
      // Single-target selection
      return exists ? [] : [tokenId];
    });
  }, [armedAction, feetPerCell, gridSize, gridType, multiattackSequence, selectedTargets, tokens]);

  const handleFamiliarAction = useCallback(async ({ actor: requestedActor, action } = {}) => {
    const actor = tokens.find((token) => token.id === requestedActor?.id) || requestedActor;
    const familiar = tokens.find((token) => token.id === action?.familiarTokenId);
    if (!actor || !familiar || !action?.familiarAction) {
      NotificationManager.error('Familiar action unavailable', 'VCS could not find the familiar and its owner.');
      return false;
    }
    if (action.familiarAction === 'perceive' && (
      !isFamiliarPresent(familiar)
      || distanceFeetBetweenTokens(actor, familiar, gridSize, feetPerCell) > 100
    )) {
      NotificationManager.warning('Familiar out of range', 'The familiar must be present and within 100 feet to share its senses.');
      return false;
    }

    const economyType = action.actionType === 'bonus_action' ? 'bonus_action' : 'action';
    const economy = consumeActionEconomy(actor.id, economyType, action.name);
    if (!economy?.success) {
      NotificationManager.warning('Action unavailable', economy?.reason || `${action.name} is not available.`);
      return false;
    }

    const actorRuntimeUpdate = {
      combatFlags: {
        ...(actor.combatFlags || {}),
        ...(economyType === 'bonus_action' ? { bonusActionUsed: true } : { actionUsed: true })
      }
    };
    const tokenUpdates = [];

    if (action.familiarAction === 'perceive') {
      Object.assign(actorRuntimeUpdate, {
        statusEffects: { ...(actor.statusEffects || {}), familiar_senses: true },
        statusEffectsMeta: {
          ...(actor.statusEffectsMeta || {}),
          familiar_senses: { sourceId: familiar.id, roundsRemaining: 1 },
        },
      });
    } else if (action.familiarAction === 'dismiss') {
      tokenUpdates.push({
        id: familiar.id,
        props: {
          familiarState: 'dismissed',
          isDismissed: true,
          visible: false,
          opacity: 0
        },
        _engineUpdate: true
      });
    } else if (action.familiarAction === 'reappear') {
      const position = findFamiliarReappearancePosition(actor, tokens, gridSize, feetPerCell);
      tokenUpdates.push({
        id: familiar.id,
        props: {
          ...position,
          grid: { x: position.gridX, y: position.gridY },
          familiarState: 'present',
          isDismissed: false,
          visible: true,
          opacity: 1
        },
        _engineUpdate: true
      });
    }

    tokenUpdates.unshift({ id: actor.id, props: actorRuntimeUpdate, _engineUpdate: true });
    const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
    if (!committed) {
      ActionEconomyManager.synchronizeCombatFlags(
        actor.id,
        actor.combatFlags || {},
        ActionEconomyProcessor.parseSpeedFeet(actor?.speed)
      );
      NotificationManager.error('Familiar action not committed', 'Authoritative VCS state did not confirm the familiar action. No combat log entry was created.');
      return false;
    }

    CombatLogManager.logEntry({
      type: 'FAMILIAR_ACTION',
      round: CombatLogManager.currentRound || 1,
      turn: CombatLogManager.currentTurn || 0,
      combatantId: actor.id,
      data: { actorName: actor.name, action: action.name, familiarTokenId: familiar.id },
      description: `${actor.name} uses ${action.name}.`,
      timestamp: new Date().toISOString(),
    });
    NotificationManager.success('Familiar action', `${action.name} resolved.`);
    return true;
  }, [tokens, gridSize, feetPerCell, consumeActionEconomy, handleBatchTokenUpdates]);

  // Handle actions wrapper for ActionTracker
  const handleActionTaken = useCallback(async (requestOrAttackerId) => {
    const preliminaryRequest = normalizeCombatActionRequest(requestOrAttackerId);
    const ownerAttacker = selectCanonicalCombatantToken(
      tokens,
      preliminaryRequest.attackerId || preliminaryRequest.attacker
    );
    if (!ownerAttacker || !preliminaryRequest.attackerId) return false;
    const deliveryFamiliar = preliminaryRequest.actionData?.familiarDelivery
      ? tokens.find((token) => token.id === preliminaryRequest.actionData.familiarTokenId)
      : null;
    if (preliminaryRequest.actionData?.familiarDelivery && (
      !isFamiliarPresent(deliveryFamiliar)
      || distanceFeetBetweenTokens(ownerAttacker, deliveryFamiliar, gridSize, feetPerCell) > 100
    )) {
      NotificationManager.warning('Familiar delivery unavailable', 'The familiar must be present and within 100 feet to deliver this touch spell.');
      return false;
    }
    const attacker = preliminaryRequest.actionData?.familiarDelivery
      ? buildFamiliarOriginAttacker(ownerAttacker, deliveryFamiliar)
      : ownerAttacker;

    let actionRequest = normalizeCombatActionRequest(preliminaryRequest, { attacker });
    let { actionData, targetIds } = actionRequest;
    // The UI can request an override, but only this role-aware BattleRoom
    // boundary may create the engine capability.
    // date_of_change: 2026-07-27
    const engineGmOverride = createAuthorizedGmOverride(actionRequest.gmOverride, isGameMaster);

    const visibleIntentTargets = tokens
      .filter((token) => token?.id && token.id !== attacker.id && canViewerSeeToken(token, isGameMaster))
      .map((token) => ({ id: token.id, name: token.name || 'Unnamed target' }));
    const selectedIntentTargetId = [selectedTokenId, ...(Array.isArray(selectedTargets) ? selectedTargets : [])]
      .map((value) => (typeof value === 'object' ? value?.id : value))
      .find((id) => visibleIntentTargets.some((target) => target.id === id));
    const normalizedActionName = String(actionData?.name || actionRequest.actionName || '').trim().toLowerCase();
    const actionType = String(actionRequest.engineActionType || actionData?.actionType || actionData?.type || '').toLowerCase();
    const hasStructuredMechanics = Boolean(
      actionData?.attack
      || actionData?.type === 'attack'
      || actionData?.type === 'weapon'
      || actionData?.type === 'spell'
      || actionData?.castingTime
      || actionData?.components
      || actionData?.save
      || actionData?.savingThrow
      || actionData?.saveDC
      || actionData?.check
      || actionData?.skillCheck
      || actionData?.abilityCheck
      || actionData?.dashAction
      || actionData?.mechanics
      || actionData?.featureKey
      || (
        normalizedActionName === 'rage'
        && actionData?.type === 'feature'
        && actionData?.uses
      )
      || /^steady aim$/i.test(String(actionData?.name || ''))
    );
    const isSpeech = normalizedActionName === 'speak';
    const needsDeclaredIntent = !isSpeech && (
      (!hasStructuredMechanics && ['bonus_action', 'reaction', 'free_action', 'free_object'].includes(actionType))
      || ['help', 'ready'].includes(normalizedActionName)
    );

    if (isSpeech || needsDeclaredIntent) {
      const intent = await requestInteractionIntent({
        title: isSpeech ? 'Speak in the scene' : `Declare ${actionData?.name || 'interaction'}`,
        description: isSpeech
          ? 'Enter the exact words spoken and choose who they are directed toward.'
          : 'This character data describes the feature but does not contain executable mechanics. Record the exact target and intent for GMA to resolve—VCS will not invent a roll, DC, movement, or effect.',
        textLabel: isSpeech ? 'What do you say?' : 'What exactly are you trying to do?',
        placeholder: isSpeech
          ? 'Halt, or be killed.'
          : 'Describe the intended effect, direction, destination, or object interaction.',
        confirmLabel: isSpeech ? 'Speak' : 'Record for GMA',
        footerNote: isSpeech
          ? 'The exact words and audience will appear in the combat log and GMA handoff.'
          : 'This records a declaration, not a fabricated mechanical success.',
        untargetedLabel: isSpeech ? 'Everyone who can hear' : 'No specific target',
        targets: visibleIntentTargets,
        initialTargetId: selectedIntentTargetId || '__audible__'
      });
      if (!intent) return false;

      actionData = isSpeech
        ? {
            ...actionData,
            speech: {
              ...intent,
              speakerId: attacker.id,
              speakerName: attacker.name,
              audience: intent.targetId ? 'target' : 'audible'
            }
          }
        : {
            ...actionData,
            interactionIntent: {
              ...intent,
              actorId: attacker.id,
              actorName: attacker.name,
              featureName: actionData?.name || actionRequest.actionName
            }
          };
      actionRequest = normalizeCombatActionRequest({ ...actionRequest, actionData }, { attacker });
      targetIds = actionRequest.targetIds;
    }
    
    debugLog(`[BattleRoom] Action taken: ${actionData.name} by ${attacker.name}`);
    
    debugLog('[BattleRoom] ACTION_ROUTING_DEBUG:', {
      actionName: actionData.name,
      route: actionRequest.route,
      hasAttackProperty: !!actionData.attack,
      hasTypeProperty: !!actionData.type,
      actionType: actionData.type,
      hasAreaOfEffect: actionRequest.hasAreaOfEffect,
      targetIds,
      fullActionData: actionData
    });

    if (actionRequest.hasAreaOfEffect && !actionRequest.hasExplicitTargets) {
      const aoeInfo = actionRequest.areaOfEffect;
      debugLog(`[BattleRoom] AoE detected (structured) for ${actionData.name} - enabling AoE targeting`);
      setAoeTargeting({
        enabled: true,
        targetPoint: null,
        areaOfEffect: aoeInfo,
        direction: 0,
        targetMode: 'center',
        spell: actionData,
        caster: attacker,
        gmOverride: engineGmOverride,
        previewPoint: null
      });

      NotificationManager.info(
        `AoE Targeting Active`,
        `Click on the grid to target ${actionData.name} (${aoeInfo.shape}, ${aoeInfo.size ?? aoeInfo.length} ft)`
      );
      return true; // Exit early when structured AoE targeting is active
    }

    if (
      actionRequest.route === 'standard'
      && actionRequest.requiresBattleRoomTargeting
      && isUseObjectAction(actionData)
    ) {
      setSelectedTargets([]);
      setArmedAction({
        attackerId: attacker.id,
        attacker,
        action: actionData,
        targetType: 'object',
        multiTarget: false,
        allowedTargets: 1,
        requiredTargets: 1,
        allowNamedTarget: true,
        gmOverride: actionRequest.gmOverride
      });
      setTargetingEnabled(true);
      setMultiattackTargetingPopup((previous) => ({ ...previous, visible: true }));
      NotificationManager.info('Choose an object', 'Click an object token, or enter the name of an object without a token.');
      return true;
    }

    if (
      actionRequest.route === 'ability'
      && actionRequest.requiresBattleRoomTargeting
      && !actionRequest.hasExplicitTargets
    ) {
      // Structured class features use the same explicit, accessible targeting
      // transaction as attacks and spells. Description-only features still
      // fail closed in ActionEconomyProcessor.
      // date_of_change: 2026-08-20
      setSelectedTargets([]);
      setArmedAction({
        attackerId: attacker.id,
        attacker,
        action: actionData,
        multiTarget: false,
        allowedTargets: 1,
        requiredTargets: 1,
        allowSelf: actionData?.allowSelf === true,
        gmOverride: engineGmOverride
      });
      setTargetingEnabled(true);
      setMultiattackTargetingPopup((previous) => ({ ...previous, visible: true }));
      NotificationManager.info(
        'Choose a target',
        `${actionData.name}: select one creature${actionData?.allowSelf ? ', including yourself' : ''}.`
      );
      return true;
    }

    try {
      return await executeCombatActionRequest(actionRequest, {
        onSpell: async (request) => {
          debugLog('[BattleRoom] Routing to handleSpellAction');
          if (handleSpellActionRef.current) {
            return handleSpellActionRef.current(attacker, request.actionData, request.targetIds, engineGmOverride);
          }
          return false;
        },
        onAttack: async (request) => {
          debugLog('[BattleRoom] Routing to handleAttackAction');
          debugLog('[BattleRoom] handleAttackAction parameters:', {
            attackerId: request.attackerId,
            actionName: request.actionData?.name,
            targetIds: request.targetIds,
            actionDataType: typeof request.actionData,
            targetIdsType: typeof request.targetIds,
            targetIdsLength: request.targetIds?.length
          });
          if (handleAttackActionRef.current) {
            const attackResult = await handleAttackActionRef.current(
              request.attackerId,
              request.actionData,
              request.targetIds,
              engineGmOverride
            );
            if (attackResult === 'retarget') {
              debugLog('[BattleRoom] handleAttackAction requested retargeting');
              return attackResult;
            }
            if (!attackResult) {
              debugLog('[BattleRoom] handleAttackAction reported failure');
              return false;
            }
            debugLog('[BattleRoom] handleAttackAction completed successfully');
            return attackResult;
          }
          return false;
        },
        onAbility: async (request) => {
          debugLog('[BattleRoom] Routing to handleAbilityAction');
          if (handleAbilityActionRef.current) {
            return handleAbilityActionRef.current(attacker, request.actionData, request.targetIds, engineGmOverride);
          }
          return false;
        },
        onStandard: async (request) => {
          debugLog('[BattleRoom] Routing standard action through ActionEconomyProcessor');

          let executionAttacker = attacker;
          let executionTokens = tokens;
          let executionRulesetState = rulesetExecutionState;
          if (wsSessionId && request.actionData?.mechanics) {
            const preparedResolution = await prepareCreatureResolution();
            if (!preparedResolution) return false;
            executionTokens = preparedResolution.tokens;
            executionRulesetState = preparedResolution.rulesetExecutionState;
            executionAttacker = executionTokens.find((token) => token?.id === attacker.id);
            if (!executionAttacker) {
              NotificationManager.warning('Action not available', 'The acting creature is no longer on the battlefield. Review the board and choose another action.');
              return false;
            }
          }

          const executionBundle = buildCombatExecutionBundle(request, {
            attacker: executionAttacker,
            tokens: executionTokens,
            targetIds: request.targetIds,
            gridSize,
            feetPerCell,
            gridType,
            extraCombatState: {
              round: CombatLogManager.currentRound || 1,
              turn: CombatLogManager.currentTurn || 0,
              deferAudit: true,
              ...executionRulesetState
            }
          });

          const processorResult = await ActionEconomyProcessor.processAction(
            executionBundle.processorRequest,
            executionBundle.combatState,
            engineGmOverride
          );

          if (!processorResult.success) {
            if (processorResult.cancelled === true || processorResult.code === 'ACTION_CANCELLED') {
              NotificationManager.info(
                'Action canceled',
                `${request.actionData?.name || 'Action'} was not resolved and no action economy was spent.`
              );
              return false;
            }
            const errorMessage = formatCombatActionError(processorResult);
            NotificationManager.error('Action failed', `${request.actionData?.name || 'Action'}: ${errorMessage}`);
            return false;
          }

          const combatStageUpdates = processorResult.combatStageUpdates || {};
          const tokenUpdates = [];
          const attackerUpdates = buildMergedCombatStageUpdate(executionAttacker, combatStageUpdates.attacker);
          if (attackerUpdates) {
            tokenUpdates.push({
              id: executionAttacker.id,
              props: attackerUpdates,
              _engineUpdate: true
            });
          }

          if (Array.isArray(combatStageUpdates.targets)) {
            combatStageUpdates.targets.forEach((targetUpdate) => {
              const targetToken = executionTokens.find((token) => token?.id === targetUpdate?.id);
              if (!targetToken) {
                return;
              }

              const mergedTargetUpdate = buildMergedCombatStageUpdate(targetToken, targetUpdate);
              if (!mergedTargetUpdate) {
                return;
              }

              const stateOnlyUpdate = wsSessionId ? withoutHitPointPatch(mergedTargetUpdate) : mergedTargetUpdate;
              if (Object.keys(stateOnlyUpdate).length === 0) return;

              tokenUpdates.push({
                id: targetToken.id,
                props: stateOnlyUpdate,
                _engineUpdate: true
              });
            });
          }

          const atomicCreature = Boolean(wsSessionId && request.actionData.mechanics);
      if (atomicCreature && !await commitCreatureResolution(executionAttacker, request.actionData, processorResult, tokenUpdates, processorResult.results || processorResult.affectedTargets || [], executionBundle.combatState, engineGmOverride)) return false;
      if (!atomicCreature && tokenUpdates.length > 0) {
            const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
            if (!committed) {
              ActionEconomyManager.synchronizeCombatFlags(
                executionAttacker.id,
                executionAttacker.combatFlags || {},
                ActionEconomyProcessor.parseSpeedFeet(executionAttacker?.speed)
              );
              NotificationManager.error(
                'Action not committed',
                `${request.actionData?.name || 'Action'} was not recorded because authoritative VCS state did not confirm the change. No combat log entry was created.`
              );
              return false;
            }
          }

          if (!atomicCreature && wsSessionId && processorResult.disengaged === true) {
            const spentBonusAction = attackerUpdates?.combatFlags?.bonusActionUsed === true
              && executionAttacker?.combatFlags?.bonusActionUsed !== true;
            try {
              await sendBattleRoomMutation('combat:disengage', {
                tokenId: executionAttacker.id,
                actionType: spentBonusAction ? 'bonus_action' : 'action',
              });
            } catch (cause) {
              NotificationManager.error('Disengage not committed', cause?.message || 'The server did not confirm Disengage.');
              return false;
            }
          }

          try {
            if (!atomicCreature) await applyAuthoritativeDamageOutcomes(executionAttacker.id, processorResult.results || processorResult.affectedTargets || [], request.actionData?.name);
            if (!atomicCreature) await applyAuthoritativeHealingOutcomes(executionAttacker.id, processorResult.results || processorResult.affectedTargets || []);
          } catch (cause) {
            NotificationManager.error('Damage not committed', cause?.message || `${request.actionData?.name || 'Action'}: the server rejected the damage result.`);
            return false;
          }

          if (!atomicCreature || request.actionData.mechanics.kind !== 'attack') ActionEconomyProcessor.logAction(
            processorResult,
            executionAttacker,
            executionBundle.processorRequest.targets || [],
            engineGmOverride,
            executionBundle.processorRequest,
            executionBundle.combatState
          );

          NotificationManager.info(
            processorResult.requiresGameMasterResolution ? 'Intent recorded' : (processorResult.resolutionStatus === 'recorded' ? 'Interaction recorded' : 'Action resolved'),
            `${executionAttacker.name}: ${processorResult.effect || request.actionData?.name || 'Action resolved'}`
          );
          // ActionEconomyProcessor already emitted the rules-complete audit event.
          // A second generic ACTION_RESOLVED record dropped check/roll details and
          // filled unrelated object fields with null, producing contradictory logs.
          // date_of_change: 2026-07-18
          return {
            processedByActionEconomy: true,
            processorResult
          };
        }
      });
    } catch (error) {
      console.error('[BattleRoom] ❌ handleActionTaken failed:', error);
      console.error('[BattleRoom] ❌ Error stack:', error.stack);
      NotificationManager.error(`Action failed: ${error.message}`);
      return false;
    }
  }, [
    tokens,
    handleBatchTokenUpdates,
    gridSize,
    feetPerCell,
    gridType,
    isGameMaster,
    requestInteractionIntent,
    selectedTargets,
    selectedTokenId,
    setTargetingEnabled,
    setMultiattackTargetingPopup,
    prepareCreatureResolution,
    commitCreatureResolution,
    applyAuthoritativeDamageOutcomes,
    applyAuthoritativeHealingOutcomes,
    sendBattleRoomMutation,
    wsSessionId,
    rulesetExecutionState
  ]);

  // Confirm armed action and resolve against selected targets
  // @date_of_change 2025-09-14 - Accept optional payload from MultiattackTargetingPopup
  // Currently used for Magic Missile dart allocation: payload = { dartAllocation }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleConfirmTargets = useCallback(async (payload = {}) => {
    debugLog('[BattleRoom] EXECUTE BUTTON CLICKED - handleConfirmTargets called');
    debugLog('[BattleRoom] Armed action:', armedAction);
    debugLog('[BattleRoom] Selected targets:', selectedTargets);
    
    if (!armedAction) {
      debugLog('[BattleRoom] No armed action - returning early');
      return;
    }
    // @date_of_change 2025-09-09 - CRITICAL FIX: Extract attackerId properly from armedAction structure
    const attackerId = armedAction.attackerId || armedAction.attacker?.id || armedAction.action?.attackerId;
    let action = armedAction.action || armedAction;
    const objectTargeting = isUseObjectAction(action);
    // Inject per-target allocation for multi-component auto-hit spells (e.g., Magic Missile)
    if (payload?.dartAllocation && action?.type === 'spell') {
      action = { 
        ...action,
        dartAllocation: payload.dartAllocation,
        options: { ...(action.options || {}), dartAllocation: payload.dartAllocation }
      };
    }
    
    debugLog('[BattleRoom] Extracted attackerId:', attackerId);
    debugLog('[BattleRoom] Extracted action:', action?.name);
    // @date_of_change: 2025-09-11 - CRITICAL FIX: Use stable ref as fallback for multiattack targeting
    const activeMulti = Boolean(multiattackRef.current.isActive);
    const stickyTargetId = multiattackRef.current.targetId;
    
    debugLog('[BattleRoom] MULTIATTACK_STATE_DEBUG:', {
      target: stickyTargetId || null,
      sequence: multiattackRef.current.sequence || null,
      isInMultiattackMode: activeMulti
    });
    
    // 1) Prefer explicit selection
    let effectiveTargets = [...selectedTargets];
    
    // 2) Fallback to sticky multiattack target if none explicitly selected
    if ((!effectiveTargets || effectiveTargets.length === 0) && stickyTargetId) {
      effectiveTargets = [stickyTargetId];
      debugLog(`[BattleRoom] Using sticky multiattack target as fallback: ${stickyTargetId}`);
      // Keep UI selection in sync so subsequent code sees it as "selected"
      setSelectedTargets(effectiveTargets);
    }

    if (objectTargeting) {
      const targetToken = effectiveTargets.length > 0
        ? tokens.find((token) => token.id === effectiveTargets[0])
        : null;
      try {
        action = buildObjectInteractionAction(action, {
          targetToken,
          objectName: payload?.objectName
        });
      } catch (error) {
        NotificationManager.warning('Choose an object', error.message);
        return;
      }

      // A scene object's interaction hint is descriptive data, not an
      // executable mechanic. Capture the player's exact intent so GMA can
      // adjudicate and commit any resulting world-state change.
      // date_of_change: 2026-07-18
      const intent = await requestInteractionIntent({
        title: `Use ${action.objectName}`,
        description: 'Describe the exact interaction you want GMA to resolve. VCS will not treat an object hint as a completed world-state change.',
        textLabel: 'What exactly do you do with the object?',
        placeholder: action.objectEffect || `Describe how you use ${action.objectName}.`,
        confirmLabel: 'Record for GMA',
        footerNote: 'This records a declaration, not a fabricated object-state change.',
        untargetedLabel: action.objectName,
        targets: targetToken ? [{ id: targetToken.id, name: targetToken.name || action.objectName }] : [],
        initialTargetId: targetToken?.id || '__audible__'
      });
      if (!intent) return;

      action = {
        ...action,
        interactionIntent: {
          ...intent,
          actorId: attackerId,
          featureName: 'Use an Object',
          objectId: action.objectId || null,
          objectTokenId: action.objectTokenId || null,
          objectName: action.objectName
        }
      };
    }
    if (payload?.cunningStrike) {
      // Cunning Strike is an explicit pre-roll choice attached to this attack;
      // the rules engine validates feature ownership, Sneak Attack eligibility,
      // die cost, save, size, condition, and movement consequences.
      // rule_reference: 2024 PHB Rogue — Cunning Strike.
      // date_of_change: 2026-07-18
      action = { ...action, cunningStrike: { ...payload.cunningStrike } };
    }
    if (payload?.resourceSpend !== undefined) {
      action = { ...action, resourceSpend: payload.resourceSpend };
    }
    
    if ((!effectiveTargets || effectiveTargets.length === 0) && !objectTargeting) {
      debugLog('[BattleRoom] No targets selected - showing warning');
      NotificationManager.warning('Select target(s) first.');
      return;
    }
    
    // Use effectiveTargets instead of targets from here on
    const targets = effectiveTargets;
    
    if (!attackerId) {
      debugLog('[BattleRoom] No attackerId found in armedAction structure');
      NotificationManager.error('Cannot identify attacker for action.');
      return;
    }
    
    const attacker = selectCanonicalCombatantToken(tokens, attackerId);
    if (!attacker) {
      debugLog('[BattleRoom] Attacker not found - showing error, attackerId:', attackerId);
      debugLog('[BattleRoom] Available tokens:', tokens.map(t => ({id: t.id, name: t.name})));
      NotificationManager.error('Attacker not found for action.');
      return;
    }
    debugLog('[BattleRoom] Confirming armed action:', { attackerId, action, targets });
    
    // @date_of_change 2025-09-05 - Handle multiattack sequences properly
    // Check if this is part of an active multiattack sequence
    if (multiattackSequence && multiattackSequence.attackerId === attackerId) {
      debugLog('[BattleRoom] Confirming target for multiattack component');
      // Execute the current multiattack component directly with the selected target
      const targetToken = tokens.find(t => t.id === targets[0]);
      if (targetToken) {
        const { componentActions, currentIndex } = multiattackSequence;
        const currentAction = componentActions[currentIndex];

        if (currentAction) {
          debugLog(`[BattleRoom] Executing multiattack component ${currentIndex + 1}:`, currentAction.name);

          // @date_of_change 2025-09-10 - PHASE ONE: Route multiattack components through ActionEconomyProcessor
          let componentResult;
          let componentProcessorResult = null;
          let componentExecutionBundle = null;
          try {
            const executionBundle = buildCombatExecutionBundle({
              attackerId,
              attacker,
              actionData: currentAction,
              targetIds: [targetToken.id]
            }, {
              attacker,
              tokens,
              gridSize,
              gridType,
              feetPerCell,
              actionEconomyConsumed: true,
              extraCombatState: {
                isMultiattackComponent: true,
                multiattackSequence,
                deferAudit: true,
                ...rulesetExecutionState
              },
              actionDataOverrides: {
                isMultiattackComponent: true,
                parentAction: multiattackSequence.parentAction,
                ...(payload?.dartAllocation && { dartAllocation: payload.dartAllocation })
              }
            });

            // For multiattack components, we typically don't consume additional action economy
            // since the main action was consumed when the multiattack was initiated
            const gmOverride = ActionEconomyProcessor.createGMOverride({
              note: `Multiattack component: ${currentAction.name}`,
              ignoreActionEconomy: true, // Components don't consume extra actions
              ignoreDataValidation: false,
              ignoreHandlerValidation: false
            });

            debugLog(`[BattleRoom] Processing multiattack component through ActionEconomyProcessor`);
            const processorResult = await ActionEconomyProcessor.processAction(
              executionBundle.processorRequest,
              executionBundle.combatState,
              gmOverride
            );
            
            if (processorResult.success) {
              componentResult = extractPrimaryProcessorOutcome(processorResult);
              componentProcessorResult = processorResult;
              componentExecutionBundle = executionBundle;
            } else {
              console.error(`[BattleRoom] ActionEconomyProcessor failed for ${currentAction.name}:`, processorResult.error);
              NotificationManager.error('Multiattack component failed', processorResult.error || `${currentAction.name} could not be resolved.`);
              return;
            }
          } catch (error) {
            console.error(`[BattleRoom] Error processing multiattack component:`, error);
            // A second resolver would roll again and could diverge from the
            // processor audit. Fail closed and let the player retry the same
            // canonical component. date_of_change: 2026-07-18
            NotificationManager.error('Multiattack component failed', error?.message || `${currentAction.name} could not be resolved.`);
            return;
          }
          
          const attackResult = componentResult; // Keep variable name for compatibility

          if (componentProcessorResult && componentExecutionBundle) {
            const tokenUpdates = [];
            const componentAttackerUpdate = buildMergedCombatStageUpdate(
              attacker,
              componentProcessorResult.combatStageUpdates?.attacker
            );
            if (componentAttackerUpdate) tokenUpdates.push({ id: attacker.id, props: componentAttackerUpdate, _engineUpdate: true });
            (componentProcessorResult.combatStageUpdates?.targets || []).forEach((targetUpdate) => {
              const token = tokens.find((entry) => entry?.id === targetUpdate?.id);
              const merged = buildMergedCombatStageUpdate(token, targetUpdate);
              const stateOnlyUpdate = wsSessionId ? withoutHitPointPatch(merged) : merged;
              if (token && stateOnlyUpdate && Object.keys(stateOnlyUpdate).length > 0) tokenUpdates.push({ id: token.id, props: stateOnlyUpdate, _engineUpdate: true });
            });
            (componentProcessorResult.results || []).forEach((outcome) => {
              if (!outcome?.updatedTarget) return;
              const token = tokens.find((entry) => entry?.id === (outcome.targetId || outcome.updatedTarget.id));
              if (!token) return;
              const updated = outcome.updatedTarget;
              const newHp = updated.hpObj?.current ?? updated.hp ?? token.hpObj?.current ?? token.hp;
              const maxHp = updated.hpObj?.max ?? updated.maxHp ?? token.hpObj?.max ?? token.maxHp;
              if (!wsSessionId) tokenUpdates.push({
                id: token.id,
                props: {
                  hpObj: { ...(token.hpObj || {}), current: newHp, ...(maxHp !== undefined ? { max: maxHp } : {}) },
                  hp: newHp,
                  ...(maxHp !== undefined ? { maxHp } : {})
                },
                _engineUpdate: true
              });
            });
            if (tokenUpdates.length > 0) {
              const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
              if (!committed) {
                NotificationManager.error('Multiattack component not committed', `${currentAction.name}: authoritative VCS state did not confirm the result.`);
                return;
              }
            }
            try {
              await applyAuthoritativeDamageOutcomes(attacker.id, componentProcessorResult.results || [], currentAction.name);
              await applyAuthoritativeHealingOutcomes(attacker.id, componentProcessorResult.results || []);
            } catch (cause) {
              NotificationManager.error('Multiattack damage not committed', cause?.message || `${currentAction.name}: the server rejected the damage result.`);
              return;
            }
            ActionEconomyProcessor.logAction(
              componentProcessorResult,
              attacker,
              componentExecutionBundle.processorRequest.targets || [],
              null,
              componentExecutionBundle.processorRequest,
              componentExecutionBundle.combatState
            );
          }

          // @date_of_change 2025-09-14 - REMOVED: Duplicate combat logging for multiattack components
          // ActionEconomyProcessor already handles all combat logging properly, including multiattack metadata
          // Removing this duplicate logging to prevent duplicate entries in combat log

          // Apply damage/effects based on result type
          // @date_of_change 2025-09-06 - Handle both attack results and save results
          if (!componentProcessorResult && currentAction.save) {
            // Save-based ability - apply effects based on save result
            if (!componentResult.success && componentResult.damage > 0) {
              debugLog(`[BattleRoom] ${targetToken.name} failed save, taking ${componentResult.damage} damage`);
              if (wsSessionId) {
                NotificationManager.error('Damage not committed', `${currentAction.name} did not produce the structured server damage contract.`);
                return;
              }
              handleTokenUpdate(targetToken.id, { hp: Math.max(0, targetToken.hp - componentResult.damage) });
            } else {
              debugLog(`[BattleRoom] ${targetToken.name} ${componentResult.success ? 'succeeded' : 'failed'} save for ${currentAction.name}`);
            }
          } else if (!componentProcessorResult) {
            // Attack-based action - apply damage if hit
            if (attackResult.hit && attackResult.damage > 0) {
              debugLog(`[BattleRoom] Attack hit, ${targetToken.name} takes ${attackResult.damage} damage`);
              if (wsSessionId) {
                NotificationManager.error('Damage not committed', `${currentAction.name} did not produce the structured server damage contract.`);
                return;
              }
              handleTokenUpdate(targetToken.id, { hp: Math.max(0, targetToken.hp - attackResult.damage) });
            }
          }

          // @date_of_change 2025-09-10 - PHASE ONE FIX: Simplified multiattack sequence advancement
          const isLastComponent = currentIndex + 1 >= componentActions.length;
          
          if (isLastComponent) {
            // Sequence complete
            debugLog('[BattleRoom] 🏁 Multiattack sequence complete - all components executed');
            endMultiattackSequence();
          } else {
            // Advance to next component
            const nextIndex = currentIndex + 1;
            const nextAction = componentActions[nextIndex];
            
            debugLog(`[BattleRoom] ➡️ Advancing multiattack: ${currentIndex + 1}/${componentActions.length} → ${nextIndex + 1}/${componentActions.length}`);
            debugLog(`[BattleRoom] Next component: ${nextAction.name}`);
            
            // Update sequence index
            setMultiattackSequence(prev => ({
              ...prev,
              currentIndex: nextIndex
            }));
            
            // Clear previous selection and arm next component
            setSelectedTargets([]);
            setArmedAction({
              attackerId: attackerId,
              action: nextAction,
              multiTarget: false,
              requiredTargets: 1, // Most attacks require 1 target
              gmOverride: armedAction.gmOverride || null
            });
            
            debugLog(`[BattleRoom] ✅ Next component armed for targeting: ${nextAction.name}`);
          }
        }
      }
    } else {
      // Regular action (not part of multiattack sequence)
      // Delegate to unified handler with explicit target IDs
      // eslint-disable-next-line no-use-before-define
      const wasMultiattackCreated = await handleActionTaken({
        attackerId,
        attacker,
        actionData: action,
        targetIds: targets,
        metadata: payload,
        gmOverride: armedAction.gmOverride || null
      });

      if (objectTargeting && wasMultiattackCreated) {
        setArmedAction(null);
        setSelectedTargets([]);
        setMultiattackTarget(null);
        setTargetingEnabled(false);
        setMultiattackTargetingPopup((previous) => ({ ...previous, visible: false }));
        return;
      }
      
      // @date_of_change 2025-09-14 - IMPROVED UX: Handle retargeting for out-of-range targets
      if (wasMultiattackCreated === 'retarget') {
        debugLog(`[BattleRoom] 🎯 RETARGET mode - clearing targets but keeping action armed`);
        // Clear invalid target selection but keep action armed for reselection
        setSelectedTargets([]);
        setMultiattackTarget(null);
        // Keep armedAction and targetingEnabled so user can select a different target
        setTargetingEnabled(true);
        return; // Exit early to avoid normal state clearing
      }

      if (wasMultiattackCreated === 'retry') {
        debugLog('[BattleRoom] RETRY mode - preserving the armed action and selected targets after validation failure');
        setTargetingEnabled(true);
        return;
      }

      // @date_of_change 2025-09-06 - Enhanced debugging for state clearing logic
      debugLog('[BattleRoom] STATE_CLEARING_CHECK:', {
        wasMultiattackCreated,
        hasMultiattackSequence: !!multiattackSequence,
        shouldClearStates: !wasMultiattackCreated && !multiattackSequence,
        actionName: action.name
      });

      // @date_of_change 2025-09-05 - Don't clear armed action if multiattack sequence was just created
      // Only clear targeting state if no multiattack sequence was created
      if (!wasMultiattackCreated && !multiattackSequence) {
        debugLog('[BattleRoom] CLEARING states - no multiattack created');
        setArmedAction(null);
        setSelectedTargets([]);
        setMultiattackSequence(null);
        setMultiattackTarget(null);
      } else {
        debugLog('[BattleRoom] PRESERVING states - multiattack active or just created');
        // Only clear selected targets, keep armed action and sequence
        setSelectedTargets([]);
      }
    }
  }, [
    armedAction,
    selectedTargets,
    tokens,
    multiattackSequence,
    handleTokenUpdate,
    handleBatchTokenUpdates,
    endMultiattackSequence,
    gridSize,
    gridType,
    feetPerCell,
    handleActionTaken,
    requestInteractionIntent,
    applyAuthoritativeDamageOutcomes,
    applyAuthoritativeHealingOutcomes,
    wsSessionId,
    setTargetingEnabled,
    setMultiattackTargetingPopup,
    rulesetExecutionState
  ]);

  // Cancel the currently armed action or multiattack sequence
  const handleCancelTargets = useCallback(() => {
    if (armedAction) {
      debugLog('[BattleRoom] Cancel armed action');
    }
    if (multiattackSequence) {
      debugLog('[BattleRoom] Cancel multiattack sequence');
      // @date_of_change 2025-09-06 - Exit multiattack mode on cancellation
      setIsInMultiattackMode(false);
      debugLog(`[BattleRoom] Exiting multiattack mode due to cancellation`);
    }
    // Use proper multiattack sequence ending
    endMultiattackSequence();
    setTargetingEnabled(false);
    setMultiattackTargetingPopup((previous) => ({ ...previous, visible: false }));
  }, [
    armedAction,
    multiattackSequence,
    endMultiattackSequence,
    setTargetingEnabled,
    setMultiattackTargetingPopup
  ]);

  // Removed duplicate useEffect hooks for layer state synchronization

  // @date_of_change: 2025-11-06 - DEPRECATED inline map transformer attachment (migrated to MapArea)
  // rule_reference: AGENTS.md modularization; MapArea owns transform/resize when map layer is active
  // useEffect(() => {
  //   if (mapState.isSelected && mapImageRef.current && mapTransformerRef.current) {
  //     mapTransformerRef.current.nodes([mapImageRef.current]);
  //     mapTransformerRef.current.getLayer().batchDraw();
  //   }
  // }, [mapState.isSelected]);

  // Map deletion with Delete key
  // date_of_change: 2025-09-08 - Added keyboard handler for deleting map image
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && mapState.isSelected && mapImage) {
        e.preventDefault();
        debugLog('[BattleRoom] Deleting map image');
        setMapImage(null);
        setMapState(prev => ({ ...prev, isSelected: false }));
        // Reset stage size to default
        setStageSize({ width: 1200, height: 800 });
      }
    };

    if (mapState.isSelected) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [mapState.isSelected, mapImage, setMapImage, setMapState, setStageSize]);

  // @date_of_change: 2025-09-18 - FIX: Sync stageSize with mapState.size when mapState changes (e.g., during session load)
  const fitStageToMap = false;
  useEffect(() => {
    if (fitStageToMap && mapState.size.width > 0 && mapState.size.height > 0) {
      setStageSize({ width: mapState.size.width, height: mapState.size.height });
      debugLog('[BattleRoom] Updated stageSize to match mapState:', mapState.size);
    }
  }, [fitStageToMap, mapState.size, mapState.size.width, mapState.size.height, setStageSize]);

  // Auto-arm first component when multiattack sequence is created
  // @date_of_change 2025-09-08 - Automatically setup targeting for first multiattack component
  useEffect(() => {
    if (multiattackSequence && !armedAction) {
      const { attackerId, componentActions, currentIndex } = multiattackSequence;
      const currentAction = componentActions[currentIndex];
      
      if (currentAction && currentAction.targetingRequired !== false) {
        debugLog(`[BattleRoom] Auto-arming first multiattack component: ${currentAction.name}`);
        setArmedAction({
          attackerId,
          action: currentAction,
          multiTarget: false,
          requiredTargets: 1,
          gmOverride: multiattackSequence.gmOverride || null
        });
        NotificationManager.info(`Multiattack: Select target for ${currentAction.name} (1/${componentActions.length})`);
      }
    }
  }, [multiattackSequence, armedAction]);

  // Handle token dragging
  const handleTokenDragEnd = useCallback(async (e, newPos) => {
    // REFACTOR: Centralized token position updates. 2025-08-22T07:57:04-04:00
    const id = e.target.id();
    const draggedToken = tokens.find(token => token.id === id);
    if (!draggedToken) return;

    if ((targetingEnabled && armedAction) || multiattackSequence) {
      // Defensive guard: an armed targeting click must never persist token movement.
      // date_of_change: 2026-06-23
      const gridX = Number(draggedToken?.grid?.x);
      const gridY = Number(draggedToken?.grid?.y);
      const rollbackPos = Number.isFinite(gridX) && Number.isFinite(gridY)
        ? { x: gridX * gridSize, y: gridY * gridSize }
        : { x: draggedToken.x || 0, y: draggedToken.y || 0 };
      e?.target?.position?.(rollbackPos);
      e?.target?.getLayer?.()?.batchDraw?.();
      setDraggedTokenId(null);
      groupDragContextRef.current = null;
      setGroupDragVisualPositions({});
      return;
    }

    // The token renderer treats `grid` as authoritative. Use the same position
    // for drag comparisons and rollback so a stale x/y field cannot create a
    // valid-looking move that is later rejected by the canonical commit.
    // date_of_change: 2026-07-26
    const renderedOriginCell = resolveTokenGridCell(draggedToken, gridSize);
    const renderedOrigin = renderedOriginCell
      ? { x: renderedOriginCell.x * gridSize, y: renderedOriginCell.y * gridSize }
      : { x: Number(draggedToken.x) || 0, y: Number(draggedToken.y) || 0 };
    const movementOriginCell = renderedOriginCell || {
      x: Math.round(renderedOrigin.x / gridSize),
      y: Math.round(renderedOrigin.y / gridSize)
    };
    const pos = (newPos && typeof newPos.x === 'number' && typeof newPos.y === 'number') ? newPos : e.target.position();
    let finalPos = pos;

    if (snapToGrid && gridSize > 0) {
      finalPos = snapToGridUtil(pos, gridSize);
    }

    // Live-room movement is previewed and committed by the server. The canvas
    // snaps back while the command is pending and advances only when the
    // authoritative snapshot arrives. date_of_change: 2026-08-15
    const liveMovableToken = isWebSocketConnected
      && wsSessionId
      && ['character', 'player', 'monster', 'npc'].includes(String(draggedToken.type || '').toLowerCase());
    const authorizedMovementOverride = createAuthorizedGmOverride(gmActionOverride, isGameMaster);
    const groupContext = groupDragContextRef.current;
    if (liveMovableToken && groupContext?.eligibleIds?.length > 1) {
      const deltaX = finalPos.x - groupContext.baseStartPos.x;
      const deltaY = finalPos.y - groupContext.baseStartPos.y;
      const groupTokens = groupContext.eligibleIds.map((tokenId) => tokens.find((token) => token.id === tokenId)).filter(Boolean);
      const unsupported = groupTokens.find((token) => !['character', 'player', 'monster', 'npc'].includes(String(token.type || '').toLowerCase()));
      e?.target?.position?.(renderedOrigin);
      e?.target?.getLayer?.()?.batchDraw?.();
      setGroupDragVisualPositions({});
      if (unsupported) {
        NotificationManager.warning('Group placement not applied', `${unsupported.name || 'A selected item'} is not a combat token. Place non-token assets separately.`);
      } else if (!['auto', 'setup', 'correction'].includes(movementIntent)) {
        NotificationManager.warning('Choose a group placement intent', 'Live group placement supports setup or an explicit Game Master correction.');
      } else {
        const groupIntent = movementIntent === 'correction' ? 'correction' : 'setup';
        const moves = groupTokens.map((token) => {
          const cell = resolveTokenGridCell(token, gridSize);
          const origin = cell ? { x: cell.x * gridSize, y: cell.y * gridSize } : { x: Number(token.x) || 0, y: Number(token.y) || 0 };
          const offset = token.id === groupContext.baseId ? { dx: 0, dy: 0 } : (groupContext.offsets.get(token.id) || { dx: 0, dy: 0 });
          const destination = token.id === groupContext.baseId
            ? { x: finalPos.x, y: finalPos.y }
            : { x: groupContext.baseStartPos.x + offset.dx + deltaX, y: groupContext.baseStartPos.y + offset.dy + deltaY };
          return {
            tokenId: token.id,
            actorId: token.combatantId || token.characterId || token.monsterId || token.id,
            path: [origin, destination],
            movementMode,
          };
        });
        setMovementFeedback(`Reviewing placement for ${moves.length} tokens…`);
        try {
          const response = await requestBattleRoom('movement:group:preview', { moves, intent: groupIntent });
          await sendBattleRoomMutation('movement:group:commit', { moves, intent: groupIntent, previewFingerprint: response?.preview?.previewFingerprint });
          setMovementFeedback(`${moves.length} tokens placed together.`);
          window.setTimeout(() => setMovementFeedback(''), 3500);
          if (movementIntent !== 'auto') setMovementIntent('auto');
        } catch (cause) {
          setMovementFeedback('');
          NotificationManager.error('Group placement not applied', cause?.message || 'The server rejected this placement.');
        }
      }
      setDraggedTokenId(null);
      groupDragContextRef.current = null;
      return;
    }
    if (liveMovableToken && (!groupContext?.eligibleIds || groupContext.eligibleIds.length <= 1)) {
      const destination = { x: finalPos.x, y: finalPos.y };
      const resolvedMovementIntent = movementIntent === 'auto'
        ? (CombatLogManager.combatStarted ? 'normal' : 'setup')
        : movementIntent;
      const movementGmOverride = resolvedMovementIntent === 'normal' ? authorizedMovementOverride : null;
      const movementRequest = {
        tokenId: draggedToken.id,
        actorId: draggedToken.combatantId || draggedToken.characterId || draggedToken.monsterId || draggedToken.id,
        path: [renderedOrigin, destination],
        movementMode,
        intent: resolvedMovementIntent,
        ...(movementGmOverride ? { gmOverride: movementGmOverride } : {}),
        ...(creatureMovementRef.current?.tokenId === draggedToken.id ? {
          abilityActionId: creatureMovementRef.current.actionId,
          abilityTargetId: creatureMovementRef.current.targetId,
          intent: 'normal',
          movementMode: 'walk',
        } : {}),
      };
      e?.target?.position?.(renderedOrigin);
      e?.target?.getLayer?.()?.batchDraw?.();
      setMovementFeedback(`Reviewing ${draggedToken.name || 'token'}’s path…`);
      try {
        const previewResponse = await requestBattleRoom('movement:preview', movementRequest);
        const preview = previewResponse?.preview;
        if (!preview?.valid) throw new Error(preview?.invalidReason || 'That path is not legal.');
        setMovementFeedback(`Moving ${draggedToken.name || 'token'} ${preview.totalCost} ft…`);
        const movementCommitEvent = resolvedMovementIntent === 'setup'
          ? 'movement:setup:commit'
          : 'movement:commit';
        const commitResponse = await sendBattleRoomMutation(movementCommitEvent, { ...movementRequest, previewFingerprint: preview.previewFingerprint });
        if (movementRequest.abilityActionId) {
          creatureMovementRef.current = null;
          setArmedAction(null);
          setSelectedTargets([]);
          setTargetingEnabled(false);
        }
        const authoritativeTokenPatch = commitResponse?.result?.tokenPatch;
        if (resolvedMovementIntent === 'setup' && authoritativeTokenPatch) {
          // The setup ACK is already the durable, revision-guarded authority.
          // Apply its compact patch immediately so a second drag cannot begin
          // from the pre-ACK origin while the room broadcast is still in flight.
          // date_of_change: 2026-08-25
          setTokens((currentTokens) => currentTokens.map((token) => {
            if (String(token?.id || '') !== String(draggedToken.id)) return token;
            const nextGrid = authoritativeTokenPatch.grid && typeof authoritativeTokenPatch.grid === 'object'
              ? authoritativeTokenPatch.grid
              : token.grid;
            return {
              ...token,
              ...authoritativeTokenPatch,
              ...(nextGrid ? {
                grid: nextGrid,
                gridX: Number(nextGrid.x),
                gridY: Number(nextGrid.y)
              } : {})
            };
          }));
        }
        if (commitResponse?.result?.pausedForReaction) {
          // The interruption panel already follows the authoritative window.
          // A local waiting message would outlive it after a decline or timeout.
          // date_of_change: 2026-11-05
          setMovementFeedback('');
        } else {
          setMovementFeedback(resolvedMovementIntent === 'normal'
            ? `${draggedToken.name || 'Token'} moved ${preview.totalCost} ft with ${preview.remainingMovement} ft remaining.`
            : `${draggedToken.name || 'Token'} repositioned.`);
          window.setTimeout(() => setMovementFeedback(''), 3500);
        }
        if (movementIntent !== 'auto') setMovementIntent('auto');
      } catch (cause) {
        setMovementFeedback('');
        NotificationManager.error('Movement not applied', cause?.message || 'The server rejected that path.');
      } finally {
        setDraggedTokenId(null);
        groupDragContextRef.current = null;
        setGroupDragVisualPositions({});
      }
      return;
    }

    // @date_of_change 2025-09-19 - ENHANCED MOVEMENT TRACKING WITH TURN-BASED ALLOCATION
    // @date_of_change: 2025-10-04 - Track movement ONLY when combat has actually started via CombatLogManager
    // Track movement for combat characters if they're the current combatant
    const isCurrentCombatant = currentCombatant && currentCombatant.id === draggedToken.id;
    const isCharacterOrMonster = draggedToken.type === 'character' || draggedToken.type === 'monster';
    const combatActuallyStarted = CombatLogManager.combatStarted;
    let movementCheckpoint = null;
    let pendingMovementResult = null;
    let pendingMovementSummary = null;
    let pendingWithdraw = null;
    if (isCurrentCombatant && isCharacterOrMonster && combatActuallyStarted) {
      // Use imported movement system
      // @date_of_change: 2025-10-08 - Removed redundant require() that caused hoisting error (utilities already imported at top)
      try {
          // Convert pixel positions to grid coordinates for movement calculation
          const toGrid = {
            x: Math.round(finalPos.x / gridSize),
            y: Math.round(finalPos.y / gridSize)
          };

          // Check if token is trying to move into occupied space
          const occupyingToken = tokens.find(t => {
            if (t.id === draggedToken.id) return false;
            // Match rendered token placement: nested token.grid is authoritative when present.
            // date_of_change: 2026-06-23
            const cell = resolveTokenGridCell(t, gridSize);
            if (!cell) return false;
            return cell.x === toGrid.x && cell.y === toGrid.y;
          });

          if (occupyingToken && !canEnterTokenSpace(draggedToken, occupyingToken, { tokens })) {
            debugLog(`[BattleRoom] Movement blocked: space occupied by ${occupyingToken.name}`);
            // Revert to original position
            finalPos = renderedOrigin;
            return;
          }

          // Prepare battlefield state for MovementEngine
          const battlefield = {
            gridSize: 5, // D&D standard: 5 feet per grid square
            tokens: tokens.map(t => {
              const cell = resolveTokenGridCell(t, gridSize);
              if (!cell) return null;
              return {
                id: t.id,
                position: cell,
                size: t.size || 1,
                reach: 5, // Default reach
                conditions: t.conditions || [],
                combatFlags: t.combatFlags || {}
              };
            }).filter(Boolean),
            terrain: new Map() // TODO: Add terrain data from map
          };

          // A turn-start notification can initialize the transient tracker from
          // a pre-reset token. Rehydrate from the same canonical snapshot that
          // drives ActionTracker immediately before validating this drag.
          // date_of_change: 2026-07-18
          const fromGrid = movementOriginCell;
          movementTracker.synchronizeTurnSnapshot(draggedToken.id, {
            ...draggedToken,
            x: fromGrid.x,
            y: fromGrid.y
          });

          // Execute movement through TurnMovementTracker
          movementCheckpoint = movementTracker.createCheckpoint(draggedToken.id);
          const withdraw = draggedToken?.combatFlags?.cunningStrikeWithdraw;
          const currentRound = CombatLogManager.currentRound || 1;
          const currentTurn = CombatLogManager.currentTurn || 0;
          if (withdraw && typeof withdraw === 'object') {
            if (Number(withdraw.round) !== Number(currentRound) || Number(withdraw.turn) !== Number(currentTurn)) {
              throw new Error('Cunning Strike Withdraw belongs to a different initiative turn and cannot be used.');
            }
            if (!Number.isFinite(Number(withdraw.maxFeet)) || Number(withdraw.maxFeet) < 0 || !withdraw.sourceTargetId) {
              throw new Error('Cunning Strike Withdraw has malformed canonical movement data.');
            }
            pendingWithdraw = withdraw;
          }
          const result = movementTracker.executeMovement(
            draggedToken.id,
            toGrid,
            battlefield,
            pendingWithdraw
              ? {
                forcedMovement: false,
                specialMovementAllowance: Number(pendingWithdraw.maxFeet),
                ignoreOpportunityAttackFrom: pendingWithdraw.sourceTargetId,
                ignoreActionEconomy: Boolean(authorizedMovementOverride?.ignoreActionEconomy)
              }
              : {
                  forcedMovement: false,
                  ignoreActionEconomy: Boolean(authorizedMovementOverride?.ignoreActionEconomy)
                }
          );

          if (!result.success) {
            movementTracker.restoreCheckpoint(draggedToken.id, movementCheckpoint);
            debugLog(`[BattleRoom] Movement validation failed: ${result.error}`);

            // Show user feedback about movement restrictions
            if (result.availableMovement !== undefined && result.requiredMovement !== undefined) {
              NotificationManager.warning(
                'Insufficient Movement',
                `${draggedToken.name} needs ${result.requiredMovement} ft but only has ${result.availableMovement} ft remaining this turn.`
              );
            } else {
              NotificationManager.error('Movement Blocked', result.error);
            }

            // @date_of_change: 2025-10-04 - Revert to original position (removed early return to allow visual snap-back)
            finalPos = renderedOrigin;
            debugLog(`[BattleRoom] Reverting token to original position:`, finalPos);
            // Don't return early - continue to visual update at end of function
          } else {
            const movementSummary = movementTracker.getMovementSummary(draggedToken.id);
            pendingMovementResult = result;
            pendingMovementSummary = movementSummary;
            debugLog(`[BattleRoom] ${draggedToken.name} moved ${result.spent} feet:`, {
              remaining: result.remaining,
              triggers: result.triggers,
              summary: movementSummary
            });
          }

      } catch (error) {
        console.error('[BattleRoom] Enhanced movement processing error:', error);
        movementTracker.restoreCheckpoint(draggedToken.id, movementCheckpoint);
        // Revert to original position on error
        finalPos = renderedOrigin;
      }
    }

    // @date_of_change: 2025-10-04 - Always update visual position, even when reverting
    // This ensures the token snaps back visually when movement is insufficient
    // @date_of_change: 2025-10-08 - Atomic persistence for group movement (prevents reversion)
    // Build a batch including the base token (id) and all eligible group tokens, then persist once
    try {
      const updates = [];
      const derivedGrid = gridSize > 0
        ? { x: Math.round(finalPos.x / gridSize), y: Math.round(finalPos.y / gridSize) }
        : undefined;

      // Include base token only if it actually moved
      if (finalPos.x !== renderedOrigin.x || finalPos.y !== renderedOrigin.y) {
        if (pendingMovementResult) {
          const canonicalMovementUpdate = buildCanonicalMovementTokenUpdate(
            draggedToken,
            { x: finalPos.x, y: finalPos.y, grid: derivedGrid },
            pendingMovementSummary,
            pendingWithdraw ? { combatFlags: { cunningStrikeWithdraw: null } } : {}
          );
          if (!canonicalMovementUpdate.success) {
            throw new Error(canonicalMovementUpdate.error);
          }
          // Position and movementUsed travel through one token update and one
          // multiplayer sync, so the panel and grid cannot diverge.
          // date_of_change: 2026-07-18
          updates.push({ id, props: canonicalMovementUpdate.props });
        } else {
          // @date_of_change: 2025-10-09 - Persist grid as nested object, not gridX/gridY (Token.js prefers token.grid)
          updates.push({ id, props: { x: finalPos.x, y: finalPos.y, ...(derivedGrid ? { grid: { x: derivedGrid.x, y: derivedGrid.y } } : {}) } });
        }
      }

      const ctx = groupDragContextRef.current;
      debugLog('[BattleRoom] [GROUP] DragEnd ctxPresent=', !!ctx, 'eligibleLen=', ctx?.eligibleIds?.length || 0, 'combat=', combatActuallyStarted);
      if (!combatActuallyStarted && ctx && ctx.eligibleIds && ctx.eligibleIds.length > 1) {
        const baseEnd = finalPos; // finalPos after snap/back logic
        const deltaX = baseEnd.x - ctx.baseStartPos.x;
        const deltaY = baseEnd.y - ctx.baseStartPos.y;
        for (const sid of ctx.eligibleIds) {
          if (sid === ctx.baseId) continue;
          const off = ctx.offsets.get(sid) || { dx: 0, dy: 0 };
          const nx = ctx.baseStartPos.x + off.dx + deltaX;
          const ny = ctx.baseStartPos.y + off.dy + deltaY;
          const derivedGridOther = (gridSize > 0) ? { x: Math.round(nx / gridSize), y: Math.round(ny / gridSize) } : undefined;
          // @date_of_change: 2025-10-09 - Persist grid as nested object, not gridX/gridY
          updates.push({ id: sid, props: { x: nx, y: ny, ...(derivedGridOther ? { grid: { x: derivedGridOther.x, y: derivedGridOther.y } } : {}) } });
        }
      }

      if (updates.length > 0) {
        // Persist all token moves in a single state update and a single WS sync
        debugLog('[BattleRoom] [MOVE] Persisting batch token updates:', updates.map(u => u.id || u.tokenId)); // @date_of_change: 2025-10-08
        const committed = await handleBatchTokenUpdates(updates, { awaitAuthoritative: true });
        if (!committed) {
          movementTracker.restoreCheckpoint(draggedToken.id, movementCheckpoint);
          pendingMovementResult = null;
          finalPos = renderedOrigin;
          if (e?.target?.position) e.target.position(finalPos);
          NotificationManager.error('Movement not saved', 'The token stayed in its original square and no movement was spent.');
        }
      } else if (pendingMovementResult) {
        movementTracker.restoreCheckpoint(draggedToken.id, movementCheckpoint);
        pendingMovementResult = null;
      }

      // Audit only after the canonical token snapshot accepted the move.
      // date_of_change: 2026-07-18
      if (pendingMovementResult) {
        CombatLogManager.logEntry({
          type: 'TOKEN_MOVED',
          round: CombatLogManager.currentRound || 1,
          turn: CombatLogManager.currentTurn || 0,
          combatantId: draggedToken.id,
          data: {
            actorName: draggedToken.name,
            action: pendingMovementResult.specialMovement === 'cunning_strike_withdraw'
              ? 'Cunning Strike: Withdraw'
              : 'Move',
            movementCost: pendingMovementResult.spent,
            distance: pendingMovementResult.spent,
            remainingMovement: pendingMovementResult.remaining,
            opportunityAttacks: pendingMovementResult.triggers.opportunityAttacks,
            warnings: pendingMovementResult.warnings,
            gmOverride: authorizedMovementOverride || undefined
          },
          description: pendingMovementResult.specialMovement === 'cunning_strike_withdraw'
            ? `${draggedToken.name} used Cunning Strike: Withdraw to move ${pendingMovementResult.spent} feet without provoking from the struck target.`
            : `${draggedToken.name} moved ${pendingMovementResult.spent} feet`,
          timestamp: new Date().toISOString()
        });

        for (const attackerId of pendingMovementResult.triggers.opportunityAttacks) {
          const attacker = tokens.find(t => t.id === attackerId);
          if (attacker) {
            debugLog(`[BattleRoom] ${attacker.name} can make an opportunity attack against ${draggedToken.name}`);
            // TODO: Trigger opportunity attack UI/automation
          }
        }
      }
    } catch (error) {
      movementTracker.restoreCheckpoint(draggedToken.id, movementCheckpoint);
      finalPos = renderedOrigin;
      console.error('[BattleRoom] Atomic movement commit failed:', error);
      NotificationManager.error('Movement not saved', 'The token stayed in its original square and no movement was spent.');
    }

    // Always update visual position to handle snap-back on insufficient movement
    e.target.position(finalPos);
    e.target.getLayer().batchDraw();

    setDraggedTokenId(null);
    groupDragContextRef.current = null;
    // @date_of_change: 2025-10-09 - Clear group drag visual overrides after persistence
    setGroupDragVisualPositions({});
  }, [tokens, snapToGrid, gridSize, handleBatchTokenUpdates, snapToGridUtil, setDraggedTokenId, setTokens, currentCombatant, targetingEnabled, setTargetingEnabled, armedAction, multiattackSequence, isWebSocketConnected, wsSessionId, requestBattleRoom, sendBattleRoomMutation, movementIntent, movementMode, gmActionOverride, isGameMaster]);

  // All duplicate functions have been removed and consolidated

  // Handle token resize from transformer
  const handleTokenChange = useCallback((updatedToken) => {
    debugLog('[BattleRoom] Token resize/transform complete', updatedToken);
    if (handleTokenUpdate && updatedToken && updatedToken.id) {
      // Extract just the position and size properties that changed
      const { id, x, y, width, height, grid, sizeCells } = updatedToken;
      handleTokenUpdate(id, { x, y, width, height, ...(grid ? { grid } : {}), ...(sizeCells ? { sizeCells } : {}) });
    }
  }, [handleTokenUpdate]);
  
  
  
  // Layer control handlers - defined early to avoid initialization order issues
  const handleActiveLayerChange = useCallback((layerId) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, 'map');
    if (!normalizedLayerId) return;
    if (normalizedLayerId !== 'map') {
      setMapState(prev => (
        prev?.isSelected ? { ...prev, isSelected: false } : prev
      ));
    }
    setSelectedTokenIds(prev => prev.filter(id => {
      const token = tokens.find(t => t.id === id);
      return token && isTokenOnBattleLayer(token, normalizedLayerId);
    }));
    setSelectedTokenId(prev => {
      const token = tokens.find(t => t.id === prev);
      return token && isTokenOnBattleLayer(token, normalizedLayerId) ? prev : null;
    });
    setActiveLayer(normalizedLayerId);
  }, [setActiveLayer, setMapState, setSelectedTokenId, setSelectedTokenIds, tokens]);

  useEffect(() => {
    setSelectedTokenIds((prev) => {
      const next = prev.filter((id) => {
        const token = tokens.find((candidate) => candidate.id === id);
        return token
          && canViewerSeeToken(token, isGameMaster)
          && isTokenOnBattleLayer(token, normalizedActiveLayer);
      });
      return next.length === prev.length && next.every((id, index) => id === prev[index]) ? prev : next;
    });
    setSelectedTokenId((prev) => {
      if (!prev) return prev;
      const token = tokens.find((candidate) => candidate.id === prev);
      return token
        && canViewerSeeToken(token, isGameMaster)
        && isTokenOnBattleLayer(token, normalizedActiveLayer)
        ? prev
        : null;
    });
  }, [tokens, isGameMaster, normalizedActiveLayer, setSelectedTokenId, setSelectedTokenIds]);
  
  // Keep the selectedTokens array for backward compatibility
  
  
  // Enhanced token selection handler that automatically activates token's layer
  // Now correctly defined after handleActiveLayerChange to avoid initialization errors
  const logTokenSelection = useCallback((e, tokenId) => {
    // @date_of_change 2025-09-07 - Better parameter handling and debugging
    const actualTokenId = tokenId || e?.target?.id();
    debugLog('[BattleRoom] Token selection attempt - tokenId:', tokenId, 'event target id:', e?.target?.id(), 'actual:', actualTokenId);
    debugLog('[BattleRoom] Current active layer:', activeLayer);
    
    if (!actualTokenId) {
      debugLog('[BattleRoom] No token ID available for selection');
      return;
    }
    
    // @date_of_change 2025-09-06 - Restrict token selection during combat
    // During combat, only allow selection of current combatant or when targeting
    const isCombatActive = CombatLogManager.combatStarted;
    const isTargeting = !!armedAction || !!multiattackSequence || targetingEnabled;
    
    debugLog(`[BattleRoom] Token selection check: combat=${isCombatActive}, targeting=${isTargeting}, armedAction=${!!armedAction}, targetingEnabled=${targetingEnabled}`);
    
    // @date_of_change 2025-09-11 - CRITICAL FIX: Prevent duplicate targeting logic 
    // This function should NOT handle targeting when handleToggleTarget is available
    // The Token component should call handleToggleTarget instead of this function during targeting
    if (isTargeting) {
      debugLog('[BattleRoom] logTokenSelection called during targeting mode - this should be handled by handleToggleTarget instead');
      debugLog('[BattleRoom] Targeting context: multiattackSequence=', !!multiattackSequence, 'armedAction=', !!armedAction);
      debugLog('[BattleRoom] Target token clicked:', actualTokenId);
      
      // During targeting, this function should not handle token selection
      // Let handleToggleTarget handle it instead via the onToggleTarget prop
      return;
    }
    
    if (isCombatActive && !isTargeting && !isGameMaster) {
      // @date_of_change: 2025-10-09 - FIX: Use true turn-based currentCombatant from state (not selectedToken)
      // During combat (not targeting), only allow selecting the current combatant
      const turnCombatant = currentCombatant;
      const owns = typeof canControlToken === 'function' ? !!canControlToken(actualTokenId) : false;
      if (turnCombatant && actualTokenId !== turnCombatant.id && !owns) {
        debugLog(`[BattleRoom] Combat active - can only select current combatant (${turnCombatant.name})`);
        debugLog(`[BattleRoom] Attempted to select ${tokens.find(t => t.id === actualTokenId)?.name || actualTokenId}, blocked`);
        return; // Block the selection
      }
    }
    
    // Find the selected token to determine its layer
    const foundToken = tokens.find(t => t.id === actualTokenId);
    
    if (foundToken) {
      // Get the token's layer (default to 'token' if not specified)
      const tokenLayer = getTokenLayerId(foundToken);
      
      debugLog(`[BattleRoom] Found token on layer '${tokenLayer}', activating this layer`);
      
      // IMPORTANT: Set this layer as active to enable dragging
      if (tokenLayer !== activeLayer) {
        handleActiveLayerChange(tokenLayer);
      }
      
      // SHIFT-click toggles multi-selection while preserving single-select compatibility
      if (e?.evt?.shiftKey) {
        setSelectedTokenIds(prev => {
          const exists = prev.includes(actualTokenId);
          const next = exists ? prev.filter(id => id !== actualTokenId) : [...prev, actualTokenId];
          // Keep single select pointing at the last clicked token
          setSelectedTokenId(actualTokenId);
          return next;
        });
      } else {
        setSelectedTokenIds([actualTokenId]);
        // Call the hook's token selection handler with the token's layer
        handleTokenSelect(actualTokenId, tokenLayer);
      }
    } else {
      // If token not found (shouldn't happen), use current active layer
      debugLog('[BattleRoom] Selected token not found in tokens array');
      handleTokenSelect(actualTokenId, activeLayer);
    }
    
    // Debug after selection
    setTimeout(() => {
      debugLog('[BattleRoom] After selection, selectedTokenId =', selectedTokenId);
      debugLog('[BattleRoom] After selection, activeLayer =', activeLayer);
    }, 0);
  }, [handleTokenSelect, activeLayer, tokens, selectedTokenId, handleActiveLayerChange, armedAction, multiattackSequence, targetingEnabled, setSelectedTokenIds, setSelectedTokenId, currentCombatant, isGameMaster, canControlToken]);

  // @date_of_change 2025-08-30
  // Wrapper for panel-originated token selection to maintain a stable prop API.
  // The panel only knows the tokenId; we resolve the token's layer here and
  // delegate to the hook's handleTokenSelect to keep logic centralized.
  const handleTokenSelectFromPanel = useCallback((tokenId) => {
    const selected = tokens.find(t => t.id === tokenId);
    const layer = selected ? getTokenLayerId(selected) : normalizeBattleLayerId(activeLayer || 'token');
    if (selected && layer !== normalizedActiveLayer) {
      // Keep UX consistent with canvas selection by activating the token's layer
      handleActiveLayerChange(layer);
    }
    // @date_of_change: 2025-10-09 - Ensure single-selection array mirrors active token for consistent highlighting
    try {
      setSelectedTokenIds([tokenId]);
    } catch (_) {}
    handleTokenSelect(tokenId, layer);
  }, [tokens, activeLayer, normalizedActiveLayer, handleActiveLayerChange, handleTokenSelect, setSelectedTokenIds]);

  const resolveContextMenuPosition = useCallback((e, pointer) => {
    const nativeEvent = e?.evt || e;
    const clientX = nativeEvent?.clientX ?? nativeEvent?.pageX;
    const clientY = nativeEvent?.clientY ?? nativeEvent?.pageY;

    if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
      return { x: clientX, y: clientY };
    }

    const stage = e?.target?.getStage?.();
    const containerRect = stage?.container?.()?.getBoundingClientRect?.();
    const pointerX = Number.isFinite(pointer?.x) ? pointer.x : 0;
    const pointerY = Number.isFinite(pointer?.y) ? pointer.y : 0;

    if (containerRect) {
      return {
        x: containerRect.left + pointerX,
        y: containerRect.top + pointerY
      };
    }

    return { x: pointerX, y: pointerY };
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback((e, assetLayer, pointer, assetType = null, assetId = null) => {
    try {
      e?.evt?.preventDefault?.();
      e?.evt?.stopPropagation?.();
      if (e) {
        e.cancelBubble = true;
      }
    } catch (_) {}

    const position = resolveContextMenuPosition(e, pointer);

    setContextMenu({
      isVisible: true,
      position,
      assetLayer,
      assetType,
      assetId
    });
  }, [resolveContextMenuPosition, setContextMenu]);

  const shouldSuppressTokenTap = useCallback(() => {
    if (!suppressNextTokenTapRef.current) {
      return false;
    }
    suppressNextTokenTapRef.current = false;
    if (suppressNextTokenTapTimerRef.current) {
      clearTimeout(suppressNextTokenTapTimerRef.current);
      suppressNextTokenTapTimerRef.current = null;
    }
    return true;
  }, []);

  useEffect(() => {
    const container = stageContainerRef.current;
    if (!container) {
      return undefined;
    }

    const clearHold = () => {
      const hold = touchContextMenuHoldRef.current;
      if (hold?.timer) {
        clearTimeout(hold.timer);
      }
      touchContextMenuHoldRef.current = null;
    };

    const findTokenAtPointer = (event) => {
      const stage = stageRef?.current;
      if (!stage) {
        return null;
      }

      // Konva normally updates this before the container's bubbling listener.
      // Set it explicitly as well so this native touch path stays correct on
      // mobile browsers that dispatch Pointer Events differently.
      // date_of_change: 2026-07-26
      stage.setPointersPositions?.(event);
      const pointer = stage.getPointerPosition?.();
      let node = pointer ? stage.getIntersection?.(pointer) : null;

      while (node) {
        const tokenId = node.id?.();
        const token = tokens.find((candidate) => candidate.id === tokenId);
        if (token) {
          return { token, node };
        }
        node = node.getParent?.();
      }

      return null;
    };

    const handlePointerDown = (event) => {
      const targetingIsArmed = !!((targetingEnabled && armedAction) || multiattackSequence);
      if (event.pointerType !== 'touch' || event.isPrimary === false || targetingIsArmed) {
        return;
      }

      const hit = findTokenAtPointer(event);
      if (!hit) {
        return;
      }

      clearHold();
      const hold = {
        clientX: event.clientX,
        clientY: event.clientY,
        pointerId: event.pointerId,
        token: hit.token,
        node: hit.node,
        timer: null
      };
      hold.timer = setTimeout(() => {
        if (touchContextMenuHoldRef.current !== hold) {
          return;
        }

        touchContextMenuHoldRef.current = null;
        // A completed hold is an inspection command, not a selection or a
        // targeting tap. Keep the next synthetic tap from changing combat UI.
        // date_of_change: 2026-07-26
        suppressNextTokenTapRef.current = true;
        if (suppressNextTokenTapTimerRef.current) {
          clearTimeout(suppressNextTokenTapTimerRef.current);
        }
        suppressNextTokenTapTimerRef.current = setTimeout(() => {
          suppressNextTokenTapRef.current = false;
          suppressNextTokenTapTimerRef.current = null;
        }, 750);
        handleContextMenu({
          target: hold.node,
          cancelBubble: true,
          evt: {
            clientX: hold.clientX,
            clientY: hold.clientY,
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation()
          }
        }, getTokenLayerId(hold.token), null, 'token', hold.token.id);
      }, TOUCH_CONTEXT_MENU_DELAY_MS);
      touchContextMenuHoldRef.current = hold;
    };

    const handlePointerMove = (event) => {
      const hold = touchContextMenuHoldRef.current;
      if (!hold || event.pointerId !== hold.pointerId || event.pointerType !== 'touch') {
        return;
      }

      const movedX = event.clientX - hold.clientX;
      const movedY = event.clientY - hold.clientY;
      if (Math.hypot(movedX, movedY) > TOUCH_CONTEXT_MENU_MOVE_TOLERANCE_PX) {
        clearHold();
      }
    };

    const handlePointerEnd = () => clearHold();

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerEnd);
    container.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      clearHold();
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerEnd);
      container.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [armedAction, handleContextMenu, multiattackSequence, stageRef, targetingEnabled, tokens]);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({
      isVisible: false,
      position: null,
      assetType: null,
      assetId: null,
      assetLayer: null
    });
  }, [setContextMenu]);

  // Token ownership assignment handlers for context menu
  // @date_of_change: 2025-10-05 - GM can assign/unassign token ownership via right-click menu
  const handleAssignOwnership = useCallback((controllerType, controllerId = null) => {
    try {
      if (contextMenu?.assetType === 'token' && contextMenu?.assetId) {
        const ids = (selectedTokenIds && selectedTokenIds.length > 1 && selectedTokenIds.includes(contextMenu.assetId))
          ? selectedTokenIds
          : [contextMenu.assetId];
        ids.forEach(id => assignTokenControl(id, controllerType, controllerId));
      }
    } finally {
      handleContextMenuClose();
    }
  }, [contextMenu, assignTokenControl, handleContextMenuClose, selectedTokenIds]);

  const handleUnassignOwnership = useCallback(() => {
    try {
      if (contextMenu?.assetType === 'token' && contextMenu?.assetId) {
        const ids = (selectedTokenIds && selectedTokenIds.length > 1 && selectedTokenIds.includes(contextMenu.assetId))
          ? selectedTokenIds
          : [contextMenu.assetId];
        ids.forEach(id => unassignTokenControl(id));
      }
    } finally {
      handleContextMenuClose();
    }
  }, [contextMenu, unassignTokenControl, handleContextMenuClose, selectedTokenIds]);

  const handleTokenPropertiesOpen = useCallback((tokenId, position) => {
    setTokenPropertiesPopup({
      visible: true,
      tokenId: tokenId,
      position: position || { x: 200, y: 200 }
    });
  }, [setTokenPropertiesPopup]);

  const handleTokenPropertiesClose = useCallback(() => {
    setTokenPropertiesPopup({
      visible: false,
      tokenId: null,
      position: { x: 100, y: 100 },
      isPinned: false
    });
  }, [setTokenPropertiesPopup]);

  const handleTokenPropertiesTogglePin = useCallback(() => {
    setTokenPropertiesPopup(prev => ({
      ...prev,
      isPinned: !prev.isPinned,
      // Keep it visible when toggling between popup and sidebar
      visible: true
    }));
  }, [setTokenPropertiesPopup]);

  useEffect(() => {
    const tokenIds = new Set((Array.isArray(tokens) ? tokens : []).map((token) => token.id));

    setSelectedTokenIds((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = prev.filter((id) => tokenIds.has(id));
      return next.length === prev.length && next.every((id, index) => id === prev[index]) ? prev : next;
    });

    setSelectedTargets((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = prev.filter((id) => tokenIds.has(id));
      return next.length === prev.length && next.every((id, index) => id === prev[index]) ? prev : next;
    });

    if (selectedTokenId && !tokenIds.has(selectedTokenId)) {
      setSelectedTokenId(null);
    }

    if (currentCombatantId && !selectCanonicalCombatantToken(tokens, currentCombatantId)) {
      setCurrentCombatant(null);
    }

    if (multiattackTarget?.id && !tokenIds.has(multiattackTarget.id)) {
      setMultiattackTarget(null);
    }

    if (contextMenu?.isVisible && contextMenu?.assetType === 'token' && contextMenu?.assetId && !tokenIds.has(contextMenu.assetId)) {
      handleContextMenuClose();
    }

    if (tokenPropertiesPopup?.visible && tokenPropertiesPopup?.tokenId && !tokenIds.has(tokenPropertiesPopup.tokenId)) {
      handleTokenPropertiesClose();
    }

    const armedAttackerDeleted = armedAction?.attackerId && !tokenIds.has(armedAction.attackerId);
    const sequenceAttackerDeleted = multiattackSequence?.attackerId && !tokenIds.has(multiattackSequence.attackerId);

    if (armedAttackerDeleted || sequenceAttackerDeleted) {
      setArmedAction(null);
      setTargetingEnabled(false);
      setSelectedTargets([]);
      setMultiattackSequence(null);
      setIsInMultiattackMode(false);
      setMultiattackTarget(null);
      setMultiattackTargetingPopup({ visible: false, position: { x: 200, y: 150 }, isPinned: false });
    }
  }, [
    tokens,
    selectedTokenId,
    currentCombatantId,
    multiattackTarget?.id,
    contextMenu,
    tokenPropertiesPopup,
    armedAction?.attackerId,
    multiattackSequence?.attackerId,
    setSelectedTokenIds,
    setSelectedTargets,
    setSelectedTokenId,
    setCurrentCombatant,
    setMultiattackTarget,
    setArmedAction,
    setTargetingEnabled,
    setMultiattackSequence,
    setIsInMultiattackMode,
    setMultiattackTargetingPopup,
    handleContextMenuClose,
    handleTokenPropertiesClose
  ]);

  

  // @date_of_change 2025-09-15 - Comprehensive combat reset for initiative tracker reset button
  const handleResetCombat = useCallback(() => {
    debugLog('[BattleRoom] 🔄 Resetting all combat systems');

    // Clear multiattack sequences and targeting
    endMultiattackSequence();
    setArmedAction(null);
    setTargetingEnabled(false);
    setSelectedTargets([]);
    setMultiattackTargetingPopup({ visible: false, isPinned: false });

    // Clear action economy state
    setActionsUsed({});
    try { ActionEconomyManager.resetAllStates(); } catch (_) {}
    lastProcessedTurnRef.current = { key: null, at: 0 };

    // Clear all conditions and status effects from tokens applied during combat
    try {
      if (Array.isArray(tokens)) {
        tokens.forEach(t => {
          const hasConds = Array.isArray(t.conditions) && t.conditions.length > 0;
          const hasSE = t.statusEffects && Object.keys(t.statusEffects || {}).length > 0;
          const hasMeta = t.statusEffectsMeta && Object.keys(t.statusEffectsMeta || {}).length > 0;
          if ((hasConds || hasSE || hasMeta) && typeof handleTokenUpdate === 'function') {
            handleTokenUpdate(t.id, { conditions: [], statusEffects: {}, statusEffectsMeta: {} });
          }
        });
      }
    } catch (e) {
      debugLog('[BattleRoom] Failed to clear token conditions on reset:', e?.message || e);
    }

    // Reset combat log to beginning
    if (CombatLogManager) {
      try {
        // Rewind to the first state (combat start)
        const rewindPoints = CombatLogManager.getRewindPoints();
        if (rewindPoints && rewindPoints.length > 0) {
          debugLog('[BattleRoom] 📜 Rewinding combat log to beginning');
          CombatLogManager.rewindTo(0); // Rewind to first state
        }
      } catch (error) {
        debugLog('[BattleRoom] Could not rewind combat log:', error);
      }
    }

    debugLog('[BattleRoom] ✅ Combat reset complete');
  }, [endMultiattackSequence, setArmedAction, setTargetingEnabled, setSelectedTargets, setMultiattackTargetingPopup, handleTokenUpdate, tokens]);

  // @date_of_change 2025-09-07 - Wrapper handlers for MultiattackTargetingPopup compatibility
  

  

  

  const handleTokenPropertiesUpdate = (updatedTokenData) => {
    // REFACTOR: Simplified properties update flow. 2025-08-22T07:57:04-04:00
    if (tokenPropertiesPopup.tokenId && updatedTokenData) {
      debugLog('[BattleRoom] Token properties update triggered', { tokenId: tokenPropertiesPopup.tokenId, updatedTokenData });

      // Directly call the centralized handler from the hook.
      // All complex logic for normalization, validation, and persistence
      // is now handled within useBattleRoom's handleTokenUpdate.
      handleTokenUpdate(tokenPropertiesPopup.tokenId, updatedTokenData);
    }
  };

  // (removed unused handleLayerReassignment)

  // Unified asset upload handler with proper layer assignment
  // Track last uploaded asset to prevent duplicate processing
  const [processingAsset, setProcessingAsset] = useState(false);
  const lastAssetRef = useRef({ assetId: null, timestamp: 0 });

  const handleAssetUpload = useCallback(async (asset) => {
    const assetId = String(asset?.id || '');
    const now = Date.now();
    if (!assetId) {
      NotificationManager.error('Image not placed', 'Choose a ready image from the asset vault.');
      return null;
    }
    if (processingAsset || (lastAssetRef.current.assetId === assetId && now - lastAssetRef.current.timestamp < 1500)) {
      return null;
    }
    if (!isGameMaster) {
      NotificationManager.error('Game Master required', 'Only the Game Master can place scene images.');
      return null;
    }

    lastAssetRef.current = { assetId, timestamp: now };
    setProcessingAsset(true);
    try {
      const resolvedAssetUrl = resolveUploadAssetUrl(asset);
      if (!resolvedAssetUrl || /^(data|blob):/i.test(resolvedAssetUrl)) {
        throw new Error('Use a durable image from the asset vault; local data and blob URLs cannot be saved to a scene.');
      }
      const targetLayer = asset.layer || activeLayer;

      if (targetLayer === 'map' || asset.type === 'map') {
        let authoritativeUrl = resolvedAssetUrl;
        if (!isLocalBattleRoom) {
          const acknowledgement = await sendBattleRoomMutation('mapBackground:replace', { sourceAssetId: assetId });
          await waitForAuthoritativeRevision(acknowledgement?.revision);
          authoritativeUrl = acknowledgement?.result?.background?.imageUrl || authoritativeUrl;
        }
        setMapImage(authoritativeUrl);
        setMapImages((current) => ({ ...current, map: authoritativeUrl }));
        setHookLayers((current) => ({
          ...current,
          map: { ...current.map, mapImage: authoritativeUrl },
        }));
        setMapState((current) => ({ ...current, imageUrl: authoritativeUrl, isSelected: true }));
        NotificationManager.success('Map background replaced', `${asset.name || 'The selected map'} is now the scene background.`);
        return authoritativeUrl;
      }

      const intrinsicSize = await loadSceneAssetImageSize(resolvedAssetUrl);
      const viewportPlacement = asset.placementHint || getViewportPlacementHint();
      const mapLocalPlacement = {
        x: Number(viewportPlacement?.x) - Number(mapState.position?.x || 0),
        y: Number(viewportPlacement?.y) - Number(mapState.position?.y || 0),
      };
      const sceneAsset = buildSceneAssetCreatePayload({
        asset,
        layer: targetLayer,
        placement: mapLocalPlacement,
        intrinsicSize,
        gridSize,
        mapSize: mapState.size,
        snapToGrid: asset.snapToGrid === true,
      });

      let committedAsset;
      if (isLocalBattleRoom) {
        committedAsset = {
          ...sceneAsset,
          assetInstanceId: createBattleRoomOperationId('scene-asset'),
          imageUrl: resolvedAssetUrl,
          createdBy: currentUser?.userId || 'local-gm',
        };
      } else {
        const acknowledgement = await sendBattleRoomMutation('sceneAsset:create', { sceneAsset });
        committedAsset = acknowledgement?.result?.asset;
        if (!committedAsset) throw new Error('The server did not return the placed scene image.');
      }
      setSceneAssets((current) => [
        ...current.filter((entry) => entry.assetInstanceId !== committedAsset.assetInstanceId),
        committedAsset,
      ]);
      setSelectedSceneAssetId(committedAsset.assetInstanceId);
      setSelectedTokenId(null);
      NotificationManager.success('Scene image placed', `${committedAsset.name} was added to the ${committedAsset.layer === 'gamemaster' ? 'GM' : 'Token'} Layer.`);
      return committedAsset;
    } catch (error) {
      console.error('[BattleRoom] Scene image placement failed:', error);
      NotificationManager.error('Image not placed', error?.message || 'The scene image could not be saved.');
      return null;
    } finally {
      setProcessingAsset(false);
    }
  }, [activeLayer, currentUser?.userId, getViewportPlacementHint, gridSize, isGameMaster, isLocalBattleRoom, mapState.position, mapState.size, processingAsset, sendBattleRoomMutation, setHookLayers, setMapImage, setMapImages, setMapState, setSceneAssets, setSelectedTokenId, waitForAuthoritativeRevision]);

  const handleSceneAssetChange = useCallback(async (assetInstanceId, patch) => {
    try {
      let asset;
      if (isLocalBattleRoom) {
        const currentAsset = sceneAssets.find((entry) => entry.assetInstanceId === assetInstanceId);
        if (!currentAsset) throw new Error('The scene image no longer exists.');
        asset = { ...currentAsset, ...patch };
        setSceneAssets((current) => current.map((entry) => entry.assetInstanceId === assetInstanceId ? asset : entry));
      } else {
        const acknowledgement = await sendBattleRoomMutation('sceneAsset:update', { assetInstanceId, patch });
        asset = acknowledgement?.result?.asset;
        if (!asset) throw new Error('The server did not confirm the scene-image update.');
        setSceneAssets((current) => current.map((entry) => entry.assetInstanceId === assetInstanceId ? asset : entry));
      }
      return asset;
    } catch (error) {
      // A rejected drag/transform must repaint from the authoritative object so
      // the canvas cannot imply that an unsaved placement committed. date_of_change: 2026-09-11
      setSceneAssets((current) => current.map((entry) => entry.assetInstanceId === assetInstanceId ? { ...entry } : entry));
      NotificationManager.error('Scene image not updated', error?.message || 'The change could not be saved.');
      return null;
    }
  }, [isLocalBattleRoom, sceneAssets, sendBattleRoomMutation, setSceneAssets]);

  const handleSceneAssetDelete = useCallback(async (assetInstanceId) => {
    const asset = sceneAssets.find((entry) => entry.assetInstanceId === assetInstanceId);
    if (!asset || !window.confirm(`Remove “${asset.name}” from this scene? The reusable vault image will remain available.`)) return;
    try {
      if (!isLocalBattleRoom) await sendBattleRoomMutation('sceneAsset:delete', { assetInstanceId });
      setSceneAssets((current) => current.filter((entry) => entry.assetInstanceId !== assetInstanceId));
      setSelectedSceneAssetId(null);
      NotificationManager.success('Scene image removed', `${asset.name} was removed from this scene.`);
    } catch (error) {
      NotificationManager.error('Scene image not removed', error?.message || 'The image could not be removed.');
    }
  }, [isLocalBattleRoom, sceneAssets, sendBattleRoomMutation, setSceneAssets]);

  // The room owns initiative even when neither drawer is mounted.
  // D&D Basic Rules, Initiative. date_of_change: 2026-11-04
  const authoritativeInitiative = useMemo(() => wsSessionId
    ? buildAuthoritativeInitiativeHydration(authoritativeCombatState, tokens)
    : null, [authoritativeCombatState, tokens, wsSessionId]);

  useEffect(() => {
    if (!authoritativeCombatState || !wsSessionId) return;
    const started = authoritativeCombatState.combatStarted === true;
    const signature = `${wsSessionId}:${activeSceneId}:${authoritativeInitiativeSignature(authoritativeInitiative)}`;
    if (started && !authoritativeInitiative) return;
    if (signature === lastAuthoritativeInitiativeHydrationRef.current) return;
    lastAuthoritativeInitiativeHydrationRef.current = signature;
    const turn = {
      round: authoritativeInitiative?.round,
      turn: authoritativeInitiative?.currentTurnIndex,
      combatants: authoritativeInitiative?.combatants
    };
    if (started) {
      if (!CombatLogManager.combatStarted) {
        CombatLogManager.startCombat(turn.combatants, viewerVisibleTokens, { authoritativeRecovery: true });
      }
      CombatLogManager.synchronizeAuthoritativeTurn(turn);
    } else {
      CombatLogManager.synchronizePreCombatState(turn);
    }
  }, [activeSceneId, authoritativeCombatState, authoritativeInitiative, viewerVisibleTokens, wsSessionId]);

  // Initiative hydration can initialize and clear the singleton combat log.
  // Replay bounded server history on the next task so recovery is ordered
  // after that initialization, then merge by entry id. date_of_change: 2026-08-26
  useEffect(() => {
    if (isLocalBattleRoom || !Array.isArray(authoritativeCombatState?.combatLogHistory)) return undefined;
    const logEntries = authoritativeCombatState.combatLogHistory
      .map((event) => event?.data?.logEntry)
      .filter((entry) => entry?.id);
    const historySignature = logEntries
      .map((entry) => `${entry.id}:${entry.lastEditedAt || entry.timestamp || ''}:${entry.description || ''}`)
      .join('|');
    // The session/scene identity makes an empty authoritative history a real
    // state transition instead of a falsy no-op. date_of_change: 2026-08-31
    const signature = `${effectiveSessionId || 'local'}:${activeSceneId || 'scene'}:${historySignature}`;
    if (signature === lastAuthoritativeCombatLogHistoryRef.current) return undefined;
    lastAuthoritativeCombatLogHistoryRef.current = signature;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('websocket-combat-log-history', {
        detail: { logEntries, sessionId: wsSessionId, timestamp: Date.now() }
      }));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeSceneId, authoritativeCombatState, effectiveSessionId, isLocalBattleRoom, wsSessionId]);

  // Select from the same saved turn as the tracker and action tray.
  // An early turn notification cannot select a different actor while its
  // economy and position are still loading. date_of_change: 2026-11-04
  useEffect(() => {
    if (!authoritativeInitiative?.activeCombatantId) {
      lastSelectedAuthoritativeTurnRef.current = null;
      return;
    }
    const { round, currentTurnIndex, activeCombatantId } = authoritativeInitiative;
    const turnKey = `${wsSessionId}:${activeSceneId}:${round}:${currentTurnIndex}:${activeCombatantId}`;
    if (lastSelectedAuthoritativeTurnRef.current === turnKey) return;
    lastSelectedAuthoritativeTurnRef.current = turnKey;
    handleTokenSelectFromPanel(activeCombatantId);
  }, [activeSceneId, authoritativeInitiative, handleTokenSelectFromPanel, wsSessionId]);

  // Draggability guard combining layer interactivity with ownership + turn gating
  // Players may only move their assigned token on their turn; GM can always move; single-player unrestricted.
  // @date_of_change: 2025-10-05
  const canDragTokenGuard = useCallback((token) => {
    if (!token) return false;
    if ((targetingEnabled && armedAction) || multiattackSequence) {
      // Action targeting is selection-only; no token may be moved while a target is being picked.
      // date_of_change: 2026-06-23
      return false;
    }
    if (!isMultiplayerMode) return true;
    if (isGameMaster) return true;
    const ownsToken = typeof canControlToken === 'function' ? canControlToken(token.id) : true;
    const isCombatActive = !!CombatLogManager.combatStarted;
    if (!isCombatActive) {
      return ownsToken; // Outside combat, allow dragging owned tokens
    }
    if (!currentCombatant) {
      debugLog('[BattleRoom][DRAG-GUARD] Combat active but no currentCombatant; allowing owned drag for token', token.id);
      return ownsToken;
    }
    const isUsersTurnForToken = currentCombatant?.id === token.id;
    return ownsToken && isUsersTurnForToken;
  }, [isMultiplayerMode, isGameMaster, canControlToken, currentCombatant, targetingEnabled, armedAction, multiattackSequence]);

  // @date_of_change 2025-09-20 - Roll20-style global drag-and-drop handlers (positioned after handleAssetUpload but before JSX)
  const handleGlobalDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're dragging image files
    if (e.dataTransfer.types.includes('Files')) {
      setDragOverlay(true);
    }
  }, []);

  const handleGlobalDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Only hide overlay if leaving the entire battle area
    if (e.target === e.currentTarget) {
      setDragOverlay(false);
    }
  }, []);

  const handleGlobalDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverlay(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      console.warn('[BattleRoom] No image files found in drop');
      return;
    }

    if (!isGameMaster) {
      NotificationManager.error('Game Master required', 'Only the Game Master can upload scene images.');
      return;
    }

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const worldPoint = {
      x: ((e.clientX - rect.left) - stagePosition.x) / stageScale,
      y: ((e.clientY - rect.top) - stagePosition.y) / stageScale,
    };
    const assetType = normalizedActiveLayer === 'map' ? 'map' : 'token';
    const filesToUpload = assetType === 'map' ? imageFiles.slice(0, 1) : imageFiles;
    if (assetType === 'map' && imageFiles.length > 1) {
      NotificationManager.info('One map at a time', 'Only the first dropped image will replace the map background.');
    }

    for (const file of filesToUpload) {
      try {
        validateAssetFile(file, assetType);
        const upload = await uploadAssetToS3(file, assetType);
        const asset = createAssetFromS3Upload(upload, file, normalizedActiveLayer);
        await handleAssetUpload({ ...asset, placementHint: worldPoint });
      } catch (error) {
        console.error('[BattleRoom] Dropped image upload failed:', error);
        NotificationManager.error('Image upload failed', `${file.name}: ${error?.message || 'The image could not be stored.'}`);
      }
    }
  }, [handleAssetUpload, isGameMaster, normalizedActiveLayer, stagePosition.x, stagePosition.y, stageScale]);

  // REMOVED: persistEntityData function 
  // date_of_change: 2025-08-30 - CRITICAL FIX: Combat should NOT modify master character/monster data
  // Combat state changes (HP, temporary effects) should only exist in the battle session
  // The previous implementation incorrectly saved combat changes back to CustomAssetManager
  // which corrupted the single source of truth for characters/monsters
  //
  // ========== CORRECT DATA ARCHITECTURE ==========
  // 
  // MASTER DATA (CustomAssetManager):
  // - Original character/monster templates with full HP, base stats
  // - Never modified during combat
  // - Shared across all sessions
  // 
  // SESSION DATA (tokens array):
  // - Each token has .entity containing combat-specific state
  // - HP changes, temporary ability modifications, status effects
  // - Specific to this battle session
  // - Saved/loaded with session files
  // 
  // FLOW:
  // 1. Token created from master template via createTokenFromDashboardData()
  // 2. Combat modifies token.entity (session-specific copy)
  // 3. Session save/load preserves combat state
  // 4. Master templates remain pristine for future sessions
  // ==================================================
  
  // Handle attack action specifically (most common action type)
  // @date_of_change: 2025-09-07 - Replaced massive 278-line function with AttackHandler utilities
  // @date_of_change: 2025-09-08 - Fixed parameter mismatch: first param is tokenId, need to resolve to token object
  const handleAttackAction = useCallback(async (attackerTokenId, actionData, explicitTargetIds, engineGmOverride = null) => {
    // Resolve attacker token from ID
    let actionTokens = tokens;
    let actionRulesetExecutionState = rulesetExecutionState;
    let attacker = actionTokens.find(token => token.id === attackerTokenId);
    
    // @date_of_change 2025-09-13 - TARGETING FIX: Weapons need targeting too even without explicit range
    // Consider action as requiring click-to-target when it has a range (not Self) or is a weapon/attack
    const needsClickTarget = (
      (actionData.range && actionData.range !== 'Self') ||
      !!actionData.attack ||
      actionData.type === 'weapon' ||
      actionData.type === 'attack'
    );
    // @date_of_change 2025-09-17 - CRITICAL FIX: Don't depend on selectedTargets state for targeting mode decision
    // When explicitTargetIds are provided (from confirmation), never enter targeting mode
    // When no explicitTargetIds and action needs targeting, enter targeting mode
    const hasExplicitTargetsForTargeting = explicitTargetIds && explicitTargetIds.length > 0;
    const willEnterTargetingModeValue = needsClickTarget && !hasExplicitTargetsForTargeting;

    if (wsSessionId && actionData?.mechanics && !willEnterTargetingModeValue) {
      const preparedResolution = await prepareCreatureResolution();
      if (!preparedResolution) return false;
      actionTokens = preparedResolution.tokens;
      actionRulesetExecutionState = preparedResolution.rulesetExecutionState;
      attacker = actionTokens.find(token => token.id === attackerTokenId);
    }
    
    console.log('🗡️ [ATTACK:START] Using new AttackHandler system', { 
      attackerTokenId,
      attackerName: attacker?.name,
      actionName: actionData?.name, 
      targetIds: explicitTargetIds,
      timestamp: new Date().toISOString()
    });
    
    debugLog('[BattleRoom] TARGETING_DEBUG', {
      actionName: actionData?.name,
      actionRange: actionData?.range,
      selectedTargetsLength: selectedTargets?.length,
      willEnterTargetingMode: willEnterTargetingModeValue,
      explicitTargetIds: explicitTargetIds?.length || 0,
      hasExplicitTargetsForTargeting
    });

    // @date_of_change: 2025-09-18 - CRITICAL FIX
    // Ensure explicit-target execution path always bypasses AoE targeting and continues
    // to AttackHandler. Previously, malformed control flow could short-circuit this path.
    // Strengthen explicit-target detection and wrap AoE parsing in try/catch to avoid
    // hidden exceptions interrupting execution.
    const hasExplicitTargets = Array.isArray(explicitTargetIds) && explicitTargetIds.length > 0;

    console.log(`[BattleRoom] 🔍 ATTACK_AOE_DEBUG - Action: ${actionData.name}`);
    console.log(`[BattleRoom] 🔍 ATTACK_AOE_DEBUG - Direct area:`, actionData.areaOfEffect || actionData.area);
    console.log(`[BattleRoom] 🔍 ATTACK_AOE_DEBUG - Structured area:`, actionData.areaType && actionData.areaSize ? { shape: actionData.areaType, size: actionData.areaSize } : null);
    console.log(`[BattleRoom] 🔍 DEBUG: hasExplicitTargets =`, hasExplicitTargets);
    console.log(`[BattleRoom] 🔍 DEBUG: About to check AoE detection...`);

    if (hasExplicitTargets) {
      console.log(`[BattleRoom] 🔍 TARGETED_EXECUTION_PATH - Explicit targets provided (${explicitTargetIds.length}). Bypassing AoE detection.`);
    } else {
      console.log(`[BattleRoom] 🔍 DEBUG: Entering AoE detection block`);
      try {
        // Check if this action has area effect (like dragon breath weapons)
        const directArea = actionData.areaOfEffect || actionData.area;
        const structuredArea = actionData.areaType && actionData.areaSize ?
          { shape: actionData.areaType, size: actionData.areaSize } : null;
        const hasAreaOfEffect = !!(directArea || structuredArea);

        console.log(`[BattleRoom] 🔍 ATTACK_AOE_DEBUG - hasAreaOfEffect:`, hasAreaOfEffect);

        if (hasAreaOfEffect) {
          console.log(`[BattleRoom] 🐉 AoE Attack ${actionData.name} requires area targeting`);

          let aoeInfo = structuredArea || null;

          // If no structured area but we have text description, try to parse it
          if (!aoeInfo && directArea && typeof directArea === 'string') {
            console.log(`[BattleRoom] 🔍 ATTACK_AOE_DEBUG - Parsing description:`, directArea);

            const description = directArea.toLowerCase();
            const sphereMatch = description.match(/(\d+)-?(?:foot|ft)?\s*(?:radius)?\s*sphere/);
            const cylinderMatch = description.match(/(\d+)-?(?:foot|ft)?\s*(?:radius)?\s*(?:,\s*\d+-?(?:foot|ft)?\s*(?:high|tall))?\s*cylinder/);
            const cubeMatch = description.match(/(\d+)-?(?:foot|ft)?\s*cube/);
            const coneMatch = description.match(/(\d+)-?(?:foot|ft)?\s*cone/);
            const lineByMatch = description.match(/(\d+)-?(?:foot|ft)?\s*by\s*(\d+)-?(?:foot|ft)?\s*line/);
            const lineLengthMatch = description.match(/(\d+)-?(?:foot|ft)?\s*(?:line|long)/);
            const lineWidthMatch = description.match(/(\d+)-?(?:foot|ft)?\s*wide/);
            const lineMatch = description.match(/(\d+)-?(?:foot|ft)?\s*line/);

            if (sphereMatch) {
              aoeInfo = { shape: 'sphere', size: parseInt(sphereMatch[1], 10) };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${aoeInfo.size}ft sphere`);
            } else if (cylinderMatch) {
              // @date_of_change 2025-10-12 - Treat cylinder radius as sphere size for targeting per PHB template parity
              const radiusVal = parseInt(cylinderMatch[1], 10);
              aoeInfo = { shape: 'sphere', size: radiusVal };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${radiusVal}ft cylinder (treated as sphere)`);
            } else if (cubeMatch) {
              aoeInfo = { shape: 'cube', size: parseInt(cubeMatch[1], 10) };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${aoeInfo.size}ft cube`);
            } else if (coneMatch) {
              aoeInfo = { shape: 'cone', size: parseInt(coneMatch[1], 10) };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${aoeInfo.size}ft cone`);
            } else if (lineByMatch) {
              const lengthVal = parseInt(lineByMatch[1], 10);
              const widthVal = parseInt(lineByMatch[2], 10);
              aoeInfo = { shape: 'line', size: lengthVal, length: lengthVal, width: widthVal };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${lengthVal}ft line (width ${widthVal}ft)`);
            } else if (lineMatch || lineLengthMatch) {
              // @date_of_change 2025-10-12 - Preserve line length/width for Lightning Bolt PHB 2024 requirements
              const lengthVal = parseInt((lineLengthMatch || lineMatch)[1], 10);
              const widthVal = lineWidthMatch ? parseInt(lineWidthMatch[1], 10) : 5;
              aoeInfo = { shape: 'line', size: lengthVal, length: lengthVal, width: widthVal };
              console.log(`[BattleRoom] 🐉 Detected AoE from description: ${actionData.name} has ${lengthVal}ft line (width ${widthVal}ft)`);
            }
          }

          if (aoeInfo) {
            const aoeShapeName = (aoeInfo.shape || aoeInfo.type || '').toLowerCase();
            if (aoeShapeName === 'line') {
              const aoeLength = Number.isFinite(aoeInfo.length)
                ? aoeInfo.length
                : Number.isFinite(aoeInfo.size)
                  ? aoeInfo.size
                  : 0;
              const aoeWidth = Number.isFinite(aoeInfo.width)
                ? aoeInfo.width
                : Number.isFinite(aoeInfo.thickness)
                  ? aoeInfo.thickness
                  : 5;
              aoeInfo = {
                ...aoeInfo,
                shape: aoeInfo.shape || 'line',
                type: aoeInfo.type || aoeInfo.shape || 'line',
                length: aoeLength,
                size: aoeLength,
                width: aoeWidth,
                thickness: aoeWidth,
              };
            }
            console.log(`[BattleRoom] 🐉 Enabling AoE targeting for attack ${aoeInfo.shape} (${aoeInfo.size} ft)`);

            // Enable AoE targeting mode for attacks
            setAoeTargeting({
              enabled: true,
              targetPoint: null,
              areaOfEffect: aoeInfo,
              direction: 0, // Default direction, user can adjust for cones/lines
              targetMode: 'center', // Default to center targeting
              spell: actionData, // Store action data (not just spells)
              caster: attacker,
              gmOverride: engineGmOverride,
              previewPoint: null
            });

            // Show notification for AoE targeting
            NotificationManager.info(
              `AoE Targeting Active`,
              `Click on the grid to target ${actionData.name} (${aoeInfo.shape}, ${aoeInfo.size} ft)`
            );

            return true; // Indicate that we've handled this action (entered targeting mode)
          }
        }
      } catch (aoeErr) {
        console.error('[BattleRoom] ❌ ATTACK_AOE_DETECTION_ERROR - Continuing without AoE:', aoeErr);
      }
    }

    console.log(`[BattleRoom] 🔍 DEBUG: Finished AoE detection, continuing...`);
    console.log(`[BattleRoom] 🔍 DEBUG: About to continue to AttackHandler call...`);

    // Validate that attacker token was found
    if (!attacker) {
      console.error('[BattleRoom] Attacker token not found for ID:', attackerTokenId);
      return false;
    }

    // Check if we've already consumed action economy for this armed action
    const actionEconomyAlreadyConsumed = armedAction?.__actionEconomyConsumed;
    
    // Use new AttackHandler system for validation and setup
    let result;
    try {
      console.log('🗡️ [CALLING_ATTACK_HANDLER]', {
        actionName: actionData.name,
        hasExplicitTargets: !!(explicitTargetIds && explicitTargetIds.length > 0),
        willEnterTargetingMode: willEnterTargetingModeValue,
        targetCount: explicitTargetIds?.length || 0
      });

      result = await AttackHandler.processAttack({
        attacker,
        actionData,
        explicitTargetIds,
        // Dependencies
        hoveredToken,
        selectedTokenId,
        tokens: actionTokens,
        gridSize,
        // @date_of_change: 2025-10-13 - Use explicit grid context from hook
        gridType: gridType,
        feetPerCell: feetPerCell,
        rulesetExecutionState: actionRulesetExecutionState,
        // Action economy
        consumeActionEconomy,
        // Targeting state
        setArmedAction,
        setTargetingEnabled,
        // Multiattack state
        setMultiattackSequence,
        selectedTargets,
        // Flags
        willEnterTargetingMode: willEnterTargetingModeValue,
        actionEconomyAlreadyConsumed,
        gmOverride: engineGmOverride
      });

      console.log('🗡️ [ATTACK:RESULT]', result);
    } catch (error) {
      console.error('🗡️ [ATTACK:ERROR]', error);
      NotificationManager.error(`Attack processing failed: ${error.message}`);
      return false;
    }

    // Handle different result types
    if (!result.success) {
      // @date_of_change 2025-09-14 - IMPROVED UX: Handle range errors gracefully
      if (result.allowTargetReselection && result.rangeError) {
        console.log('[BattleRoom] Range error - allowing target reselection');

        // Extract more helpful range information from the error
        const rangeErrorMessage = result.errors?.find(e => e.includes('out of range')) || result.reason || '';
        const invalidTargetId = Array.isArray(explicitTargetIds) ? explicitTargetIds[0] : null;
        const targetName = actionTokens.find((token) => String(token?.id) === String(invalidTargetId))?.name
          || rangeErrorMessage.match(/Target (.+?) out of range/)?.[1]
          || 'That target';

        // Show helpful message with range information
        let rangeInfo = '';
        if (actionData.range && typeof actionData.range === 'object' && actionData.range.normal) {
          rangeInfo = ` (Range: ${actionData.range.normal}/${actionData.range.long} ft)`;
        } else if (actionData.range && typeof actionData.range === 'string') {
          rangeInfo = ` (Range: ${actionData.range})`;
        }

        NotificationManager.warning(
          'Target out of range',
          `${targetName} is out of range for ${actionData.name}${rangeInfo}. Select a different target or move closer.`
        );

        // Keep the action armed but clear the invalid target selection
        // Don't clear armedAction - keep it so user can select a different target
        return 'retarget'; // Special return value to indicate retargeting needed
      }

      console.warn('[BattleRoom] Attack processing failed:', result.reason);
      return false;
    }

    // Entering targeting mode has not resolved mechanics yet.
    if (result.targetingMode) {
      return true;
    }

    // Starting a multiattack consumes its main action before component
    // targeting begins. Persist that canonical flag before advertising the
    // sequence as armed or writing its audit event. date_of_change: 2026-07-18
    if (result.multiattack && result.processorResult) {
      const multiattackStage = buildMergedCombatStageUpdate(attacker, result.processorResult.combatStageUpdates?.attacker);
      const committed = wsSessionId && actionData.mechanics
        ? await commitCreatureResolution(attacker, actionData, result.processorResult,
          multiattackStage ? [{ id: attacker.id, props: multiattackStage }] : [], [], result.combatState, engineGmOverride)
        : !multiattackStage || await handleBatchTokenUpdates([{
        id: attacker.id,
        props: multiattackStage,
        _engineUpdate: true
      }], { awaitAuthoritative: true });
      if (!committed) {
        ActionEconomyManager.synchronizeCombatFlags(attacker.id, attacker.combatFlags || {}, ActionEconomyProcessor.parseSpeedFeet(attacker?.speed));
        NotificationManager.error('Multiattack not committed', 'Authoritative VCS state did not confirm the spent action.');
        setMultiattackSequence(null);
        setArmedAction(null);
        setTargetingEnabled(false);
        return false;
      }
      ActionEconomyProcessor.logAction(
        result.processorResult,
        attacker,
        result.targets || [],
        engineGmOverride,
        result.processorRequest,
        result.combatState
      );
      return true;
    }

    // @date_of_change 2025-09-17 - CRITICAL FIX: Handle AoE abilities that were already processed
    if (result.aoeComplete && !result.processorResult) {
      console.log('✅ [AOE_BATTLE_COMPLETE]', {
        actionName: actionData.name,
        reason: result.reason,
        targetCount: result.targets?.length || 0
      });

      // AoE abilities are already fully processed by ActionEconomyProcessor
      // No additional execution needed, just notify success
      NotificationManager.success(`${actionData.name} affected ${result.targets?.length || 0} targets`);
      return true;
    }

    if ((result.processedByActionEconomy || result.aoeComplete) && result.processorResult) {
      // @date_of_change 2025-10-12 - Use ActionEconomyProcessor outputs for combat resolution to avoid duplicate execution
      // rule_reference: AGENTS.md (engine determinism; avoid double rolls and HP drift)
      const proc = result.processorResult;
      const procResults = Array.isArray(proc.results) ? proc.results : [];
      const messages = [];
      const errorMessages = [];
      const tokenUpdates = [];

      const attackerStageUpdate = proc.combatStageUpdates?.attacker;
      const mergedAttackerUpdate = buildMergedCombatStageUpdate(attacker, attackerStageUpdate);
      if (mergedAttackerUpdate) {
        tokenUpdates.push({ id: attacker.id, props: mergedAttackerUpdate, _engineUpdate: true });
      }

      if (Array.isArray(proc.combatStageUpdates?.targets)) {
        proc.combatStageUpdates.targets.forEach((targetUpdate) => {
          const targetToken = actionTokens.find((token) => token?.id === targetUpdate?.id);
          const merged = buildMergedCombatStageUpdate(targetToken, targetUpdate);
          const stateOnlyUpdate = wsSessionId ? withoutHitPointPatch(merged) : merged;
          if (targetToken && stateOnlyUpdate && Object.keys(stateOnlyUpdate).length > 0) tokenUpdates.push({ id: targetToken.id, props: stateOnlyUpdate, _engineUpdate: true });
        });
      }

      procResults.forEach((attackOutcome, index) => {
        const targetId = attackOutcome.targetId;
        const targetToken = targetId ? actionTokens.find(t => t.id === targetId) : null;

        if (!targetToken) {
          console.warn('[BattleRoom] ⚠️ Unable to resolve target token for ActionEconomy result', {
            attackOutcome,
            index
          });
          return;
        }

        if (attackOutcome.updatedTarget) {
          const updated = attackOutcome.updatedTarget;
          const newHp = updated.hpObj?.current ?? updated.hp ?? targetToken.hpObj?.current ?? targetToken.hp;
          const maxHp = updated.hpObj?.max ?? updated.maxHp ?? targetToken.hpObj?.max ?? targetToken.maxHp;
          const tempHp = updated.hpObj?.temp ?? updated.tempHp ?? targetToken.hpObj?.temp ?? targetToken.tempHp ?? 0;

          const newProps = {
            hpObj: {
              ...(targetToken.hpObj || {}),
              current: newHp,
              ...(maxHp !== undefined ? { max: maxHp } : {}),
              temp: tempHp
            },
            hp: newHp,
            ...(maxHp !== undefined ? { maxHp: maxHp } : {}),
            tempHp
          };
          if (!wsSessionId) tokenUpdates.push({ id: targetToken.id, props: newProps, _engineUpdate: true });
        } else if (attackOutcome.damage?.total) {
          console.error('[BattleRoom] ❗ ActionEconomy result missing updatedTarget for damage application', {
            attacker: attacker?.id,
            targetId: targetToken.id,
            damage: attackOutcome.damage,
            attackOutcome
          });
          NotificationManager.error(`Attack resolution missing HP update for ${targetToken.name}`);
        }

        const attackRoll = attackOutcome.attackRoll;
        if (attackRoll) {
          const targetAc = attackOutcome.targetAC ?? attackOutcome.defense ?? '??';
          const rollInfo = ` (rolled ${attackRoll.total}${targetAc !== '??' ? ` vs AC ${targetAc}` : ''})`;
          if (attackRoll.isHit) {
            const damageInfo = attackOutcome.damage ? ` for ${attackOutcome.damage.total} ${attackOutcome.damage.type || ''} damage` : '';
            const criticalInfo = attackRoll.isCritical ? ' (CRITICAL HIT!)' : '';
            const uncannyInfo = attackOutcome.uncannyDodge?.used
              ? ` ${targetToken.name} uses Uncanny Dodge, reducing ${attackOutcome.uncannyDodge.originalDamage} damage to ${attackOutcome.uncannyDodge.reducedDamage}.`
              : '';
            const cunningInfo = attackOutcome.cunningStrike
              ? (attackOutcome.cunningStrike.effect === 'withdraw'
                ? ` Cunning Strike: Withdraw is armed for up to ${attackOutcome.cunningStrike.maxFeet} feet.`
                : (attackOutcome.cunningStrike.immune
                  ? ` ${targetToken.name} is immune to Cunning Strike: ${attackOutcome.cunningStrike.label}.`
                  : ` Cunning Strike: ${attackOutcome.cunningStrike.label} ${attackOutcome.cunningStrike.conditionApplied ? `applies ${attackOutcome.cunningStrike.conditionApplied}` : 'does not take effect'}.`))
              : '';
            messages.push(`${attacker.name} hits ${targetToken.name}${rollInfo}${damageInfo}${criticalInfo}.${uncannyInfo}${cunningInfo}`);
          } else {
            messages.push(`${attacker.name} misses ${targetToken.name}${rollInfo}.`);
          }
        } else if (attackOutcome.reason) {
          errorMessages.push(`${attacker.name} could not resolve ${actionData.name}: ${attackOutcome.reason}`);
        }
      });

      const atomicCreature = Boolean(wsSessionId && actionData.mechanics);
      if (atomicCreature && !await commitCreatureResolution(attacker, actionData, proc, tokenUpdates, procResults, result.combatState, engineGmOverride)) return 'retry';
      if (!atomicCreature && tokenUpdates.length > 0) {
        const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
        if (!committed) {
          ActionEconomyManager.synchronizeCombatFlags(attacker.id, attacker.combatFlags || {}, ActionEconomyProcessor.parseSpeedFeet(attacker?.speed));
          NotificationManager.error('Attack not committed', `${actionData.name}: authoritative VCS state did not confirm the result. No combat log entry was created.`);
          return false;
        }
      }

      try {
        if (!atomicCreature) await applyAuthoritativeDamageOutcomes(attacker.id, procResults, actionData.name);
        if (!atomicCreature) await applyAuthoritativeHealingOutcomes(attacker.id, procResults);
      } catch (cause) {
        NotificationManager.error('Damage not committed', cause?.message || `${actionData.name}: the server rejected the damage result.`);
        return false;
      }

      // Reconcile the transient calculator only after the canonical batch has
      // confirmed reaction expenditure. This preserves one source of truth.
      // date_of_change: 2026-07-18
      (proc.combatStageUpdates?.targets || []).forEach((targetUpdate) => {
        if (!targetUpdate?.combatFlags) return;
        const targetToken = actionTokens.find((token) => token?.id === targetUpdate.id);
        ActionEconomyManager.synchronizeCombatFlags(
          targetUpdate.id,
          { ...(targetToken?.combatFlags || {}), ...targetUpdate.combatFlags },
          ActionEconomyProcessor.parseSpeedFeet(targetToken?.speed)
        );
      });

      if (!atomicCreature || actionData.mechanics.kind !== 'attack') ActionEconomyProcessor.logAction(
        proc,
        attacker,
        result.targets || [],
        engineGmOverride,
        result.processorRequest,
        result.combatState
      );
      messages.forEach(msg => NotificationManager.success('Attack Result', msg));
      errorMessages.forEach(msg => NotificationManager.error('Attack Failed', msg));

      setArmedAction(null);
      setSelectedTargets([]);
      setTargetingEnabled(false);

      return true;
    }

    // If we have a validated attack ready for execution, continue with existing attack execution logic
    // @date_of_change 2025-10-13 - POLICY: Legacy execution path removed.
    // rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS
    if (result.readyForExecution) {
      console.error('[BattleRoom] Legacy attack execution path is disabled by policy. ActionEconomyProcessor must return processorResult.');
      NotificationManager.error(
        'Action blocked',
        `${actionData.name}: missing ActionEconomyProcessor results (processorResult). Ensure action has canonical mechanics and rerun.`
      );
      return false;
    }

    return false;
  }, [
    hoveredToken, 
    selectedTokenId, 
    tokens, 
    gridSize,
    consumeActionEconomy,
    setArmedAction,
    setTargetingEnabled,
    setMultiattackSequence,
    selectedTargets,
    armedAction,
    feetPerCell,
    gridType,
    handleBatchTokenUpdates,
    commitCreatureResolution,
    applyAuthoritativeDamageOutcomes,
    applyAuthoritativeHealingOutcomes,
    wsSessionId,
    prepareCreatureResolution,
    rulesetExecutionState
  ]);

  const formatSpellSlotChoiceLabel = useCallback((level, slot = null) => {
    const label = level === 0 ? 'Cantrip' : `Level ${level}`;
    if (!slot) return label;
    const total = Number(slot.total) || 0;
    const used = Number(slot.used) || 0;
    const remaining = Math.max(0, total - used);
    return `${label} (${remaining}/${total} left)`;
  }, []);

  const lastServiceSessionTimestampRef = useRef(null);
  const lastServiceRuntimeTurnKeyRef = useRef(null);
  const latestCanonicalTokensRef = useRef(tokens);
  useEffect(() => {
    latestCanonicalTokensRef.current = tokens;
  }, [tokens]);
  useEffect(() => {
    if (!serviceSessionData) return;
    const timestamp = serviceSessionData.timestamp || serviceSessionData.savedAt || null;
    if (timestamp && lastServiceSessionTimestampRef.current === timestamp) return;
    const isInitialServiceRestore = lastServiceSessionTimestampRef.current === null;
    lastServiceSessionTimestampRef.current = timestamp;
    updateRole('gm');
    try {
      const syncPolicy = serviceSessionData.syncPolicy || {};
      const serviceCombatState = serviceSessionData.combatState || {};
      const serviceRuntimeTurnKey = `${Number(serviceCombatState.currentRound) || 1}:${serviceCombatState.currentCombatantId || 'none'}`;
      const isSameServiceTurn = lastServiceRuntimeTurnKeyRef.current === serviceRuntimeTurnKey;
      lastServiceRuntimeTurnKeyRef.current = serviceRuntimeTurnKey;
      const localCombatLogState = syncPolicy.mergeCombatLog && !isInitialServiceRestore
        ? CombatLogManager.exportState()
        : null;
      const incomingBattleState = serviceSessionData.battleState || null;
      const runtimeSafeBattleState = !isInitialServiceRestore
        && isSameServiceTurn
        && syncPolicy.preserveLocalCombatRuntime
        && incomingBattleState
        && Array.isArray(incomingBattleState.tokens)
        ? {
            ...incomingBattleState,
            tokens: preserveLocalCombatRuntime(
              incomingBattleState.tokens,
              latestCanonicalTokensRef.current,
              { combatantReference: serviceCombatState.currentCombatantId || null }
            )
          }
        : incomingBattleState;
      const sessionForRestore = !isInitialServiceRestore && runtimeSafeBattleState
        ? {
            ...serviceSessionData,
            battleState: {
              ...runtimeSafeBattleState,
              ...(syncPolicy.preserveLocalViewport ? {
                zoom: undefined,
                mapState: runtimeSafeBattleState.mapState ? {
                  ...runtimeSafeBattleState.mapState,
                  position: mapState?.position || runtimeSafeBattleState.mapState.position
                } : runtimeSafeBattleState.mapState
              } : {}),
              ...(syncPolicy.preserveLocalSelection ? { selectedTokenId } : {})
            }
          }
        : serviceSessionData;
      const result = loadSessionFromData(sessionForRestore);
      if (result?.success) {
        if (localCombatLogState) {
          CombatLogManager.mergeState(localCombatLogState);
          window.dispatchEvent(new CustomEvent('combat-log-updated', {
            detail: { source: 'GmaBattleRoomBridge', timestamp: Date.now() }
          }));
        }
        restoreBattleRoomState(result);
        const combatState = serviceSessionData.combatState || {};
        const initiativeOrder = Array.isArray(combatState.initiativeOrder) ? combatState.initiativeOrder : [];
        const activeIndex = Math.max(0, initiativeOrder.findIndex((combatant) => (
          String(combatant?.id) === String(combatState.currentCombatantId)
        )));
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent('websocket-initiative-update', {
            detail: {
              isRemoteUpdate: true,
              initiativeData: {
                combatants: initiativeOrder,
                currentTurnIndex: activeIndex,
                activeCombatantId: combatState.currentCombatantId || null,
                round: Number(combatState.currentRound) || 1,
                isInitiativeConfirmed: Boolean(combatState.combatStarted),
                combatStarted: Boolean(combatState.combatStarted)
              }
            }
          }));
        }, 100);
      }
    } catch (error) {
      console.error('[BattleRoom] Failed to apply GMA service snapshot:', error);
    }
  }, [loadSessionFromData, mapState?.position, restoreBattleRoomState, selectedTokenId, serviceSessionData, updateRole]);

  const externalInitiativeState = useMemo(() => {
    if (!serviceSessionData?.initiativeExternallyManaged) return null;
    const combatState = serviceSessionData.combatState || {};
    const initiativeOrder = Array.isArray(combatState.initiativeOrder)
      ? combatState.initiativeOrder
      : [];
    const activeCombatantId = combatState.currentCombatantId || null;
    const activeIndex = Math.max(0, initiativeOrder.findIndex((combatant) => (
      String(combatant?.id) === String(activeCombatantId)
    )));
    const roundNumber = Number(combatState.currentRound) || 1;
    const combatStartedExternally = Boolean(combatState.combatStarted);

    return {
      combatants: initiativeOrder,
      currentTurnIndex: activeIndex,
      activeCombatantId,
      round: roundNumber,
      isInitiativeConfirmed: combatStartedExternally,
      combatStarted: combatStartedExternally,
      revision: [
        activeCombatantId || 'none',
        activeIndex,
        roundNumber,
        combatStartedExternally ? 'started' : 'pending',
        initiativeOrder.map((combatant) => `${combatant?.id}:${combatant?.initiative}`).join('|')
      ].join('::')
    };
  }, [serviceSessionData]);

  const resolveSpellSlotSelection = useCallback(async (attacker, actionData, initialSpellLevel) => {
    const initialLevel = resolveSpellLevel(actionData?.spellLevel, actionData?.level, initialSpellLevel || 1);
    let resolverMeta = SpellHandler.resolve(actionData, initialLevel);

    if (
      actionData?.__slotLevelChosen
      || initialLevel <= 0
      || !resolverMeta?.upcast?.prompt
    ) {
      return { spellLevel: initialLevel, resolverMeta };
    }

    const spellsData = resolveCombatantSpellData(attacker, actionData) || {};
    const slots = spellsData?.slots && typeof spellsData.slots === 'object'
      ? spellsData.slots
      : {};
    const baseLevel = Math.max(1, Number(resolverMeta.upcast.baseLevel) || initialLevel);
    const availableLevels = Object.entries(slots)
      .map(([level, slot]) => ({
        level: Number(level),
        slot
      }))
      .filter(({ level, slot }) => (
        Number.isFinite(level)
        && level >= baseLevel
        && level <= 9
        && Number(slot?.total) > Number(slot?.used || 0)
      ))
      .sort((a, b) => a.level - b.level);

    if (!availableLevels.some(({ level }) => level > initialLevel)) {
      return { spellLevel: initialLevel, resolverMeta };
    }

    const options = availableLevels.map(({ level, slot }) => formatSpellSlotChoiceLabel(level, slot));
    const choice = await requestChoice({
      title: 'Choose Spell Slot',
      prompt: `${actionData?.name || 'This spell'} can be cast using available spell slots. Choose the slot before selecting targets.`,
      options
    });
    if (!choice) {
      return { spellLevel: initialLevel, resolverMeta, cancelled: true };
    }
    const selected = availableLevels.find(({ level, slot }) => formatSpellSlotChoiceLabel(level, slot) === choice);
    const selectedLevel = selected?.level || initialLevel;

    resolverMeta = SpellHandler.resolve(actionData, selectedLevel);
    return { spellLevel: selectedLevel, resolverMeta };
  }, [formatSpellSlotChoiceLabel]);

  // Handle spell casting action
  const handleSpellAction = useCallback(async (attacker, actionData, explicitTargetIds = [], engineGmOverride = null) => {
    // @date_of_change 2025-09-16 - Enhanced spell action debugging for AoE detection
    debugLog(`[BattleRoom] Processing spell: ${actionData.name} by ${attacker.name}`);
    debugLog('[BattleRoom] SPELL_DEBUG - Full action data:', actionData);
    debugLog('[BattleRoom] SPELL_DEBUG - OriginalAction data:', actionData.originalAction);
    debugLog('[BattleRoom] SPELL_DEBUG - Has areaOfEffect:', !!actionData.areaOfEffect);
    debugLog('[BattleRoom] SPELL_DEBUG - Has area:', !!actionData.area);
    debugLog('[BattleRoom] SPELL_DEBUG - Has description:', !!actionData.description);
    debugLog('[BattleRoom] SPELL_DEBUG - Has desc:', !!actionData.desc);
    debugLog('[BattleRoom] SPELL_DEBUG - Description content:', actionData.description);
    debugLog('[BattleRoom] SPELL_DEBUG - Desc content:', actionData.desc);

    // @date_of_change 2025-09-16 - CRITICAL FIX: Handle both description and desc field names
    // The action data may have been transformed, so check both possible field names
    const spellDescription = actionData.description || actionData.desc || '';
    const spellLevel = resolveSpellLevel(actionData.spellLevel, actionData.level, 1);
    const slotSelection = await resolveSpellSlotSelection(attacker, actionData, spellLevel);
    if (slotSelection.cancelled) {
      NotificationManager.info('Spell cancelled', `${actionData.name}: no spell slot selected.`);
      return false;
    }
    const selectedSpellLevel = slotSelection.spellLevel;
    const selectedSpellAction = {
      ...actionData,
      spellLevel: selectedSpellLevel,
      // The selected slot is the combat-facing spell level for upcast resolution.
      // rule_reference: D&D 5e 2024 spellcasting slots; date_of_change: 2026-06-05
      level: selectedSpellLevel,
      __slotLevelChosen: true
    };
    debugLog('[BattleRoom] SPELL_DEBUG - Resolved description:', spellDescription);
    debugLog('[BattleRoom] SPELL_DEBUG - Resolved level:', selectedSpellLevel);

    // Check if spell requires targeting and no targets provided
    // @date_of_change 2025-09-19 - Enhanced targeting detection for spells with missing range data
    const spellDescriptionLower = spellDescription.toLowerCase();

    // Enhanced targeting detection - check for explicit range or context clues
    const hasValidRange = selectedSpellAction.range && selectedSpellAction.range !== 'Self' && selectedSpellAction.range !== 'Personal';
    const isTouchSpell = spellDescriptionLower.includes('you touch') || spellDescriptionLower.includes('a creature you touch') || spellDescriptionLower.includes('touch a');
    const isTargetedSpell = spellDescriptionLower.includes('target') || spellDescriptionLower.includes('creature') || spellDescriptionLower.includes('ally');
    let requiresTargeting = hasValidRange || isTouchSpell || (isTargetedSpell && !spellDescriptionLower.includes('yourself'));
    // @date_of_change 2025-10-23 - Use SpellResolver meta to force targeting for creature-targeted buffs (e.g., Bless)
    try {
      const metaForTargeting = slotSelection.resolverMeta || SpellHandler.resolve(selectedSpellAction, selectedSpellLevel);
      const metaWantsCreatureTargets = !!(metaForTargeting && metaForTargeting.targeting === 'creature');
      if (metaWantsCreatureTargets) {
        requiresTargeting = true;
      }
    } catch (_) {}

    // Self-only auto-exec: only when resolver explicitly says targeting === 'self'
    // Many creature-targeting buffs (e.g., Bless) may have range text 'Self' upstream; do NOT auto-exec those.
    try {
      const resolvedSpellLevel = selectedSpellLevel;
      const metaCheck = slotSelection.resolverMeta || SpellHandler.resolve(selectedSpellAction, resolvedSpellLevel);
      if (metaCheck && metaCheck.targeting === 'self') {
        const enrichedActionData = {
          ...selectedSpellAction,
          spellLevel: resolvedSpellLevel,
          level: resolvedSpellLevel,
          options: { ...(selectedSpellAction.options || {}), gridSize, feetPerCell, gridType, meta: metaCheck }
        };
        const executionBundle = buildCombatExecutionBundle({
          attackerId: attacker?.id,
          attacker,
          actionData: enrichedActionData,
          targetIds: []
        }, {
          attacker,
          tokens,
          gridSize,
          gridType,
          feetPerCell,
          extraCombatState: { deferAudit: true, ...rulesetExecutionState }
        });
        const processorResult = await ActionEconomyProcessor.processAction(
          executionBundle.processorRequest,
          executionBundle.combatState,
          engineGmOverride
        );
        if (!processorResult.success) {
          const errorMessage = formatCombatActionError(processorResult, 'Spell execution failed');
          if (processorResult.requiresGameMasterResolution) {
            NotificationManager.warning(
              'Game Master resolution required',
              `${enrichedActionData.name}: ${errorMessage} Resolve it manually or hand it to the configured AI.`
            );
          } else {
            NotificationManager.error('Spell execution failed', `${enrichedActionData.name}: ${errorMessage}`);
          }
          return false;
        }
        const attackerStageUpdate = processorResult.combatStageUpdates?.attacker;
        const mergedAttackerUpdate = buildMergedCombatStageUpdate(attacker, attackerStageUpdate);
        if (mergedAttackerUpdate) {
          const committed = await handleBatchTokenUpdates([{
            id: attacker.id,
            props: mergedAttackerUpdate,
            _engineUpdate: true
          }], { awaitAuthoritative: true });
          if (!committed) {
            ActionEconomyManager.synchronizeCombatFlags(attacker.id, attacker.combatFlags || {}, ActionEconomyProcessor.parseSpeedFeet(attacker?.speed));
            NotificationManager.error('Spell not committed', `${enrichedActionData.name}: authoritative VCS state did not confirm the result.`);
            return false;
          }
        }
        ActionEconomyProcessor.logAction(
          processorResult,
          attacker,
          executionBundle.processorRequest.targets || [],
          engineGmOverride,
          executionBundle.processorRequest,
          executionBundle.combatState
        );
        NotificationManager.success('Spell resolved', processorResult.effect || enrichedActionData.name);
        setArmedAction(null);
        setSelectedTargets([]);
        setTargetingEnabled(false);
        setMultiattackSequence(null);
        setMultiattackTarget(null);
        return true;
      }
    } catch (_) {}

    debugLog(`[BattleRoom] TARGETING_DEBUG - ${selectedSpellAction.name}:`, {
      range: selectedSpellAction.range,
      hasValidRange,
      isTouchSpell,
      isTargetedSpell,
      requiresTargeting,
      descriptionSample: spellDescriptionLower.substring(0, 50) + '...'
    });

    const hasTargets = explicitTargetIds && explicitTargetIds.length > 0;
    
    if (requiresTargeting && !hasTargets) {
      // @date_of_change 2025-09-16 - AOE TARGETING SYSTEM with enhanced debugging and nested area detection
      // Check if spell has area of effect - look at multiple possible locations
      const directArea = selectedSpellAction.areaOfEffect || selectedSpellAction.area;
      const nestedArea = selectedSpellAction.originalAction?.area;
      const hasAreaOfEffect = !!(directArea || nestedArea);

      debugLog('[BattleRoom] AOE_DEBUG - Direct area:', directArea);
      debugLog('[BattleRoom] AOE_DEBUG - Nested area:', nestedArea);
      debugLog('[BattleRoom] AOE_DEBUG - hasAreaOfEffect:', hasAreaOfEffect);

      // @date_of_change 2025-09-15 - Enhanced AoE detection to parse spell descriptions
      // Parse area effects from spell description if not explicitly defined
      let aoeInfo = null;
      const areaData = directArea ?? nestedArea;

      // @date_of_change 2025-10-12 - Inline helper to keep Lightning Bolt line dimensions per PHB 2024
      const coerceLineDimensions = (info, hintTexts = []) => {
        if (!info || typeof info !== 'object') return info;
        const shapeName = (info.shape || info.type || '').toLowerCase();
        if (shapeName !== 'line') return info;

        let resolvedLength = Number.isFinite(info.length)
          ? info.length
          : Number.isFinite(info.size)
            ? info.size
            : 0;
        let resolvedWidth = Number.isFinite(info.width)
          ? info.width
          : Number.isFinite(info.thickness)
            ? info.thickness
            : 5;

        const extractLineDimensions = (text) => {
          if (!text || typeof text !== 'string') return null;
          const lower = text.toLowerCase();

          const byMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*by\s*(\d+)-?(?:foot|ft)?\s*line/);
          if (byMatch) {
            return {
              length: parseInt(byMatch[1], 10),
              width: parseInt(byMatch[2], 10)
            };
          }

          const longWideMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*(?:long|length)\s*(?:and|by)\s*(\d+)\s*(?:foot|ft|feet)\s*wide/);
          if (longWideMatch) {
            return {
              length: parseInt(longWideMatch[1], 10),
              width: parseInt(longWideMatch[2], 10)
            };
          }

          const wideLongMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*wide\s*(?:and|by)\s*(\d+)\s*(?:foot|ft|feet)\s*(?:long|length)/);
          if (wideLongMatch) {
            return {
              length: parseInt(wideLongMatch[2], 10),
              width: parseInt(wideLongMatch[1], 10)
            };
          }

          const rangeLineMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*line/);
          if (rangeLineMatch) {
            return {
              length: parseInt(rangeLineMatch[1], 10),
              width: null
            };
          }

          const lengthMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*(?:long|length)/);
          const widthMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*wide/);
          if (lengthMatch || widthMatch) {
            return {
              length: lengthMatch ? parseInt(lengthMatch[1], 10) : null,
              width: widthMatch ? parseInt(widthMatch[1], 10) : null
            };
          }

          return null;
        };

        const hintArray = Array.isArray(hintTexts) ? hintTexts : [hintTexts];
        for (const text of hintArray) {
          const dims = extractLineDimensions(text);
          if (!dims) continue;
          if (Number.isFinite(dims.length) && (!Number.isFinite(resolvedLength) || dims.length > resolvedLength)) {
            resolvedLength = dims.length;
          }
          if (Number.isFinite(dims.width) && (!Number.isFinite(resolvedWidth) || dims.width > resolvedWidth)) {
            resolvedWidth = dims.width;
          }
        }

        if (!Number.isFinite(resolvedWidth) || resolvedWidth <= 0) {
          resolvedWidth = 5;
        }
        if (!Number.isFinite(resolvedLength) || resolvedLength <= 0) {
          resolvedLength = resolvedWidth;
        }
        if (resolvedLength < resolvedWidth) {
          const swap = resolvedLength;
          resolvedLength = resolvedWidth;
          resolvedWidth = swap;
        }

        return {
          ...info,
          shape: info.shape || 'line',
          type: info.type || info.shape || 'line',
          length: resolvedLength,
          size: resolvedLength,
          width: resolvedWidth,
          thickness: resolvedWidth,
        };
      };

      const parseAoEString = (text) => {
        if (!text || typeof text !== 'string') return null;
        const lower = text.toLowerCase();

        const sphereMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*(?:radius)?\s*(?:sphere|circle)/);
        if (sphereMatch) {
          return { shape: 'sphere', size: parseInt(sphereMatch[1], 10) };
        }

        const cylinderMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*(?:radius)?\s*(?:,\s*\d+-?(?:foot|ft)?\s*(?:high|tall))?\s*cylinder/);
        if (cylinderMatch) {
          // Treat cylinders as spheres for targeting templates per VTT convention
          return { shape: 'sphere', size: parseInt(cylinderMatch[1], 10) };
        }

        const cubeMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*(?:cube|square)/);
        if (cubeMatch) {
          return { shape: 'cube', size: parseInt(cubeMatch[1], 10) };
        }

        const coneMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*cone/);
        if (coneMatch) {
          return { shape: 'cone', size: parseInt(coneMatch[1], 10) };
        }

        const lineByMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*by\s*(\d+)-?(?:foot|ft)?\s*line/);
        const lineDirectMatch = lower.match(/(\d+)-?(?:foot|ft)?\s*line/);
        const lineLongWideMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*(?:long|length)\s*(?:and|by)\s*(\d+)\s*(?:foot|ft|feet)\s*wide/);
        const lineWideLongMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*wide\s*(?:and|by)\s*(\d+)\s*(?:foot|ft|feet)\s*(?:long|length)/);
        const lineLengthMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*(?:line|long|length)/);
        const lineWidthMatch = lower.match(/(\d+)\s*(?:foot|ft|feet)\s*wide/);

        if (lineByMatch || lineDirectMatch || lineLongWideMatch || lineWideLongMatch || lineLengthMatch) {
          let lengthVal = null;
          let widthVal = null;

          if (lineByMatch) {
            lengthVal = parseInt(lineByMatch[1], 10);
            widthVal = parseInt(lineByMatch[2], 10);
          } else if (lineLongWideMatch) {
            lengthVal = parseInt(lineLongWideMatch[1], 10);
            widthVal = parseInt(lineLongWideMatch[2], 10);
          } else if (lineWideLongMatch) {
            widthVal = parseInt(lineWideLongMatch[1], 10);
            lengthVal = parseInt(lineWideLongMatch[2], 10);
          } else if (lineDirectMatch) {
            lengthVal = parseInt(lineDirectMatch[1], 10);
            if (lineWidthMatch) widthVal = parseInt(lineWidthMatch[1], 10);
          } else if (lineLengthMatch) {
            lengthVal = parseInt(lineLengthMatch[1], 10);
            if (lineWidthMatch) widthVal = parseInt(lineWidthMatch[1], 10);
          }

          if (lengthVal) {
            return coerceLineDimensions({ shape: 'line', size: lengthVal, length: lengthVal, width: widthVal ?? 5 }, text);
          }
        }

        return null;
      };

      if (areaData) {
        if (typeof areaData === 'object' && areaData !== null && !Array.isArray(areaData)) {
          if (areaData.shape && (areaData.size != null || areaData.length != null || areaData.radius != null)) {
            const baseLength = Number.isFinite(areaData.length)
              ? areaData.length
              : Number.isFinite(areaData.size)
                ? areaData.size
                : Number.isFinite(areaData.radius)
                  ? areaData.radius
                  : 0;
            aoeInfo = {
              ...areaData,
              size: Number.isFinite(areaData.size) ? areaData.size : baseLength,
              length: baseLength,
            };
            if (Number.isFinite(areaData.width)) aoeInfo.width = areaData.width;
            if (Number.isFinite(areaData.thickness)) aoeInfo.thickness = areaData.thickness;
            aoeInfo = coerceLineDimensions(aoeInfo);
          }
        } else if (typeof areaData === 'string') {
          aoeInfo = parseAoEString(areaData);
        }
      }

      if (!aoeInfo && spellDescription) {
        aoeInfo = parseAoEString(spellDescription);
      }

      if (!aoeInfo && typeof selectedSpellAction.name === 'string' && selectedSpellAction.name.toLowerCase().includes('fireball')) {
        aoeInfo = { shape: 'sphere', size: 20 };
      }

      if (aoeInfo) {
        aoeInfo = coerceLineDimensions(aoeInfo);
        console.log(`[BattleRoom] 🔮 Enabling AoE targeting for ${aoeInfo.shape} (${aoeInfo.size} ft)`);

        // Enable AoE targeting mode
        setAoeTargeting({
          enabled: true,
          targetPoint: null,
          areaOfEffect: aoeInfo,
          direction: 0, // Default direction, user can adjust for cones/lines
          targetMode: 'center', // Default to center targeting
          spell: selectedSpellAction,
          caster: attacker,
          gmOverride: engineGmOverride
        });

        // Disable regular targeting
        setTargetingEnabled(false);
        setArmedAction(null);

        NotificationManager.info(
          `AoE Targeting Active`,
          `Click on the grid to target ${selectedSpellAction.name} (${aoeInfo.shape}, ${aoeInfo.size} ft radius)`
        );

        return;
      }

      // Regular single-target or multi-target spell targeting
      debugLog(`[BattleRoom] Spell ${selectedSpellAction.name} requires targeting`);
      debugLog('[BattleRoom] SPELL_LEVEL_DEBUG - spellLevel:', selectedSpellAction.spellLevel, 'level:', selectedSpellAction.level, 'resolved:', selectedSpellLevel, 'fallback: 1');
      const finalSpellLevel = selectedSpellLevel;
      const resolverMeta = slotSelection.resolverMeta || SpellHandler.resolve(selectedSpellAction, finalSpellLevel);
      debugLog('[BattleRoom] SPELL_LEVEL_DEBUG - Final resolved level:', finalSpellLevel);
      const isMultiComponent = !!(resolverMeta && resolverMeta.multiComponent && resolverMeta.multiComponent.targeting === 'per_component');
      let allowedTargets = 1;
      if (resolverMeta?.multiComponent && resolverMeta.multiComponent.targeting === 'per_component') {
        const base = resolverMeta.multiComponent.baseCount || 0;
        const perSlot = resolverMeta.multiComponent.perSlot || 0;
        const inc = Math.max(0, (finalSpellLevel - (resolverMeta.upcast?.baseLevel || finalSpellLevel)) * perSlot);
        allowedTargets = base + inc;
      } else if (resolverMeta?.upcast?.extraTargetsPerSlot) {
        const baseLevel = resolverMeta.upcast.baseLevel || finalSpellLevel;
        const extra = Math.max(0, finalSpellLevel - baseLevel) * (resolverMeta.upcast.extraTargetsPerSlot || 0);
        allowedTargets = 1 + extra;
      }
      // Every targeting transaction starts empty. Legal self-targeting remains
      // available as an explicit player choice, but is never silently forced.
      // date_of_change: 2026-08-20
      setSelectedTargets([]);
      setArmedAction({
        attackerId: attacker.id,
        action: { ...selectedSpellAction, spellLevel: finalSpellLevel, level: finalSpellLevel },
        multiTarget: isMultiComponent || allowedTargets > 1,
        requiredTargets: 1,
        allowedTargets,
        allowSelf: !!resolverMeta?.allowSelf,
        gmOverride: engineGmOverride
      });
      setTargetingEnabled(true);
      const selfNote = resolverMeta?.allowSelf ? ' (including yourself)' : '';
      NotificationManager.info('Select Targets', `Select up to ${allowedTargets} target(s) for ${selectedSpellAction.name}${selfNote}`);
      return;

    }
    // @date_of_change 2025-10-23 - Execute resolved spell through ActionEconomyProcessor (Magic Missile fix)
    try {
      const resolvedSpellLevel = selectedSpellLevel;
      const resolverMeta = slotSelection.resolverMeta || SpellHandler.resolve(selectedSpellAction, resolvedSpellLevel);

      const explicitTargets = Array.isArray(explicitTargetIds) ? explicitTargetIds : [];
      const enrichedActionData = {
        ...selectedSpellAction,
        spellLevel: resolvedSpellLevel,
        level: resolvedSpellLevel,
        options: {
          ...(selectedSpellAction.options || {}),
          gridSize,
          feetPerCell,
          gridType,
          meta: resolverMeta
        }
      };
      const executionBundle = buildCombatExecutionBundle({
        attackerId: attacker?.id,
        attacker,
        actionData: enrichedActionData,
        targetIds: explicitTargets
      }, {
        attacker,
        tokens,
        gridSize,
        gridType,
        feetPerCell,
        extraCombatState: { deferAudit: true, ...rulesetExecutionState }
      });

      if (explicitTargets.length > 0 && executionBundle.unresolvedTargetIds.length > 0) {
        console.error('[BattleRoom] ❌ Spell execution aborted - unresolved target tokens', {
          spellName: actionData.name,
          explicitTargets,
          resolvedTargets: executionBundle.targets.map(t => t.id),
          unresolvedTargets: executionBundle.unresolvedTargetIds
        });
        NotificationManager.error(
          'Spell execution failed',
          `${selectedSpellAction.name}: could not resolve ${executionBundle.unresolvedTargetIds.length} selected target(s).`
        );
        return false;
      }

      debugLog('[BattleRoom] Executing spell through ActionEconomyProcessor', {
        spellName: enrichedActionData.name,
        targetIds: explicitTargets,
        spellLevel: resolvedSpellLevel,
        metaKind: resolverMeta?.kind
      });

      const processorResult = await ActionEconomyProcessor.processAction(
        executionBundle.processorRequest,
        executionBundle.combatState,
        engineGmOverride
      );

      if (!processorResult.success) {
        const failureFeedback = buildCombatActionFailureFeedback(processorResult, {
          actionName: enrichedActionData.name,
          targetName: executionBundle.processorRequest.targets?.[0]?.name,
          range: enrichedActionData.range,
          title: 'Spell execution failed',
          fallback: 'Spell execution failed'
        });
        const errorMessage = formatCombatActionError(processorResult, 'Spell execution failed');
        console.error('[BattleRoom] ❌ Spell execution failed:', {
          spellName: enrichedActionData.name,
          error: errorMessage,
          processorResult
        });
        if (processorResult.requiresGameMasterResolution) {
          NotificationManager.warning(
            'Game Master resolution required',
            `${enrichedActionData.name}: ${errorMessage} Resolve it manually or hand it to the configured AI.`
          );
        } else {
          if (failureFeedback.disposition === 'retarget') {
            NotificationManager.warning(failureFeedback.title, failureFeedback.message);
          } else {
            NotificationManager.error(failureFeedback.title, failureFeedback.message);
          }
        }
        // Failed validation spends nothing. Keep the existing targeting
        // transaction open so the player can correct the target or review the
        // actionable canonical-data error. date_of_change: 2026-08-31
        return failureFeedback.disposition;
      }

      const tokenUpdates = [];
      let concentrationSpellName = null;
      let concentrationEffectInstanceId = null;
      const getConcentrationEffectInstanceId = () => {
        if (!concentrationEffectInstanceId) {
          concentrationEffectInstanceId = `effect:${String(enrichedActionData.id || enrichedActionData.name || 'spell').replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 70)}:${Date.now()}`;
        }
        return concentrationEffectInstanceId;
      };
      const attackerStageUpdate = processorResult.combatStageUpdates?.attacker;
      const mergedAttackerUpdate = buildMergedCombatStageUpdate(attacker, attackerStageUpdate);
      if (mergedAttackerUpdate) {
        tokenUpdates.push({ id: attacker.id, props: mergedAttackerUpdate, _engineUpdate: true });
      }

      const spellResult = processorResult.result || null;
      const affectedSpellTargets = Array.isArray(spellResult?.affectedTargets) && spellResult.affectedTargets.length > 0
        ? spellResult.affectedTargets
        : (Array.isArray(processorResult.affectedTargets) ? processorResult.affectedTargets : []);

      if (affectedSpellTargets.length) {
        affectedSpellTargets.forEach(targetInfo => {
          const targetToken = tokens.find(t => t.id === targetInfo.targetId);
          if (!targetToken) {
            console.warn('[BattleRoom] ⚠️ Unable to resolve target token for spell result', targetInfo);
            return;
          }

          // If engine returned an updated target, use it; otherwise, apply healing fallback when provided
          if (targetInfo.updatedTarget) {
            const updated = targetInfo.updatedTarget;
            const newHp = updated.hpObj?.current ?? updated.hp ?? targetToken.hpObj?.current ?? targetToken.hp;
            const maxHp = updated.hpObj?.max ?? updated.maxHp ?? targetToken.hpObj?.max ?? targetToken.maxHp;
            const tempHp = updated.hpObj?.temp ?? updated.tempHp ?? targetToken.hpObj?.temp ?? targetToken.tempHp ?? 0;
              if (!wsSessionId) tokenUpdates.push({
              id: targetToken.id,
              props: {
                hpObj: {
                ...(targetToken.hpObj || {}),
                current: newHp,
                max: maxHp,
                temp: tempHp
                },
                hp: newHp,
                maxHp,
                tempHp
              },
              _engineUpdate: true
            });
          } else if (targetInfo.healing && typeof targetInfo.healing.total === 'number') {
            // Fallback: apply healing to token if engine provided healing amount but not updatedTarget
            try {
              const updated = CombatManager.applyHealing(targetToken, Number(targetInfo.healing.total) || 0);
              const newHp = updated.hpObj?.current ?? updated.hp ?? targetToken.hpObj?.current ?? targetToken.hp;
              const maxHp = updated.hpObj?.max ?? updated.maxHp ?? targetToken.hpObj?.max ?? targetToken.maxHp;
              const tempHp = updated.hpObj?.temp ?? updated.tempHp ?? targetToken.hpObj?.temp ?? targetToken.tempHp ?? 0;
              if (!wsSessionId) tokenUpdates.push({
                id: targetToken.id,
                props: {
                  hpObj: {
                  ...(targetToken.hpObj || {}),
                  current: newHp,
                  max: maxHp,
                  temp: tempHp
                  },
                  hp: newHp,
                  maxHp,
                  tempHp
                },
                _engineUpdate: true
              });
            } catch (_) {}
          }
        });
      }

      if (spellResult?.magicMissile) {
        debugLog('[BattleRoom] Magic Missile allocation applied:', spellResult.magicMissile);
      }

      // @date_of_change: 2025-10-27 - Self-range utility upkeep (Detect Magic, etc.)
      try {
        const isSelfUtility = resolverMeta?.targeting === 'self' && resolverMeta?.kind === 'utility';
        const isDetectMagic = isSelfUtility && String(enrichedActionData.name || '').toLowerCase().includes('detect magic');
        if (isDetectMagic) {
          const durationRounds = Number.isFinite(resolverMeta?.durationRounds) ? resolverMeta.durationRounds : 100;
          const durationSeconds = Number.isFinite(resolverMeta?.durationSeconds) ? resolverMeta.durationSeconds : durationRounds * 6;
          const prev = attacker.statusEffects || {};
          const prevMeta = attacker.statusEffectsMeta || {};
          tokenUpdates.push({
            id: attacker.id,
            props: {
              statusEffects: { ...prev, detect_magic: true, ...(!wsSessionId ? { concentrating: true } : {}) },
              statusEffectsMeta: {
                ...prevMeta,
                detect_magic: {
                  effectInstanceId: getConcentrationEffectInstanceId(),
                  sourceId: attacker.id,
                  roundsRemaining: durationRounds,
                  durationSeconds,
                  concentration: true,
                  description: 'Detect Magic (concentration, 30 ft aura).'
                }
              }
            },
            _engineUpdate: true
          });
          concentrationSpellName = String(enrichedActionData.name || 'Spell');
        }
      } catch (e) {
        console.warn('[BattleRoom] Failed to apply self-utility statusEffects:', e?.message || e);
      }

      // @date_of_change: 2025-10-25 - Apply Bless visual/mechanical flags to targets (statusEffects)
      try {
        const isBless = (resolverMeta?.kind === 'buff') && String(enrichedActionData.name || '').toLowerCase().includes('bless');
        if (isBless && Array.isArray(spellResult?.affectedTargets)) {
          const defaultRounds = 10; // 1 minute
          spellResult.affectedTargets.forEach(tInfo => {
            const t = tokens.find(tok => tok.id === tInfo.targetId);
            if (!t) return;
            const prev = t.statusEffects || {};
            const prevMeta = t.statusEffectsMeta || {};
            tokenUpdates.push({
              id: t.id,
              props: {
                statusEffects: { ...prev, bless: true },
                statusEffectsMeta: {
                  ...prevMeta,
                  bless: { effectInstanceId: getConcentrationEffectInstanceId(), sourceId: attacker.id, roundsRemaining: defaultRounds, concentration: true }
                }
              },
              _engineUpdate: true
            });
          });
          // Mark caster concentrating for UI feedback
          const casterPrev = attacker.statusEffects || {};
          if (!wsSessionId) tokenUpdates.push({
            id: attacker.id,
            props: { statusEffects: { ...casterPrev, concentrating: true } },
            _engineUpdate: true
          });
          concentrationSpellName = String(enrichedActionData.name || 'Spell');
        }
      } catch (e) {
        console.warn('[BattleRoom] Failed to apply Bless statusEffects:', e?.message || e);
      }

      if (tokenUpdates.length > 0) {
        const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
        if (!committed) {
          ActionEconomyManager.synchronizeCombatFlags(attacker.id, attacker.combatFlags || {}, ActionEconomyProcessor.parseSpeedFeet(attacker?.speed));
          NotificationManager.error('Spell not committed', `${enrichedActionData.name}: authoritative VCS state did not confirm the result. No combat log entry was created.`);
          return false;
        }
      }

      try {
        await applyAuthoritativeDamageOutcomes(attacker.id, affectedSpellTargets, enrichedActionData.name);
        await applyAuthoritativeHealingOutcomes(attacker.id, affectedSpellTargets);
      } catch (cause) {
        NotificationManager.error('Spell damage not committed', cause?.message || `${enrichedActionData.name}: the server rejected the damage result.`);
        return false;
      }

      ActionEconomyProcessor.logAction(
        processorResult,
        attacker,
        executionBundle.processorRequest.targets || [],
        engineGmOverride,
        executionBundle.processorRequest,
        executionBundle.combatState
      );
      if (concentrationSpellName) {
        if (wsSessionId) {
          try {
            await sendBattleRoomMutation('concentration:start', {
              tokenId: attacker.id,
              combatantId: attacker.combatantId || attacker.characterId || attacker.monsterId || attacker.id,
              effectInstanceId: concentrationEffectInstanceId || getConcentrationEffectInstanceId(),
              sourceActionId: String(enrichedActionData.id || enrichedActionData.name || 'spell').slice(0, 120),
              sourceName: concentrationSpellName,
            });
          } catch (cause) {
            NotificationManager.error('Concentration not started', cause?.message || 'The concentration effect could not be committed.');
            return false;
          }
        }
      }

      NotificationManager.success('Spell resolved', processorResult.effect || enrichedActionData.name);

      setArmedAction(null);
      setSelectedTargets([]);
      setTargetingEnabled(false);
      setMultiattackSequence(null);
      setMultiattackTarget(null);

      return true;

    } catch (spellError) {
      console.error('[BattleRoom] ❌ Unexpected error executing spell:', spellError);
      NotificationManager.error('Spell execution error', spellError.message || 'Unknown error');
      return false;
    }
    
    // (intentionally removed malformed duplicate success branch)
  }, [tokens, setArmedAction, setTargetingEnabled, setSelectedTargets, gridSize, feetPerCell, gridType, setMultiattackSequence, setMultiattackTarget, handleBatchTokenUpdates, resolveSpellSlotSelection, applyAuthoritativeDamageOutcomes, applyAuthoritativeHealingOutcomes, sendBattleRoomMutation, wsSessionId, rulesetExecutionState]);
  const handleAbilityAction = useCallback(async (attacker, actionData, targetIds = [], engineGmOverride = null) => {
    if (!attacker?.id || !actionData) {
      return false;
    }

    if (actionData.mechanics?.kind === 'moveToward') {
      if (!wsSessionId || targetIds.length !== 1) {
        NotificationManager.error('Movement path required', 'Join a live BattleRoom and select exactly one visible hostile creature. No bonus action was spent.');
        return false;
      }
      creatureMovementRef.current = { tokenId: attacker.id, actionId: actionData.id, targetId: targetIds[0] };
      setMovementIntent('auto');
      setMovementMode('walk');
      setArmedAction(null);
      setTargetingEnabled(false);
      setSelectedTargets([]);
      setMultiattackTargetingPopup((previous) => ({ ...previous, visible: false }));
      setMovementFeedback(`${actionData.name}: drag ${attacker.name} along a path toward the selected creature. No bonus action is spent until movement is committed.`);
      return { pendingMovement: true, resolved: false };
    }

    let executionAttacker = attacker;
    let executionTokens = tokens;
    let executionRulesetState = rulesetExecutionState;
    if (wsSessionId && actionData.mechanics) {
      const preparedResolution = await prepareCreatureResolution();
      if (!preparedResolution) return false;
      executionTokens = preparedResolution.tokens;
      executionRulesetState = preparedResolution.rulesetExecutionState;
      executionAttacker = executionTokens.find((token) => token?.id === attacker.id);
      if (!executionAttacker) {
        NotificationManager.warning('Ability not available', 'The acting creature is no longer on the battlefield. Review the board and choose another action.');
        return false;
      }
    }

    // date_of_change: 2026-06-07
    // WHAT: Resolve ability/save actions through ActionEconomyProcessor instead of a notification-only stub.
    // WHY: Feature, legendary, and save-based actions must consume economy, produce audit details,
    // and write updated target state back into the BattleRoom token snapshot.
    const executionBundle = buildCombatExecutionBundle({
      attackerId: executionAttacker.id,
      attacker: executionAttacker,
      actionData,
      targetIds
    }, {
      attacker: executionAttacker,
      tokens: executionTokens,
      targetIds,
      gridSize,
      feetPerCell,
      gridType,
      extraCombatState: {
        round: CombatLogManager.currentRound || 1,
        turn: CombatLogManager.currentTurn || 0,
        deferAudit: true,
        ...executionRulesetState
      }
    });

    if (executionBundle.unresolvedTargetIds.length > 0) {
      NotificationManager.error(
        'Ability execution failed',
        `${actionData.name}: could not resolve ${executionBundle.unresolvedTargetIds.length} selected target(s).`
      );
      return false;
    }

    const processorResult = await ActionEconomyProcessor.processAction(
      executionBundle.processorRequest,
      executionBundle.combatState,
      engineGmOverride
    );

    if (!processorResult.success) {
      const errorMessage = processorResult.error || processorResult.reason || 'Ability execution failed';
      const rangeError = Array.isArray(processorResult.errors)
        ? processorResult.errors.find((entry) => /out of range|out of .*reach/i.test(String(entry)))
        : null;
      if (rangeError) {
        const targetName = executionBundle.processorRequest.targets?.[0]?.name || 'That target';
        const rangeLabel = typeof actionData?.range === 'string' ? ` (Range: ${actionData.range})` : '';
        NotificationManager.warning(
          'Target out of range',
          `${targetName} is out of range for ${actionData.name}${rangeLabel}. Select a different target or move closer.`
        );
        return 'retarget';
      }
      NotificationManager.error('Ability failed', `${actionData.name}: ${errorMessage}`);
      return false;
    }

    const combatStageUpdates = processorResult.combatStageUpdates || {};
    const tokenUpdates = [];
    const attackerUpdates = buildMergedCombatStageUpdate(executionAttacker, combatStageUpdates.attacker);
    if (attackerUpdates) {
      tokenUpdates.push({
        id: executionAttacker.id,
        props: attackerUpdates,
        _engineUpdate: true
      });
    }

    if (Array.isArray(combatStageUpdates.targets)) {
      combatStageUpdates.targets.forEach((targetUpdate) => {
        const targetToken = executionTokens.find((token) => token?.id === targetUpdate?.id);
        if (!targetToken) return;

        const mergedTargetUpdate = buildMergedCombatStageUpdate(targetToken, targetUpdate);
        if (!mergedTargetUpdate) return;

        const stateOnlyUpdate = wsSessionId ? withoutHitPointPatch(mergedTargetUpdate) : mergedTargetUpdate;
        if (Object.keys(stateOnlyUpdate).length === 0) return;

        tokenUpdates.push({
          id: targetToken.id,
          props: stateOnlyUpdate,
          _engineUpdate: true
        });
      });
    }

    const resultTargets = Array.isArray(processorResult.results)
      ? processorResult.results
      : (Array.isArray(processorResult.affectedTargets) ? processorResult.affectedTargets : []);
    resultTargets.forEach((targetInfo) => {
      if (!targetInfo?.updatedTarget) return;
      const targetId = targetInfo.targetId || targetInfo.updatedTarget.id;
      const targetToken = executionTokens.find((token) => token?.id === targetId);
      if (!targetToken) return;

      const updated = targetInfo.updatedTarget;
      const newHp = updated.hpObj?.current ?? updated.hp ?? targetToken.hpObj?.current ?? targetToken.hp;
      const maxHp = updated.hpObj?.max ?? updated.maxHp ?? targetToken.hpObj?.max ?? targetToken.maxHp;
      const tempHp = updated.hpObj?.temp ?? updated.tempHp ?? targetToken.hpObj?.temp ?? targetToken.tempHp ?? 0;
      if (!wsSessionId) tokenUpdates.push({
        id: targetToken.id,
        props: {
          hpObj: {
          ...(targetToken.hpObj || {}),
          current: newHp,
          ...(maxHp !== undefined ? { max: maxHp } : {}),
          temp: tempHp
          },
          hp: newHp,
          ...(maxHp !== undefined ? { maxHp } : {}),
          tempHp
        },
        _engineUpdate: true
      });

    });

    const atomicCreature = Boolean(wsSessionId && actionData.mechanics);
      if (atomicCreature && !await commitCreatureResolution(executionAttacker, actionData, processorResult, tokenUpdates, resultTargets, executionBundle.combatState, engineGmOverride)) return false;
      if (!atomicCreature && tokenUpdates.length > 0) {
      const committed = await handleBatchTokenUpdates(tokenUpdates, { awaitAuthoritative: true });
      if (!committed) {
        ActionEconomyManager.synchronizeCombatFlags(
          executionAttacker.id,
          executionAttacker.combatFlags || {},
          ActionEconomyProcessor.parseSpeedFeet(executionAttacker?.speed)
        );
        NotificationManager.error(
          'Ability not committed',
          `${actionData.name}: authoritative VCS state did not confirm the change. No combat log entry was created.`
        );
        return false;
      }
    }

    try {
      if (!atomicCreature) await applyAuthoritativeDamageOutcomes(executionAttacker.id, resultTargets, actionData.name);
      if (!atomicCreature) await applyAuthoritativeHealingOutcomes(executionAttacker.id, resultTargets);
    } catch (cause) {
      NotificationManager.error('Ability damage not committed', cause?.message || `${actionData.name}: the server rejected the damage result.`);
      return false;
    }

    if (!atomicCreature || actionData.mechanics.kind !== 'attack') ActionEconomyProcessor.logAction(
      processorResult,
      executionAttacker,
      executionBundle.processorRequest.targets || [],
      engineGmOverride,
      executionBundle.processorRequest,
      executionBundle.combatState
    );

    NotificationManager.info(
      processorResult.requiresGameMasterResolution ? 'Intent recorded' : 'Ability resolved',
      `${executionAttacker.name}: ${processorResult.effect || actionData.name || 'Ability resolved'}`
    );

    setArmedAction(null);
    setSelectedTargets([]);
    setTargetingEnabled(false);
    setMultiattackTargetingPopup((previous) => ({ ...previous, visible: false }));

    return true;
  }, [
    tokens,
    gridSize,
    feetPerCell,
    gridType,
    handleBatchTokenUpdates,
    prepareCreatureResolution,
    commitCreatureResolution,
    applyAuthoritativeDamageOutcomes,
    applyAuthoritativeHealingOutcomes,
    wsSessionId,
    setArmedAction,
    setSelectedTargets,
    setTargetingEnabled,
    setMultiattackTargetingPopup,
    rulesetExecutionState
  ]);

  // Handle actions wrapper for ActionTracker
  
  // Keep handler refs in sync after definitions
  useEffect(() => { handleAttackActionRef.current = handleAttackAction; }, [handleAttackAction]);
  useEffect(() => { handleSpellActionRef.current = handleSpellAction; }, [handleSpellAction]);
  useEffect(() => { handleAbilityActionRef.current = handleAbilityAction; }, [handleAbilityAction]);

  // @date_of_change 2025-09-10 - REMOVED UNUSED MULTIATTACK HANDLER
  // This function was not called anywhere and created potential conflicts with main multiattack flow

  // Complete multiattack sequence
  

  // Handle next multiattack component - for auto-executing non-targeting components only
  // @date_of_change 2025-09-09 - CRITICAL FIX: Remove dependency on selectedTargets for auto-execution
  // This function is only used for components that don't require targeting (auto-execution)
  // Components that require targeting are handled through handleConfirmTargets flow
  const handleNextMultiattackComponent = useCallback(async () => {
    console.log('[BattleRoom] Auto-executing next multiattack component (no targeting required)');
    
    if (!multiattackSequence) {
      console.warn('[BattleRoom] Cannot execute multiattack component - no sequence');
      return;
    }

    const { attackerId, componentActions, currentIndex } = multiattackSequence;
    const attacker = tokens.find(t => t.id === attackerId);
    const currentAction = componentActions[currentIndex];

    if (!attacker || !currentAction) {
      console.error('[BattleRoom] Missing attacker or current action for multiattack component');
      return;
    }

    // This should only be used for components that don't require targeting
    if (currentAction.targetingRequired !== false) {
      console.warn('[BattleRoom] Component requires targeting, should use handleConfirmTargets flow instead');
      return;
    }

    console.log(`[BattleRoom] Auto-executing multiattack component ${currentIndex + 1}/${componentActions.length}: ${currentAction.name}`);
    
    try {
      // Execute action without targeting (e.g. self-buffs, area effects that don't need specific targets)
      if (handleAttackActionRef.current) {
        await handleAttackActionRef.current(
          attackerId,
          currentAction,
          [],
          multiattackSequence.gmOverride || null
        );
      }

      // @date_of_change 2025-09-11 - CLEANUP: Remove duplicate logging - ActionEconomyProcessor already logs
      // Component actions are logged by ActionEconomyProcessor, no need to duplicate here

      // @date_of_change 2025-09-10 - Use unified multiattack advancement logic
      const isLastComponent = currentIndex + 1 >= componentActions.length;
      
      if (isLastComponent) {
        console.log('[BattleRoom] 🏁 Multiattack sequence complete (auto-executed)');
        setMultiattackSequence(null);
        setArmedAction(null);
        setSelectedTargets([]);
        setIsInMultiattackMode(false);
      } else {
        // Advance to next component using same logic as main flow
        const nextIndex = currentIndex + 1;
        const nextAction = componentActions[nextIndex];
        
        console.log(`[BattleRoom] ➡️ Auto-advancing multiattack: ${currentIndex + 1}/${componentActions.length} → ${nextIndex + 1}/${componentActions.length}`);
        
        setMultiattackSequence(prev => ({
          ...prev,
          currentIndex: nextIndex
        }));
        
        // Arm next component for targeting (most components need targeting)
        setSelectedTargets([]);
        setArmedAction({
          attackerId: attackerId,
          action: nextAction,
          multiTarget: false,
          requiredTargets: 1,
          gmOverride: multiattackSequence.gmOverride || null
        });
        
        console.log(`[BattleRoom] ✅ Next component armed for targeting: ${nextAction.name}`);
      }

    } catch (error) {
      console.error('[BattleRoom] Error executing multiattack component:', error);
      NotificationManager.error('Failed to execute multiattack component');
    }
  }, [multiattackSequence, tokens]);

  // Handle opening multiattack targeting popup
  const handleOpenMultiattackTargeting = useCallback(() => {
    console.log('[BattleRoom] Open multiattack targeting');
    setMultiattackTargetingPopup(prev => ({ ...prev, visible: true }));
  }, [setMultiattackTargetingPopup]);

  // Log combatants change if required
  const handleCombatantChange = useCallback((combatantData, prevCombatantData) => {
    if (wsSessionId) return;
    // @date_of_change 2025-09-19 - Enhanced to handle turn-based movement tracking
    // @date_of_change 2025-10-03 - Only track turns/movement when combat is actually active
    // Parameter structure: (newCombatant, prevCombatant) from InitiativeTracker

    // Use imported movement tracker for turn management

    if (typeof combatantData === 'object' && combatantData !== null && combatantData.id) {
      console.log('Combatant turn started:', combatantData.name, 'Previous:', prevCombatantData?.name);

      // @date_of_change: 2025-10-03 - DO NOT auto-start combat when tokens are selected
      // Combat should only start when GM explicitly rolls initiative
      // This allows GM to freely position tokens during setup phase without triggering action economy

      // @date_of_change: 2025-10-03 - Only track movement/actions when combat is actually active
      // @date_of_change: 2025-10-04 - Track turns ONLY when combat has been confirmed via CombatLogManager
      // During setup phase (before initiative confirmed), GM should be able to freely position tokens
      const combatActuallyStarted = CombatLogManager.combatStarted;
      const turnRound = Number(CombatLogManager.currentRound) || 1;
      const turnKey = `${turnRound}:${combatantData.id}`;
      const now = Date.now();
      const lastTurn = lastProcessedTurnRef.current || {};
      const duplicateTurnNotification = lastTurn.key === turnKey && (now - Number(lastTurn.at || 0)) < 1000;

      if (combatActuallyStarted && duplicateTurnNotification) {
        console.log('[BattleRoom] Duplicate turn notification ignored:', {
          combatant: combatantData.name,
          turnKey
        });
      }

      if (combatActuallyStarted && !duplicateTurnNotification) {
        // Initiative owns turn starts; duplicate React/WebSocket notifications must not refund
        // movement, actions, or ongoing-effect durations. date_of_change: 2026-06-23
        lastProcessedTurnRef.current = { key: turnKey, at: now };
        // End the previous combatant's turn in movement tracker
        if (prevCombatantData?.id) {
          movementTracker.endTurn(prevCombatantData.id);
          const previousToken = selectCanonicalCombatantToken(tokens, prevCombatantData);
          if (previousToken?.combatFlags?.steadyAimActive || previousToken?.combatFlags?.movementLockedThisTurn) {
            handleTokenUpdate(previousToken.id, {
              combatFlags: {
                ...(previousToken.combatFlags || {}),
                steadyAimActive: false,
                movementLockedThisTurn: false,
                movementLockReason: null,
                bonusActionUsed: false
              },
              _engineUpdate: true
            });
          }
          console.log(`[TurnMovementTracker] Ended turn for ${prevCombatantData.name}`);
        }

        // Start the new combatant's turn with movement allocation
        const currentToken = selectCanonicalCombatantToken(tokens, combatantData);
        if (currentToken && (currentToken.type === 'character' || currentToken.type === 'monster')) {
          // The token is the persisted turn snapshot. Clear every turn-scoped
          // field together at the beginning of its own turn; initiative carries
          // only ordering/identity and must never retain a competing mechanics
          // copy. Durable flags such as Hidden observer state are preserved.
          // date_of_change: 2026-07-15
          // A missing previous combatant means initialization/reconnect, not a
          // proven turn boundary. In that case retain the authoritative token
          // snapshot (including Steady Aim) instead of refunding the turn.
          const startingCombatFlags = prevCombatantData?.id
            ? resetTurnScopedCombatFlags(currentToken.combatFlags)
            : (currentToken.combatFlags || {});
          if (prevCombatantData?.id) {
            handleTokenUpdate(currentToken.id, {
              combatFlags: startingCombatFlags,
              _engineUpdate: true
            });
          }

          // Prepare token data for movement tracker
          // @date_of_change: 2025-09-21 - Convert to grid coordinates for consistent movement calculations
          // @date_of_change: 2025-11-04 - Normalize speed to canonical 5e walk speed when available (AGENTS.md: single source of truth)
          const resolveSpeed = () => {
            try {
              if (typeof currentToken.speed === 'number' && Number.isFinite(currentToken.speed)) return currentToken.speed;
              if (currentToken.speed && typeof currentToken.speed === 'object' && Number.isFinite(currentToken.speed.walk)) return currentToken.speed.walk;
              const cd = currentToken.characterData || currentToken.canonicalData || {};
              if (typeof cd.speed === 'number' && Number.isFinite(cd.speed)) return cd.speed;
              if (cd.speed && typeof cd.speed === 'object' && Number.isFinite(cd.speed.walk)) return cd.speed.walk;
              const md = currentToken.monsterData || {};
              if (typeof md.speed === 'number' && Number.isFinite(md.speed)) return md.speed;
              if (md.speed && typeof md.speed === 'object' && Number.isFinite(md.speed.walk)) return md.speed.walk;
            } catch (_) {}
            return 30; // conservative default per PHB
          };
          const normalizedSpeed = resolveSpeed();

          // If token carries a stale numeric speed, update it so UI/ActionEconomy agree (no silent fallbacks)
          try {
            if (typeof currentToken.speed !== 'number' || currentToken.speed !== normalizedSpeed) {
              handleTokenUpdate(currentToken.id, { speed: normalizedSpeed });
            }
          } catch (_) {}

          // Movement validation and Token.js both resolve nested `grid` before
          // legacy pixels. Starting a turn from x/y could therefore make a
          // player spend movement from a square other than the one they see.
          // date_of_change: 2026-07-26
          const canonicalGridCell = resolveTokenGridCell(currentToken, gridSize);
          const tokenData = {
            id: currentToken.id,
            name: currentToken.name,
            x: canonicalGridCell?.x ?? Math.round((currentToken.x || 0) / gridSize),
            y: canonicalGridCell?.y ?? Math.round((currentToken.y || 0) / gridSize),
            speed: normalizedSpeed,
            character: currentToken.characterData,
            monster: currentToken.monsterData,
            conditions: currentToken.conditions || [],
            combatFlags: startingCombatFlags,
            size: currentToken.size || 1
          };

          movementTracker.startTurn(combatantData.id, tokenData);

          const movementSummary = movementTracker.getMovementSummary(combatantData.id);
          console.log(`[TurnMovementTracker] Started turn for ${combatantData.name}:`, movementSummary);
        }

        // Log turn start in combat log
        CombatLogManager.startTurn(combatantData, CombatLogManager.currentTurn, tokens);
        // Initialize action economy at real turn start (no resets on selection)
        // @date_of_change: 2025-10-07 - Start action economy only when combat is active and turn actually changes
        ActionEconomyManager.startTurn(combatantData.id, CombatLogManager.currentTurn || 0);

        // @date_of_change: 2025-10-25 - Ongoing Effects: decrement durations on the active combatant's turn
        try {
          const activeToken = selectCanonicalCombatantToken(tokens, combatantData);
          if (activeToken && activeToken.statusEffectsMeta) {
            const meta = { ...(activeToken.statusEffectsMeta || {}) };
            const se = { ...(activeToken.statusEffects || {}) };
            let nextConditions = Array.isArray(activeToken.conditions) ? [...activeToken.conditions] : [];
            let changed = false;
            const expired = [];
            Object.keys(meta).forEach(key => {
              const entry = meta[key];
              if (!entry) return;
              const remaining = Number(entry.roundsRemaining);
              if (Number.isFinite(remaining)) {
                const next = Math.max(0, remaining - 1);
                meta[key] = { ...entry, roundsRemaining: next };
                if (next === 0) {
                  // Expire effect
                  delete meta[key];
                  if (se[key]) delete se[key];
                  if (entry.condition) {
                    const expiredCondition = String(entry.condition).trim().toLowerCase();
                    nextConditions = nextConditions.filter((condition) => (
                      String(condition?.name ?? condition).trim().toLowerCase() !== expiredCondition
                    ));
                  }
                  expired.push({ key, sourceId: entry.sourceId });
                  changed = true;
                } else {
                  changed = true;
                }
              }
            });
            if (changed) {
              handleTokenUpdate(activeToken.id, {
                statusEffects: se,
                statusEffectsMeta: meta,
                conditions: nextConditions
              });
              // Log expirations and clear concentrating ring if last effect from a caster ended
              expired.forEach(({ key, sourceId }) => {
                try {
                  CombatLogManager.logMessage(`${key.replace(/_/g, ' ')} expired on ${activeToken.name}`, { combatantId: activeToken.id });
                  if (sourceId) {
                    // Check if any token still has an effect from this sourceId
                    const anyRemaining = tokens.some(tok => {
                      const m = tok.statusEffectsMeta || {};
                      return Object.values(m).some(e => e && e.sourceId === sourceId);
                    });
                    if (!anyRemaining) {
                      const caster = tokens.find(t => t.id === sourceId);
                      if (caster && caster.statusEffects && caster.statusEffects.concentrating) {
                        if (wsSessionId) {
                          sendBattleRoomMutation('concentration:end', {
                            tokenId: caster.id,
                            combatantId: caster.combatantId || caster.characterId || caster.monsterId || caster.id,
                            reason: 'All linked effects expired',
                          }).catch((cause) => NotificationManager.error('Concentration not ended', cause?.message || 'The server could not end concentration.'));
                        } else {
                          const casterPrev = { ...caster.statusEffects };
                          delete casterPrev.concentrating;
                          handleTokenUpdate(caster.id, { statusEffects: casterPrev });
                          CombatLogManager.logMessage(`Concentration ended for ${caster.name}`, { combatantId: caster.id });
                        }
                      }
                    }
                  }
                } catch (_) {}
              });
            }
          }
        } catch (e) {
          console.warn('[BattleRoom] Ongoing effects tick failed:', e?.message || e);
        }
      } else if (!combatActuallyStarted) {
        console.log('[BattleRoom] Combat not started - skipping turn tracking for:', combatantData.name);
      }

      // Store only the canonical token identity. Mechanics, sheet data, and turn
      // flags are always read from the current token collection.
      const fullToken = selectCanonicalCombatantToken(tokens, combatantData);
      setCurrentCombatant(fullToken?.id || combatantData.id);

    } else {
      // Handle legacy case where combatantData might be just an ID
      console.log('Combatant changed (legacy):', combatantData, prevCombatantData);
    }
  }, [handleTokenUpdate, tokens, setCurrentCombatant, gridSize, sendBattleRoomMutation, wsSessionId]);

  // Local rooms save the same roll and total that the tracker displays.
  // date_of_change: 2026-11-04
  const applyLocalInitiative = useCallback((combatants) => {
    const byId = new Map(combatants.map((actor) => [actor.id, actor]));
    setTokens((current) => current.map((token) => {
      const actor = byId.get(token.id);
      if (!actor) return token;
      return {
        ...token,
        initiative: actor.initiative,
        initiativeModifier: actor.initiativeRoll?.modifier,
        initiativeRoll: actor.initiativeRoll
      };
    }));
  }, [setTokens]);

  // Check if we need to deselect tokens (click on empty stage area)
  const checkDeselect = useCallback((e) => {
    // deselect when clicking on empty area
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedTokenId(null);
      // @date_of_change: 2025-10-08 - Also clear multi-selection when clicking empty stage
      setSelectedTokenIds([]);
      // @date_of_change 2025-09-18 - FIX: Also deselect map when clicking on empty area
      setMapState(prev => ({ ...prev, isSelected: false }));
    }
  }, [setSelectedTokenId, setSelectedTokenIds, setMapState]);
  
  // @date_of_change 2025-09-16 - Enhanced grid targeting using shared utility
  // Helper function to convert mouse position to grid coordinates with intersection support
  const getGridPositionFromMouse = useCallback((e) => {
    const stage = e?.target?.getStage?.() || stageRef?.current;
    if (!stage) {
      return null;
    }

    const nativeEvent = e?.evt || e?.nativeEvent;
    if (nativeEvent && typeof stage.setPointersPositions === 'function') {
      stage.setPointersPositions(nativeEvent);
    }

    const pointerPosition = stage.getPointerPosition();
    const stagePoint = getStageLocalPointer();
    const gridTarget = getGridPositionFromStagePoint(stagePoint);

    if (!pointerPosition || !stagePoint || !gridTarget) {
      return null;
    }

    const mapRelativeX = stagePoint.x - mapState.position.x;
    const mapRelativeY = stagePoint.y - mapState.position.y;

    console.log(`[BattleRoom] 🎯 Grid targeting: mouse(${pointerPosition.x}, ${pointerPosition.y}) stage(${stagePoint.x}, ${stagePoint.y}) map-offset(${mapState.position.x}, ${mapState.position.y}) relative(${mapRelativeX}, ${mapRelativeY}) → grid(${gridTarget.x}, ${gridTarget.y}) mode:${gridTarget.targetMode}`);

    return gridTarget;
  }, [getGridPositionFromStagePoint, getStageLocalPointer, mapState.position.x, mapState.position.y, stageRef]);

  // @date_of_change 2025-09-15 - AOE TARGETING SYSTEM
  // Clear AoE targeting state
  const clearAoETargeting = useCallback(() => {
    setAoeTargeting({
      enabled: false,
      targetPoint: null,
      areaOfEffect: null,
      direction: 0,
      targetMode: 'center',
      previewPoint: null,
      spell: null,
      caster: null,
      gmOverride: null
    });
  }, []);

  // Handle AoE targeting click
  const handleAoETargetClick = useCallback(async (gridPos) => {
    if (!aoeTargeting.enabled || !aoeTargeting.areaOfEffect || !aoeTargeting.spell || !aoeTargeting.caster) {
      return false; // Not handled
    }

    const normalizedAreaOfEffect = normalizeAreaOfEffect(aoeTargeting.areaOfEffect);
    if (!normalizedAreaOfEffect) {
      console.warn('[BattleRoom] Invalid AoE targeting payload:', aoeTargeting.areaOfEffect);
      clearAoETargeting();
      return false;
    }

    console.log('[BattleRoom] 🎯 AoE target selected at grid position:', gridPos);

  // Update the target point for visual feedback
  setAoeTargeting(prev => ({
    ...prev,
    targetPoint: gridPos
  }));

  // @date_of_change 2025-09-16 - Use same cone calculation logic as visual overlay
  let affectedCells = [];
  

  const shape = String(normalizedAreaOfEffect.shape || '').toLowerCase();
  // @date_of_change 2025-10-13 - Capture final directional heading to pass into engine
  let finalDirection = Number.isFinite(aoeTargeting.direction) ? aoeTargeting.direction : 0;

  const casterToken = aoeTargeting.caster;
  let casterCenterX = 0;
  let casterCenterY = 0;
  if (casterToken) {
    let tokenWidth = casterToken.width || gridSize;
    let tokenHeight = casterToken.height || gridSize;
    if (casterToken.sizeCells && Number.isFinite(casterToken.sizeCells.w) && Number.isFinite(casterToken.sizeCells.h)) {
      tokenWidth = Number(casterToken.sizeCells.w) * gridSize;
      tokenHeight = Number(casterToken.sizeCells.h) * gridSize;
    }
    casterCenterX = casterToken.x + (tokenWidth / 2);
    casterCenterY = casterToken.y + (tokenHeight / 2);
  }

  if (shape === 'cone' && casterToken) {
    const targetX = gridPos.x * gridSize + (gridPos.targetMode === 'center' ? gridSize / 2 : 0);
    const targetY = gridPos.y * gridSize + (gridPos.targetMode === 'center' ? gridSize / 2 : 0);
    const calculatedDirection = Math.atan2(targetY - casterCenterY, targetX - casterCenterX) * 180 / Math.PI;
    finalDirection = calculatedDirection;

    const originData = RangeCalculator.getDirectionalAoEOrigin(casterToken, calculatedDirection, { gridSize });
    affectedCells = RangeCalculator.calculateConeAreaCells(
      originData.edge.grid,
      calculatedDirection,
      normalizedAreaOfEffect.size,
      { feetPerCell }
    );
  } else if (shape === 'line' && casterToken) {
    const targetX = gridPos.x * gridSize + (gridPos.targetMode === 'center' ? gridSize / 2 : 0);
    const targetY = gridPos.y * gridSize + (gridPos.targetMode === 'center' ? gridSize / 2 : 0);

    let directionDeg = Number.isFinite(aoeTargeting.direction)
      ? aoeTargeting.direction
      : Math.atan2(targetY - casterCenterY, targetX - casterCenterX) * 180 / Math.PI;

    if (!Number.isFinite(directionDeg)) {
      directionDeg = 0;
    }
    finalDirection = directionDeg;

    const lineLengthFeet = Number(normalizedAreaOfEffect.length ?? normalizedAreaOfEffect.size ?? 0);
    const lineWidthFeet = Number(normalizedAreaOfEffect.width ?? normalizedAreaOfEffect.thickness ?? 5);

    const originData = RangeCalculator.getDirectionalAoEOrigin(casterToken, directionDeg, { gridSize });

    affectedCells = RangeCalculator.calculateLineAreaCells(
      originData.edge.grid,
      directionDeg,
      lineLengthFeet,
      lineWidthFeet,
      { feetPerCell }
    );

    console.log(`[BattleRoom] ⚡ Line AoE calculated with direction ${directionDeg.toFixed(1)}°, length ${lineLengthFeet}ft, width ${lineWidthFeet}ft`);
  } else {
    // Fallback to RangeCalculator for non-cone AoE
    affectedCells = RangeCalculator.calculateAreaOfEffect(
      gridPos,
      normalizedAreaOfEffect,
      gridType, // grid type
      feetPerCell,
      aoeTargeting.targetMode // corner vs center targeting
    );
  }

  // Find all tokens within the affected area using footprint cells
  const areaSet = new Set(affectedCells.map(c => `${c.x},${c.y}`));
  const affectedTokenIds = [];
  tokens.forEach(token => {
    const cells = RangeCalculator.getTokenCells(token, { gridSize, feetPerCell });
    const inArea = cells.some(c => areaSet.has(`${c.x},${c.y}`));
    if (inArea) {
      affectedTokenIds.push(token.id);
    }
  });

  console.log(`[BattleRoom] 🔮 AoE ${aoeTargeting.spell.name} affects ${affectedTokenIds.length} tokens:`, affectedTokenIds);

  try {
    let actionResolved = false;
    // @date_of_change 2025-09-16 - Support both spells and non-spell area actions
    // Execute the action with the affected targets - route to appropriate handler
    if (aoeTargeting.spell.type === 'spell') {
      console.log(`[BattleRoom] 🔮 Executing AoE spell: ${aoeTargeting.spell.name}`);
      // date_of_change: 2025-09-18 - Pass grid targetPoint so engine centers AoE correctly
      const spellWithTargetPoint = {
        ...aoeTargeting.spell,
        // @date_of_change: 2025-10-14 - Pass AoE definition to engine for directional handling
        areaOfEffect: normalizedAreaOfEffect,
        options: {
          ...(aoeTargeting.spell.options || {}),
          // @date_of_change 2025-10-13 - Pass full template context to engine (no legacy fallbacks)
          targetPoint: { x: gridPos.x, y: gridPos.y },
          direction: finalDirection,
          targetMode: gridPos.targetMode,
          gridSize,
          // @date_of_change: 2025-10-13 - Use hook grid context
          feetPerCell: feetPerCell,
          gridType: gridType
        }
      };
      if (handleSpellActionRef.current) {
        actionResolved = await handleSpellActionRef.current(
          aoeTargeting.caster,
          spellWithTargetPoint,
          affectedTokenIds,
          aoeTargeting.gmOverride || null
        );
      }
    } else {
      console.log(`[BattleRoom] 🐉 Executing AoE attack: ${aoeTargeting.spell.name}`);
      if (handleAttackActionRef.current) {
        actionResolved = await handleAttackActionRef.current(
          aoeTargeting.caster.id,
          {
            ...aoeTargeting.spell,
            areaOfEffect: normalizedAreaOfEffect
          },
          affectedTokenIds,
          aoeTargeting.gmOverride || null
        );
      }
    }

    if (!actionResolved) {
      clearAoETargeting();
      return false;
    }

    const actionType = aoeTargeting.spell.type === 'spell' ? 'Spell' : 'Attack';
    NotificationManager.success(
      `${aoeTargeting.spell.name} ${actionType === 'Spell' ? 'Cast' : 'Used'}!`,
      `Affected ${affectedTokenIds.length} targets at grid position (${gridPos.x}, ${gridPos.y})`
    );

    // @date_of_change 2025-09-16 - FIX: Clear AoE targeting after attack completes
    // Wait for the attack to complete before clearing AoE state
    setTimeout(() => {
      clearAoETargeting();
    }, 100);

  } catch (error) {
    console.error('[BattleRoom] ❌ Failed to execute AoE action:', error);
    const actionType = aoeTargeting.spell.type === 'spell' ? 'spell' : 'attack';
    NotificationManager.error(
      `${actionType === 'spell' ? 'Spell' : 'Attack'} Failed`,
      `Failed to use ${aoeTargeting.spell.name}: ${error.message}`
    );
  }

  return true; // Handled
}, [aoeTargeting, tokens, gridSize, feetPerCell, gridType, clearAoETargeting]);

// Handle stage mouse down event
const handleStageMouseDown = useCallback((e) => {
  if (Date.now() < mapAttentionSuppressUntilRef.current) {
    e?.evt?.preventDefault?.();
    return;
  }

  if (sharedPingActive && sharedPingAvailable) {
    if (e?.evt?.button !== 0) return;

    const local = getStageLocalPointer();
    const x = Number(local?.x) - Number(mapState.position?.x || 0);
    const y = Number(local?.y) - Number(mapState.position?.y || 0);
    const width = Number(mapState.size?.width);
    const height = Number(mapState.size?.height);
    if (
      !Number.isFinite(x)
      || !Number.isFinite(y)
      || !Number.isFinite(width)
      || !Number.isFinite(height)
      || x < 0
      || y < 0
      || x > width
      || y > height
    ) {
      return;
    }

    e?.evt?.preventDefault?.();
    sendWebSocketMessage('mapPing', { sessionId: wsSessionId, x, y });
    setSharedPingActive(false);
    return;
  }

  // Measurement uses the same Stage pointer model as selection/fog/AoE so it stays in sync with zoom/pan.
  if (measurementActive && fogEditMode === 'none' && !aoeTargeting.enabled) {
    const isLeftButton = e?.evt?.button === 0;
    if (!isLeftButton) {
      return;
    }

    const gridInfo = getGridPositionFromMouse(e);
    if (!gridInfo || typeof gridInfo.x !== 'number' || typeof gridInfo.y !== 'number') {
      return;
    }

    const nextCell = { x: gridInfo.x, y: gridInfo.y };
    setMeasureStartCell(nextCell);
    setMeasureEndCell(nextCell);
    setIsMeasuring(true);
    return;
  }

  // @date_of_change: 2026-04-08 - Ignore right-click at the Stage layer
  // Custom context menus are opened via token/map onContextMenu handlers.
  // Letting the Stage process button=2 causes marquee/background selection
  // to race with the menu-open path and suppress the custom menu UX.
  try {
    const isRightButton = e?.evt?.button === 2;
    if (isRightButton) {
      e?.evt?.preventDefault?.();
      return;
    }
  } catch (_) {}

  // date_of_change: 2025-10-06 - Middle Mouse Panning
  // Use the middle mouse button to pan the Stage so left-drag can be used for selection/drawing/etc.
  try {
    const isMiddleButton = e?.evt?.button === 1;
    if (isMiddleButton) {
      e.evt.preventDefault();
      panSessionRef.current = {
        clientX: e.evt.clientX,
        clientY: e.evt.clientY
      };
      setIsPanning(true);
      return; // consume
    }
  } catch (_) {}

  // Fog draw tool takes precedence over other interactions
  // date_of_change: 2025-09-24 - Allow painting even when fog is not shown, so GM can prep areas
  // GAP-003: polygon/lasso shapes added — date_of_change: 2026-05-20
  if (isGameMaster && fogEditMode !== 'none') {
    const local = getStageLocalPointer();
    if (!local) return;

    // ── Lasso: begin drag-collection on mouse-down ───────────────────────
    if (fogShape === 'lasso') {
      fogLassoPointsRef.current = [{ x: local.x, y: local.y }];
      fogLassoDragging.current = true;
      setFogPolyPreview({ points: [local.x, local.y], closingPoint: null });
      return;
    }

    // ── Polygon: each click adds a vertex; handled on mousedown (not drag) ─
    if (fogShape === 'polygon') {
      fogPolygonPointsRef.current = [...fogPolygonPointsRef.current, { x: local.x, y: local.y }];
      const pts = fogPolygonPointsRef.current;
      const flat = pts.flatMap(p => [p.x, p.y]);
      setFogPolyPreview({ points: flat, closingPoint: pts.length >= 2 ? { x: pts[0].x, y: pts[0].y } : null });
      return;
    }

    // ── Rect (default): begin drag ───────────────────────────────────────
    fogDragRef.current = { start: { x: local.x, y: local.y }, current: { x: local.x, y: local.y } };

    // date_of_change: 2025-09-25 - Initialize drag preview
    setFogDragPreview({
      x: local.x,
      y: local.y,
      width: 0,
      height: 0
    });

    return; // Consume
  }

  // (moved) Marquee selection rectangle updates now handled in onMouseMove

  // @date_of_change 2025-09-16 - AOE TARGETING SYSTEM with intersection support
  // Handle AoE targeting clicks before other interactions
  if (aoeTargeting.enabled) {
    const gridInfo = getGridPositionFromMouse(e);

    // Update AoE targeting with detected target mode (intersection vs center)
    setAoeTargeting(prev => ({
      ...prev,
      targetMode: gridInfo.targetMode
    }));

    handleAoETargetClick(gridInfo);
    // @date_of_change 2025-09-16 - GRID TARGETING FIX
    // Prevent token deselection during AoE targeting, regardless of handler result
    // This ensures that clicking on grid during AoE targeting shows overlay instead of deselecting token
    return; // Always prevent other mouse events when AoE targeting is enabled
  }

  // @date_of_change: 2025-10-08 - Begin marquee selection on empty Stage OR map background left-drag (with Shift override when map is interactive)
  try {
    const isLeftButton = e?.evt?.button === 0;
    const stage = e.target?.getStage?.();
    const target = e.target;
    const clickedOnStageBackground = target === stage;
    const targetId = typeof target?.id === 'function' ? target.id() : undefined;
    const clickedOnMapBg = targetId === 'map-background' || targetId === 'token-layer-map' || targetId === 'gamemaster-layer-map';
    // Identify which layer the background image belongs to
    const bgLayer = targetId === 'map-background' ? 'map' : (targetId === 'token-layer-map' ? 'token' : (targetId === 'gamemaster-layer-map' ? 'gamemaster' : null));
    const isBgLayerInteractive = bgLayer ? (activeLayer === bgLayer && !(layers?.[bgLayer]?.locked)) : false;
    const shiftKey = !!(e?.evt?.shiftKey);
    // Allow marquee selection on map background if:
    // - the map background is non-interactive (locked or not the active layer), or
    // - Shift is held to explicitly request selection over map manipulation
    const canStartSelectionOnMap = clickedOnMapBg && (!isBgLayerInteractive || shiftKey);
    const canStartSelection = isLeftButton && (clickedOnStageBackground || canStartSelectionOnMap) && (fogEditMode === 'none');
    if (canStartSelection) {
      const startPt = getStageLocalPointer();
      if (startPt) {
        selectionDragRef.current = {
          start: { x: startPt.x, y: startPt.y },
          current: { x: startPt.x, y: startPt.y },
          shiftKey: shiftKey
        };
        setIsSelecting(true);
        setSelectionRect({ x: startPt.x, y: startPt.y, width: 0, height: 0 });
        console.log('[BattleRoom] [SELECTION] Marquee start @', startPt, 'shift=', !!(e?.evt?.shiftKey), 'reason=', clickedOnStageBackground ? 'stage' : 'map-bg'); // @date_of_change: 2025-10-08
        return; // Consume to avoid deselect
      }
    }
  } catch (_) {}

  checkDeselect(e);
// date_of_change: 2025-09-24 - Added fog tool dependencies
// GAP-003: Added fogShape dep — date_of_change: 2026-05-20
}, [sharedPingActive, sharedPingAvailable, getStageLocalPointer, mapState.position, mapState.size, sendWebSocketMessage, wsSessionId, measurementActive, aoeTargeting.enabled, getGridPositionFromMouse, handleAoETargetClick, checkDeselect, isGameMaster, fogEditMode, fogShape, activeLayer, layers, setIsMeasuring, setIsSelecting, setMeasureEndCell, setMeasureStartCell, setSelectionRect]);


  // Handle stage mouse move event
const handleStageMouseMove = useCallback((e) => {
    if (Date.now() < mapAttentionSuppressUntilRef.current) {
      return;
    }

    if (measurementActive && isMeasuring && fogEditMode === 'none' && !aoeTargeting.enabled) {
      const gridInfo = getGridPositionFromMouse(e);
      if (gridInfo && typeof gridInfo.x === 'number' && typeof gridInfo.y === 'number') {
        setMeasureEndCell({ x: gridInfo.x, y: gridInfo.y });
      }
      return;
    }

    if (isPanning) {
      return;
    }

    // @date_of_change: 2025-10-08 - Update marquee selection rectangle while dragging
    if (selectionDragRef.current && isSelecting) {
      try {
        const cur = getStageLocalPointer();
        if (cur) {
          selectionDragRef.current.current = { x: cur.x, y: cur.y };
          const { start } = selectionDragRef.current;
          const x = Math.min(start.x, cur.x);
          const y = Math.min(start.y, cur.y);
          const width = Math.abs(cur.x - start.x);
          const height = Math.abs(cur.y - start.y);
          setSelectionRect({ x, y, width, height });
          if ((width % 5 === 0) || (height % 5 === 0)) {
            // light throttle to avoid log spam
            console.log('[BattleRoom] [SELECTION] Marquee update', { x, y, width, height });
          }
        }
      } catch (_) {}
      return; // Consume while selecting to avoid other handlers
    }

    // GAP-003: Lasso — collect points while dragging — date_of_change: 2026-05-20
    if (fogLassoDragging.current && fogEditMode !== 'none' && fogShape === 'lasso') {
      const local = getStageLocalPointer();
      if (!local) return;
      fogLassoPointsRef.current.push({ x: local.x, y: local.y });
      const flat = fogLassoPointsRef.current.flatMap(p => [p.x, p.y]);
      setFogPolyPreview({ points: flat, closingPoint: null });
      return;
    }

    // GAP-003: Polygon — update closing line toward cursor — date_of_change: 2026-05-20
    if (fogShape === 'polygon' && fogEditMode !== 'none' && fogPolygonPointsRef.current.length > 0) {
      const local = getStageLocalPointer();
      if (!local) return;
      const pts = fogPolygonPointsRef.current;
      const flat = pts.flatMap(p => [p.x, p.y]);
      flat.push(local.x, local.y);
      setFogPolyPreview({ points: flat, closingPoint: pts.length >= 1 ? { x: pts[0].x, y: pts[0].y } : null });
      return;
    }

    // Update fog draw drag rectangle (rect shape — default)
    if (fogDragRef.current && fogEditMode !== 'none') {
      const local = getStageLocalPointer();
      if (!local) return;
      fogDragRef.current.current = { x: local.x, y: local.y };

      // date_of_change: 2025-09-25 - Update drag preview visual feedback
      const { start } = fogDragRef.current;
      const x = Math.min(start.x, local.x);
      const y = Math.min(start.y, local.y);
      const width = Math.abs(local.x - start.x);
      const height = Math.abs(local.y - start.y);

      setFogDragPreview({
        x,
        y,
        width,
        height
      });

      return;
    }

    // @date_of_change 2025-09-16 - AOE TARGETING SYSTEM with intersection support
    // Update AoE preview while mouse moves during targeting
    if (aoeTargeting.enabled && aoeTargeting.areaOfEffect && aoeTargeting.caster) {
      const gridInfo = getGridPositionFromMouse(e);

      // @date_of_change 2025-09-16 - DIRECTIONAL AOE CONTROL with precise cursor positioning
      // Calculate direction for cones and lines based on mouse position relative to caster
      let calculatedDirection = aoeTargeting.direction || 0;
      const isDirectionalAoE = aoeTargeting.areaOfEffect.shape.toLowerCase() === 'cone' ||
                               aoeTargeting.areaOfEffect.shape.toLowerCase() === 'line';

      if (isDirectionalAoE) {
        // @date_of_change 2025-09-16 - CRITICAL FIX: Use exact mouse coordinates for precise cone positioning
        // Get exact mouse position instead of snapped grid coordinates
        const stage = e.target.getStage();
        const pointerPosition = stage.getPointerPosition();

        // @date_of_change 2025-09-18 - FIX: Account for map offset in directional AoE calculations
        // Convert mouse position to map-relative coordinates
        const mapRelativeMouseX = pointerPosition.x - mapState.position.x;
        const mapRelativeMouseY = pointerPosition.y - mapState.position.y;

        // Calculate direction from caster token center to exact mouse position (in degrees)
        const casterCenterX = aoeTargeting.caster.x + (gridSize / 2);
        const casterCenterY = aoeTargeting.caster.y + (gridSize / 2);
        const deltaX = mapRelativeMouseX - casterCenterX;
        const deltaY = mapRelativeMouseY - casterCenterY;
        calculatedDirection = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      }

      // Only update if position, target mode, or direction changed
      const directionChanged = Math.abs(calculatedDirection - (aoeTargeting.direction || 0)) > 1; // 1 degree threshold
      if (!aoeTargeting.previewPoint ||
          aoeTargeting.previewPoint.x !== gridInfo.x ||
          aoeTargeting.previewPoint.y !== gridInfo.y ||
          aoeTargeting.targetMode !== gridInfo.targetMode ||
          directionChanged) {

        setAoeTargeting(prev => ({
          ...prev,
          previewPoint: gridInfo,
          targetMode: gridInfo.targetMode,
          direction: calculatedDirection
        }));
      }
    }
  // date_of_change: 2025-09-24 - Added fog tool dependencies
  // GAP-003: Added fogShape dep — date_of_change: 2026-05-20
  }, [aoeTargeting.enabled, aoeTargeting.areaOfEffect, aoeTargeting.previewPoint, aoeTargeting.targetMode, aoeTargeting.direction, aoeTargeting.caster, getGridPositionFromMouse, gridSize, fogEditMode, fogShape, isMeasuring, isPanning, isSelecting, measurementActive, getStageLocalPointer, setMeasureEndCell, setSelectionRect, mapState.position.x, mapState.position.y]);

  // Handle stage mouse up event
  const handleStageMouseUp = useCallback((e) => {
    if (Date.now() < mapAttentionSuppressUntilRef.current) {
      e?.evt?.preventDefault?.();
      selectionDragRef.current = null;
      setIsSelecting(false);
      setSelectionRect(null);
      return;
    }

    if (measurementActive && fogEditMode === 'none' && !aoeTargeting.enabled) {
      if (isMeasuring) {
        const gridInfo = getGridPositionFromMouse(e);
        if (gridInfo && typeof gridInfo.x === 'number' && typeof gridInfo.y === 'number') {
          setMeasureEndCell({ x: gridInfo.x, y: gridInfo.y });
        }
      }
      setIsMeasuring(false);
      return;
    }

    // GAP-003: Lasso — commit on mouse-up — date_of_change: 2026-05-20
    if (fogLassoDragging.current && fogEditMode !== 'none' && fogShape === 'lasso') {
      fogLassoDragging.current = false;
      const rawPts = fogLassoPointsRef.current;
      fogLassoPointsRef.current = [];
      setFogPolyPreview(null);

      if (rawPts.length >= 3) {
        const type = fogEditMode === 'reveal' ? 'reveal' : 'cover';
        const normPts = rawPts.map(p => toNormalizedFogPoint(p)).filter(Boolean);
        if (normPts.length >= 3) {
          addFogOperationPolygon(type, 'lasso', normPts);
        }
      }
      return;
    }

    if (fogDragRef.current && fogEditMode !== 'none') {
      const { start, current } = fogDragRef.current;
      const rectPct = toNormalizedFogRect(start, current);
      fogDragRef.current = null;

      // date_of_change: 2025-09-25 - Clear drag preview
      setFogDragPreview(null);

      if (rectPct) {
        const type = fogEditMode === 'reveal' ? 'reveal' : 'cover';
        console.log('[BattleRoom] Adding fog operation:', { type, rectPct }); // @date_of_change: 2025-09-30 - Debug fog operations
        addFogOperationRectNormalized(type, rectPct);
        // Keep fog tool active; user will toggle it off explicitly
      }
      return;
    }

    // date_of_change: 2026-04-07 - Stop manual middle-mouse panning without re-triggering Stage drag internals
    if (isPanning) {
      try {
        e?.evt?.preventDefault?.();
      } catch (_) {}
      panSessionRef.current = null;
      setIsPanning(false);
      return;
    }

    // @date_of_change: 2025-10-08 - Finalize marquee selection and select tokens within rect
    if (selectionDragRef.current && isSelecting) {
      try {
        const { start, current, shiftKey } = selectionDragRef.current;
        const minX = Math.min(start.x, current.x);
        const minY = Math.min(start.y, current.y);
        const maxX = Math.max(start.x, current.x);
        const maxY = Math.max(start.y, current.y);
        const w = Math.abs(current.x - start.x);
        const h = Math.abs(current.y - start.y);

        // Treat a tiny box as a click (no-op here; single-click is handled elsewhere)
        const isTiny = w < 3 && h < 3;

        if (!isTiny) {
          let newlySelected = getTokensForBattleLayer(tokens, normalizedActiveLayer, isGameMaster)
            .filter(t => {
              const tx1 = t.x;
              const ty1 = t.y;
              const tx2 = (t.x || 0) + (t.width || 0);
              const ty2 = (t.y || 0) + (t.height || 0);
              // AABB overlap test with inclusive edges
              return !(tx2 < minX || ty2 < minY || tx1 > maxX || ty1 > maxY);
            })
            .map(t => t.id);

          // @date_of_change: 2025-10-09 - During combat, players may select the active combatant OR tokens they control
          if (CombatLogManager.combatStarted && !isGameMaster) {
            const activeId = currentCombatant?.id;
            const ownsFn = (id) => (typeof canControlToken === 'function' ? !!canControlToken(id) : false);
            newlySelected = newlySelected.filter(id => id === activeId || ownsFn(id));
          }

          // Merge with existing selection if Shift was held
          let nextIds;
          if (shiftKey) {
            const set = new Set(selectedTokenIds);
            for (const id of newlySelected) set.add(id);
            nextIds = Array.from(set);
          } else {
            nextIds = newlySelected;
          }

          setSelectedTokenIds(nextIds);
          // Maintain backward-compat single selection for panels
          setSelectedTokenId(nextIds[0] || null);
          console.log('[BattleRoom] [SELECTION] Marquee end → selected', nextIds.length, 'ids', nextIds); // @date_of_change: 2025-10-08
        }
      } catch (_) {
        // swallow selection errors
      } finally {
        setIsSelecting(false);
        setSelectionRect(null);
        selectionDragRef.current = null;
      }
    }
  }, [measurementActive, fogEditMode, fogShape, aoeTargeting.enabled, isMeasuring, getGridPositionFromMouse, toNormalizedFogRect, toNormalizedFogPoint, addFogOperationRectNormalized, addFogOperationPolygon, isPanning, isSelecting, selectedTokenIds, normalizedActiveLayer, canControlToken, currentCombatant?.id, isGameMaster, setIsMeasuring, setMeasureEndCell, setIsSelecting, setSelectedTokenId, setSelectedTokenIds, setSelectionRect, tokens]); // @date_of_change: 2025-10-06 - Added panning deps; 2025-10-08 - Added selection deps; GAP-003: polygon/lasso deps 2026-05-20

  // GAP-011: track cursor position in stage-local coords for StatusBar
  // date_of_change: 2026-05-20
  const handleStageMouseMoveForStatusBar = useCallback((e) => {
    try {
      const stage = e?.target?.getStage?.();
      if (!stage) return;
      const ptr = stage.getPointerPosition();
      if (!ptr) return;
      // Convert from stage screen coords → map coords (undo scale + offset)
      const scale = clampStageZoom(zoom || 1);
      const mapX = (ptr.x - stagePosition.x) / scale;
      const mapY = (ptr.y - stagePosition.y) / scale;
      setMousePosition({ x: Math.round(mapX), y: Math.round(mapY) });
    } catch (_) {}
  }, [zoom, stagePosition.x, stagePosition.y]);

  /**
   * GAP-003: Double-click on stage closes and commits the in-progress polygon.
   * Rule reference: GAP-003 spec — "Polygon tool: click-to-place vertices, double-click to close."
   * date_of_change: 2026-05-20
   */
  const handleStageDblClick = useCallback(() => {
    if (!isGameMaster || fogEditMode === 'none' || fogShape !== 'polygon') return;
    const pts = fogPolygonPointsRef.current;
    fogPolygonPointsRef.current = [];
    setFogPolyPreview(null);
    if (pts.length < 3) return;
    const type = fogEditMode === 'reveal' ? 'reveal' : 'cover';
    const normPts = pts.map(p => toNormalizedFogPoint(p)).filter(Boolean);
    if (normPts.length >= 3) {
      addFogOperationPolygon(type, 'polygon', normPts);
    }
  }, [isGameMaster, fogEditMode, fogShape, toNormalizedFogPoint, addFogOperationPolygon]);

  // @date_of_change: 2025-10-08 - Group drag context for multi-selected token movement
  const groupDragContextRef = useRef(null);
  // @date_of_change: 2025-10-09 - Ephemeral visual overrides for group dragging
  // React-Konva will reset manual node.position() on re-render. We keep visual positions in state
  // and pass them to Token render during an active group drag to ensure consistent visuals.
  const [groupDragVisualPositions, setGroupDragVisualPositions] = useState({}); // { [tokenId]: { x, y } }

  useEffect(() => {
    const container = stageContainerRef.current;
    if (!container) return undefined;

    const clearAttentionHold = () => {
      const hold = mapAttentionHoldRef.current;
      if (!hold) return;
      if (hold.pingTimer) window.clearTimeout(hold.pingTimer);
      if (hold.focusTimer) window.clearTimeout(hold.focusTimer);
      if (hold.didEmit) {
        mapAttentionSuppressUntilRef.current = Date.now() + MAP_ATTENTION_CLICK_SUPPRESSION_MS;
        selectionDragRef.current = null;
        setIsSelecting(false);
        setSelectionRect(null);
      }
      mapAttentionHoldRef.current = null;
    };

    const resolveMapAttentionPoint = (event) => {
      const stage = stageRef?.current;
      if (!stage || !event.target?.closest?.('.konvajs-content')) return null;

      stage.setPointersPositions?.(event);
      const pointer = stage.getPointerPosition?.();
      const hit = pointer ? stage.getIntersection?.(pointer) : null;
      const hitId = typeof hit?.id === 'function' ? hit.id() : '';
      const allowedMapIds = new Set(['map-background', 'token-layer-map', 'gamemaster-layer-map']);
      // Holds on tokens, co-located badges, transformer handles, or other
      // interactive shapes retain their existing interaction instead.
      if (hit && !allowedMapIds.has(hitId)) return null;

      const local = getStageLocalPointFromClient(event.clientX, event.clientY);
      const x = Number(local?.x) - Number(mapState.position?.x || 0);
      const y = Number(local?.y) - Number(mapState.position?.y || 0);
      const width = Number(mapState.size?.width);
      const height = Number(mapState.size?.height);
      if (
        !Number.isFinite(x)
        || !Number.isFinite(y)
        || !Number.isFinite(width)
        || !Number.isFinite(height)
        || x < 0
        || y < 0
        || x > width
        || y > height
      ) {
        return null;
      }
      return { x, y };
    };

    const emitAttention = (hold, focusCamera) => {
      if (mapAttentionHoldRef.current !== hold) return;
      hold.didEmit = true;
      mapAttentionSuppressUntilRef.current = Date.now() + MAP_ATTENTION_CLICK_SUPPRESSION_MS;
      selectionDragRef.current = null;
      setIsSelecting(false);
      setSelectionRect(null);
      sendWebSocketMessage('mapPing', {
        sessionId: wsSessionId,
        x: hold.point.x,
        y: hold.point.y,
        focusCamera,
      });
    };

    const handlePointerDown = (event) => {
      const pointerType = String(event.pointerType || 'mouse');
      const isTouchLike = pointerType === 'touch' || pointerType === 'pen';
      if (isTouchLike && (event.isPrimary === false || mapAttentionHoldRef.current)) {
        // A second touch means pan/pinch, never map attention.
        clearAttentionHold();
        return;
      }
      if (
        event.button !== 0
        || !sharedPingAvailable
        || sharedPingActive
        || measurementActive
        || drawingEnabled
        || fogEditMode !== 'none'
        || aoeTargeting.enabled
        || ((targetingEnabled && armedAction) || multiattackSequence)
      ) {
        return;
      }

      const point = resolveMapAttentionPoint(event);
      if (!point) return;

      clearAttentionHold();
      const hold = {
        pointerId: event.pointerId,
        pointerType,
        clientX: event.clientX,
        clientY: event.clientY,
        point,
        didEmit: false,
        pingTimer: null,
        focusTimer: null,
      };

      hold.pingTimer = window.setTimeout(() => {
        emitAttention(hold, !isTouchLike && event.shiftKey === true);
      }, MAP_PING_HOLD_DELAY_MS);

      if (isTouchLike) {
        hold.focusTimer = window.setTimeout(() => {
          emitAttention(hold, true);
        }, MAP_TOUCH_FOCUS_HOLD_DELAY_MS);
      }
      mapAttentionHoldRef.current = hold;
    };

    const handlePointerMove = (event) => {
      const hold = mapAttentionHoldRef.current;
      if (!hold || event.pointerId !== hold.pointerId) return;
      if (Math.hypot(event.clientX - hold.clientX, event.clientY - hold.clientY) > MAP_ATTENTION_MOVE_TOLERANCE_PX) {
        clearAttentionHold();
      }
    };

    const handlePointerEnd = (event) => {
      const hold = mapAttentionHoldRef.current;
      if (!hold || event.pointerId === hold.pointerId) clearAttentionHold();
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerEnd, true);
    container.addEventListener('pointercancel', handlePointerEnd, true);

    return () => {
      clearAttentionHold();
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerEnd, true);
      container.removeEventListener('pointercancel', handlePointerEnd, true);
    };
  }, [
    aoeTargeting.enabled,
    armedAction,
    drawingEnabled,
    fogEditMode,
    getStageLocalPointFromClient,
    mapState.position?.x,
    mapState.position?.y,
    mapState.size?.height,
    mapState.size?.width,
    measurementActive,
    multiattackSequence,
    sendWebSocketMessage,
    setIsSelecting,
    setSelectionRect,
    sharedPingActive,
    sharedPingAvailable,
    stageRef,
    targetingEnabled,
    wsSessionId,
  ]);

  // @date_of_change: 2025-10-08 - Real-time group drag move: mirror base token movement to other selected tokens visually
  const handleTokenDragMove = useCallback((e) => {
    // @date_of_change: 2025-10-09 - Disable group drag visuals during combat
    if (CombatLogManager.combatStarted) return;
    const ctx = groupDragContextRef.current;
    if (!ctx || !ctx.eligibleIds || ctx.eligibleIds.length <= 1) return;
    try {
      const baseNow = e.target.position();
      const deltaX = baseNow.x - ctx.baseStartPos.x;
      const deltaY = baseNow.y - ctx.baseStartPos.y;
      // Build new visual positions for non-base eligible tokens
      const nextPositions = {};
      for (const sid of ctx.eligibleIds) {
        if (sid === ctx.baseId) continue;
        const off = ctx.offsets.get(sid) || { dx: 0, dy: 0 };
        nextPositions[sid] = {
          x: ctx.baseStartPos.x + off.dx + deltaX,
          y: ctx.baseStartPos.y + off.dy + deltaY
        };
      }
      // Update state so Token render uses these positions and React-Konva keeps them stable
      setGroupDragVisualPositions(nextPositions);
    } catch (_) {}
  }, []);

  // Create missing handler functions that were working before refactor
  // @date_of_change: 2025-09-07 - Reconnecting existing functionality per AGENTS.md
  

  // @date_of_change: 2025-09-12 - Wire up Topbar icon-mode actions
  // Provide a functional iconbar for Topbar "icons" mode with real onClick handlers.
  // This replaces the inert default iconbar defined in BattleRoomLayout when not overridden.
  const triggerTopIconAction = (handler) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof handler === 'function') {
      handler(event);
    }
  };

  const suppressTopIconClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCompactFogToggle = () => {
    const rect = compactFogButtonRef.current?.getBoundingClientRect?.();
    if (rect) {
      setCompactFogAnchorRect({
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    }
    handleToggleFogControls();
  };

  const handleFogShapeChange = (shape) => {
    setFogShape(shape);
    // Clear any in-progress polygon/lasso when switching shapes.
    fogPolygonPointsRef.current = [];
    fogLassoPointsRef.current = [];
    fogLassoDragging.current = false;
    setFogPolyPreview(null);
  };

  const topIconbarContent = (
    <div className="battle-room-compact-tools flex min-w-0 max-w-full gap-2 items-center overflow-x-auto">
      {/* Grid visibility toggle */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Toggle Grid"
        onMouseDown={triggerTopIconAction(handleGridToggle)}
        onClick={suppressTopIconClick}
        aria-label="Toggle grid visibility"
      >
        <GridIcon size={16} />
      </button>

      {/* Snap to grid toggle */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Toggle Snap"
        onMouseDown={triggerTopIconAction(handleSnapToggle)}
        onClick={suppressTopIconClick}
        aria-label="Toggle snap to grid"
      >
        <Magnet size={16} />
      </button>

      {/* Fog remains a first-class GM control when the full toolbar collapses.
          The portaled panel becomes a bottom sheet on phones, then yields the
          map after a paint mode is armed. date_of_change: 2026-09-26 */}
      {isGameMaster && (
        <>
          <button
            type="button"
            ref={compactFogButtonRef}
            className="btn-ghost w-8 h-8 p-0"
            title={fogEditMode === 'none'
              ? 'Fog tools'
              : `${fogEditMode === 'reveal' ? 'Reveal' : 'Cover'} ${fogShape} fog tool active`}
            onMouseDown={triggerTopIconAction(handleCompactFogToggle)}
            onClick={suppressTopIconClick}
            aria-label={fogEditMode === 'none'
              ? 'Open fog tools'
              : `Open fog tools. ${fogEditMode} ${fogShape} is active`}
            aria-expanded={fogControlsOpen}
            aria-pressed={fogEditMode !== 'none'}
            data-testid="battle-iconbar-fog"
          >
            <CloudFog size={18} />
          </button>
          {fogControlsOpen && (
            <FogControls
              onClose={handleCloseFogControls}
              triggerRef={compactFogButtonRef}
              anchorRect={compactFogAnchorRect}
              fogOfWar={fogOfWar}
              onFogToggle={toggleFogEnabled}
              onFogOpacityChange={setFogOpacity}
              onFogIgnoreGMToggle={toggleFogIgnoreGM}
              onFogRevealAll={revealAllFog}
              onFogCoverAll={coverAllFog}
              fogEditMode={fogEditMode}
              onFogModeChange={setFogEditMode}
              fogShape={fogShape}
              onFogShapeChange={handleFogShapeChange}
              onFogUndo={undoLastFogOperation}
              canUndoFog={(fogOfWar?.operations?.length ?? 0) > 0}
            />
          )}
        </>
      )}

      {/* Viewer-local label density remains available when the full toolbar
          collapses on narrow screens. date_of_change: 2026-08-25 */}
      <button
        type="button"
        className={showTokenLabels
          ? 'btn-ghost w-8 h-8 p-0 text-[#d0b07a]'
          : 'btn-ghost w-8 h-8 p-0'}
        title={showTokenLabels ? 'Hide token names' : 'Show token names on the map'}
        // Use semantic click activation so mouse, touch, and keyboard operate
        // the compact viewer preference identically. date_of_change: 2026-09-13
        onMouseDown={suppressTopIconClick}
        onClick={triggerTopIconAction(handleToggleTokenLabels)}
        aria-label={showTokenLabels ? 'Hide token names' : 'Show token names on the map'}
        aria-pressed={showTokenLabels}
        data-testid="battle-iconbar-token-labels"
      >
        {showTokenLabels ? <Tag size={16} /> : <EyeOff size={16} />}
      </button>

      {/* Open Asset Browser */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Browse Assets"
        onMouseDown={handleOpenAssetBrowser}
        onClick={suppressTopIconClick}
        aria-label="Open Asset Browser"
      >
        <FolderOpen size={16} />
      </button>

      {/* Put native scene lifecycle beside assets so it stays in the first
          visible compact controls instead of being clipped at phone widths.
          date_of_change: 2026-08-13 */}
      {isGameMaster && (
        <button
          type="button"
          className="btn-ghost w-8 h-8 p-0 disabled:cursor-not-allowed disabled:opacity-50"
          title={wsSessionId ? 'Manage campaign scenes' : 'Join a live BattleRoom to manage campaign scenes'}
          onMouseDown={triggerTopIconAction(() => setSceneManagerOpen(true))}
          onClick={suppressTopIconClick}
          aria-label="Manage campaign scenes"
          disabled={!wsSessionId}
          data-testid="battle-iconbar-scenes"
        >
          <Layers3 size={16} />
        </button>
      )}

      {/* Save Room */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Save Room"
        onMouseDown={triggerTopIconAction(() => {
          if (typeof handleSaveSession === 'function') {
            // @date_of_change 2025-09-15 - Pass BattleRoom state to session save
            const battleRoomState = {
              actionsUsed,
              armedAction,
              selectedTargets,
              multiattackSequence,
              isInMultiattackMode,
              multiattackTarget,
              targetingEnabled,
              currentCombatantId
            };
            handleSaveSession(false, null, battleRoomState);
          }
        })}
        onClick={suppressTopIconClick}
        aria-label="Save Room"
      >
        <Download size={16} />
      </button>

      {/* Zoom Controls */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Zoom In"
        onMouseDown={triggerTopIconAction(handleViewportZoomIn)}
        onClick={suppressTopIconClick}
        aria-label="Zoom In"
      >
        <ZoomIn size={16} />
      </button>
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Zoom Out"
        onMouseDown={triggerTopIconAction(handleViewportZoomOut)}
        onClick={suppressTopIconClick}
        aria-label="Zoom Out"
      >
        <ZoomOut size={16} />
      </button>
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Reset Zoom"
        onMouseDown={triggerTopIconAction(handleViewportZoomReset)}
        onClick={suppressTopIconClick}
        aria-label="Reset Zoom"
      >
        <RefreshCw size={16} />
      </button>

      {/* Measurement Tool toggle */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0"
        title="Toggle Measurement"
        onMouseDown={triggerTopIconAction(handleToggleMeasurement)}
        onClick={suppressTopIconClick}
        aria-label="Toggle Measurement"
      >
        <Ruler size={16} />
      </button>

      {/* Shared ping remains reachable when the full toolbar collapses on a
          compact viewport. date_of_change: 2026-08-11 */}
      <button
        type="button"
        className="btn-ghost w-8 h-8 p-0 disabled:cursor-not-allowed disabled:opacity-50"
        title={sharedPingAvailable ? 'Ping mode. You can also hold the map; Shift+hold focuses everyone.' : 'Join a live BattleRoom to place shared pings'}
        onMouseDown={triggerTopIconAction(handleToggleSharedPing)}
        onClick={suppressTopIconClick}
        aria-label="Place shared map ping"
        aria-pressed={sharedPingActive}
        disabled={!sharedPingAvailable}
        data-testid="battle-iconbar-ping"
      >
        <MapPin size={16} />
      </button>

    </div>
  );

  // @date_of_change: 2025-09-12 - Sidebar icon rails state and handlers (left/right)
  // These allow icon-mode clicks to expand the sidebars and focus specific sections.
  const [leftExpandSection, setLeftExpandSection] = useState(null); // { section: 'initiative'|'actions'|'gm', ts }
  const [rightExpandSection, setRightExpandSection] = useState(null); // { section: 'sheet'|'log'|'chat', ts }
  const [sidebarOpenRequest, setSidebarOpenRequest] = useState(null);

  // Named callbacks kept to avoid HMR/stale reference issues
  const handleLeftIconAction = useCallback((section) => {
    setLeftExpandSection({ section, ts: Date.now() });
  }, []);

  const handleRightIconAction = useCallback((section) => {
    setRightExpandSection({ section, ts: Date.now() });
  }, []);

  const handleGmActionOverrideChange = useCallback((nextOverride) => {
    if (!isGameMaster) return;
    setGmActionOverride({
      enabled: Boolean(nextOverride?.enabled),
      note: String(nextOverride?.note || '')
    });
  }, [isGameMaster]);

  const handleEditCombatLog = useCallback(() => {
    if (!isGameMaster) return;
    const ts = Date.now();
    setCombatLogEditMode(true);
    setRightExpandSection({ section: 'log', ts });
    setSidebarOpenRequest({ side: 'right', ts });
  }, [isGameMaster]);

  const handleTargetingPositionChange = useCallback((position) => {
    setMultiattackTargetingPopup((previous) => ({ ...previous, position }));
  }, [setMultiattackTargetingPopup]);

  const handleOpenSheetPanel = useCallback((tab = 'stats') => {
    // date_of_change: 2026-06-07
    // WHAT: Open the right-side sheet directly from action-panel selections.
    // WHY: Spell slots, weapons, and derived action details are character-sheet data; opening
    // the sheet keeps the action panel from duplicating resources while preserving quick access.
    setRightExpandSection({ section: 'sheet', tab, ts: Date.now() });
  }, []);

  const buildPersistableBattleRoomState = useCallback(() => ({
    actionsUsed,
    armedAction,
    selectedTargets,
    multiattackSequence,
    isInMultiattackMode,
    multiattackTarget,
    targetingEnabled,
    currentCombatantId
  }), [
    actionsUsed,
    armedAction,
    selectedTargets,
    multiattackSequence,
    isInMultiattackMode,
    multiattackTarget,
    targetingEnabled,
    currentCombatantId
  ]);

  const handleSaveCurrentSession = useCallback(async (overwrite = false) => {
    await handleSaveSession(overwrite, null, buildPersistableBattleRoomState());
  }, [buildPersistableBattleRoomState, handleSaveSession]);

  useEffect(() => {
    if (typeof onTestStateChange !== 'function') {
      return;
    }

    onTestStateChange({
      sessionId: wsSessionId || sessionId || null,
      role: role || null,
      isGameMaster,
      activeLayer: normalizedActiveLayer,
      tokenCount: tokens.length,
      sceneAssetCount: sceneAssets.length,
      selectedTokenName: selectedVisibleToken?.name || null,
      showGrid,
      snapToGrid,
      showTokenLabels,
      measurementActive,
      isAssetBrowserOpen,
      sharedPingCount: sharedPings.length,
      lastMapAttentionFocus: sharedPings.length > 0
        ? sharedPings[sharedPings.length - 1]?.focusCamera === true
        : false,
      // The harness needs rendered token coordinates to exercise real canvas
      // gestures. This is test-only observability and is never exposed by the
      // live BattleRoom. date_of_change: 2026-07-26
      ...(testMode ? {
        stageViewport: {
          width: stageSize.width,
          height: stageSize.height,
          x: stagePosition.x,
          y: stagePosition.y,
          scale: stageScale,
          gridSize
        },
        tokens: tokens.map((token) => ({
          id: token.id,
          name: token.name,
          type: token.type,
          initiative: token.initiative,
          initiativeModifier: token.initiativeModifier,
          initiativeRoll: token.initiativeRoll,
          abilities: token.abilities,
          grid: token.grid || null,
          x: token.x,
          y: token.y,
          width: token.width,
          height: token.height,
          sizeCells: token.sizeCells || null
        })),
        sceneAssets: sceneAssets.map((asset) => ({
          assetInstanceId: asset.assetInstanceId,
          name: asset.name,
          layer: asset.layer,
          x: asset.x,
          y: asset.y,
          width: asset.width,
          height: asset.height,
          selected: asset.assetInstanceId === selectedSceneAssetId,
        }))
      } : {})
    });
  }, [
    onTestStateChange,
    wsSessionId,
    sessionId,
    role,
    isGameMaster,
    normalizedActiveLayer,
    tokens.length,
    sceneAssets,
    selectedSceneAssetId,
    selectedVisibleToken?.name,
    showGrid,
    snapToGrid,
    showTokenLabels,
    measurementActive,
    isAssetBrowserOpen,
    sharedPings,
    testMode,
    stageSize.width,
    stageSize.height,
    stagePosition.x,
    stagePosition.y,
    stageScale,
    gridSize,
    tokens
  ]);

  const userInfoTopbarProps = {
    onSaveSession: () => handleSaveCurrentSession(false),
    onLoadSession: handleLoadSessionIntoRoom,
    onOverwriteSession: () => handleSaveCurrentSession(true),
    onGoLive: handleGoLive
  };

  const userInfoTopbar = typeof renderUserInfoTopbar === 'function'
    ? renderUserInfoTopbar(userInfoTopbarProps)
    : <UserInfoTopbar {...userInfoTopbarProps} />;

  // Shared token renderer used on both playable layers. Co-located selections
  // are rendered in a final foreground pass so their full token—not a badge
  // covering it—receives the next drag gesture. date_of_change: 2026-08-25
  const renderSceneToken = (token) => {
    const vis = groupDragVisualPositions[token.id];
    const tokenForRender = vis
      ? {
          ...token,
          x: vis.x,
          y: vis.y,
          ...(gridSize > 0
            ? { grid: { x: Math.round(vis.x / gridSize), y: Math.round(vis.y / gridSize) } }
            : {})
        }
      : token;

    return (
      <Token
        key={token.id}
        token={tokenForRender}
        isSelected={Array.isArray(selectedTokenIds) ? selectedTokenIds.includes(token.id) : (selectedTokenId === token.id)}
        isPrimarySelected={selectedTokenId === token.id}
        onSelect={(e) => logTokenSelection(e, token.id)}
        onChange={handleTokenChange}
        onDragEnd={handleTokenDragEnd}
        onDragStart={handleTokenDragStart}
        onDragMove={handleTokenDragMove}
        getAssetDraggable={getAssetDraggableInLayer}
        canDragToken={canDragTokenGuard}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleTokenMouseEnter}
        onMouseLeave={handleTokenMouseLeave}
        targetingEnabled={!!((targetingEnabled && armedAction) || multiattackSequence)}
        isTargetSelected={selectedTargets.includes(token.id) || multiattackTarget?.id === token.id}
        onToggleTarget={handleToggleTarget}
        shouldSuppressTap={shouldSuppressTokenTap}
        gridSize={gridSize}
        viewScale={stageScale}
        showTokenLabels={showTokenLabels}
      />
    );
  };

  // Main render component with proper BattleRoom layout structure
  // @date_of_change: 2025-09-07 - Fix BattleRoomLayout to use proper props structure
  // @date_of_change: 2025-10-01 - Add UserInfoTopbar above layout for user identity and role display
  return (
    <div className="h-screen flex flex-col" data-testid={testMode ? 'battle-room-root' : undefined}>
      {userInfoTopbar}

      <div className="flex-1 overflow-hidden">
        <BattleRoomLayout
      // @date_of_change 2025-09-20 - Global drag-and-drop support for Roll20-style image dropping
      onDragOver={handleGlobalDragOver}
      onDragLeave={handleGlobalDragLeave}
      onDrop={handleGlobalDrop}
      // Pass components as props to layout system
      topIconbar={topIconbarContent}
      onLeftIconAction={handleLeftIconAction}
      onRightIconAction={handleRightIconAction}
      openSidebarRequest={sidebarOpenRequest}
      leftPanel={
        <LeftPanel
          // Basic token data
          tokens={viewerVisibleTokens}
          allTokens={isGameMasterPresentation ? tokens : viewerVisibleTokens}
          selectedTokenId={selectedTokenId}
          // @date_of_change: 2025-10-09 - Use turn-based currentCombatant, not selection-driven
          currentCombatant={visibleCurrentCombatant}
          onTokenSelect={handleTokenSelectFromPanel}
          // Programmatic expand integration for icon rail clicks
          expandSection={leftExpandSection}
          
          // Layer controls
          layers={layers}
          onLayerChange={updateLayerVisibility}
          
          // Combat state
          // @date_of_change: 2025-10-09 - Use CombatLogManager currentRound instead of hardcoded 1
          round={authoritativeInitiative?.round ?? CombatLogManager.currentRound ?? 1}
          combatStarted={wsSessionId ? authoritativeCombatState?.combatStarted === true : CombatLogManager.combatStarted}
          
          // Action handlers
          // @date_of_change 2025-09-13 - ROUTING FIX: Use router to delegate spells/weapons to correct handlers
          onActionTaken={handleActionTaken}
          onFamiliarAction={handleFamiliarAction}
          onCombatantChange={handleCombatantChange}
          onTokenUpdate={handleTokenUpdate}
          onHitPointAdjustment={handleGmHitPointAdjustment}
          
          // Targeting system integration
          // @date_of_change 2025-09-10 - CRITICAL FIX: Prevent duplicate targeting handlers
          // Only pass targeting callbacks to LeftPanel when popup is NOT visible to avoid double execution
          armedAction={armedAction}
          targetingEnabled={targetingEnabled}
          selectedTargets={selectedTargets}
          selectedTargetsCount={selectedTargets.length}
          onArmAction={(action) => setArmedAction(action)}
          onConfirmArmed={multiattackTargetingPopup.visible ? null : handleConfirmTargets}
          onCancelArmed={multiattackTargetingPopup.visible ? null : handleCancelTargets}

          // Multiattack integration
          multiattackSequence={multiattackSequence}
          multiattackTarget={multiattackTarget}
          isInMultiattackMode={isInMultiattackMode}
          onExecuteMultiattackComponent={handleNextMultiattackComponent}
          onSkipMultiattackComponent={handleNextMultiattackComponent}
          
          // @date_of_change 2026-07-11 - Grid/token context for the pinned targeting
          // popup's advantage/Sneak Attack preview (players: visible tokens only).
          previewCombatState={{
            tokens: isGameMasterPresentation ? tokens : viewerVisibleTokens,
            gridSize,
            gridType,
            feetPerCell,
            round: CombatLogManager.currentRound || 1,
            turn: CombatLogManager.currentTurn || 0
          }}

          // Multiattack targeting popup integration
          pinnedMultiattackTargeting={{
            ...multiattackTargetingPopup,
            visible: multiattackTargetingPopup.visible || (!!targetingEnabled && !!armedAction)
          }}
          onMultiattackTargetingClose={() => setMultiattackTargetingPopup(prev => ({ ...prev, visible: false }))}
          onMultiattackTargetingUpdate={(data) => console.log('Multiattack targeting update:', data)}
          onMultiattackTargetingTogglePin={() => setMultiattackTargetingPopup(prev => ({
            ...prev,
            isPinned: !prev.isPinned,
            visible: true
          }))}
          onStartMultiattack={handleOpenMultiattackTargeting}
          onAddToMultiattack={(action) => console.log('Add to multiattack:', action)}
          onExecuteMultiattack={handleConfirmTargets}
          onClearMultiattack={() => setMultiattackSequence(null)}
          
          // GM Panel integration
          onCombatStateChange={(changes) => console.log('Combat state change:', changes)}

          // @date_of_change 2025-09-15 - Combat reset integration for initiative tracker reset button
          onResetCombat={handleResetCombat}

          // @date_of_change 2025-09-24 - Multiplayer integration props
          connectedPlayers={connectedPlayers}
          currentUser={currentUser}
          isGameMaster={isGameMaster}
          tokenOwnership={tokenOwnership}
          getTokenControlInfo={getTokenControlInfo}
          // Role/ownership gating for actions
          canControlToken={canControlToken}

          // @date_of_change: 2025-10-05 - WebSocket initiative sync
          // Local-only rooms must keep the full initiative workflow available.
          // Passing the WebSocket callback while no session exists makes the
          // tracker interpret the room as disconnected multiplayer and disable
          // reroll/start/turn controls.
          sendInitiativeUpdate={isLocalBattleRoom ? null : sendInitiativeUpdate}
          onLocalInitiativeChange={applyLocalInitiative}
          initiativeExternallyManaged={Boolean(serviceSessionData?.initiativeExternallyManaged)}
          externalInitiativeState={externalInitiativeState}
          authoritativeInitiative={authoritativeInitiative}

          // Dice automation controls for GM panel
          diceSettings={diceSettings}
          onDiceSettingsChange={handleDiceSettingsChange}
          onOpenSheetPanel={handleOpenSheetPanel}
          gmOverride={gmActionOverride}
          onGmOverrideChange={handleGmActionOverrideChange}
          onEditCombatLog={handleEditCombatLog}
        />
      }
      rightPanel={
        <RightPanel
          selectedToken={selectedVisibleToken}
          selectedTokens={selectedVisibleToken ? [selectedVisibleToken] : []}
          tokens={viewerVisibleTokens}
          onCombatantChange={handleCombatantChange}
          onTokenChange={handleTokenUpdate}
          // Programmatic expand integration for icon rail clicks
          expandSection={rightExpandSection}
          pinnedTokenProperties={tokenPropertiesPopup.visible && tokenPropertiesPopup.isPinned}
          onTokenPropertiesClose={() => setTokenPropertiesPopup(prev => ({ ...prev, visible: false }))}
          onTokenPropertiesUpdate={(tokenId, updates) => handleTokenUpdate(tokenId, updates)}
          onTokenSizeChange={handleTokenSizeChange}
          onTokenPropertiesTogglePin={() => setTokenPropertiesPopup(prev => ({ ...prev, isPinned: !prev.isPinned }))}
          // @date_of_change: 2025-10-09 - Pass role and ownership info for gating
          isGameMaster={isGameMaster}
          canControlToken={canControlToken}
          currentUser={currentUser}
          getTokenControlInfo={getTokenControlInfo}
          combatLogEditMode={combatLogEditMode}
          onCombatLogEditModeChange={setCombatLogEditMode}
          authoritativeRewindPoints={isGameMaster && wsSessionId ? (authoritativeRewindPoints || []) : null}
          rewindPending={rewindPending}
          rewindError={rewindError}
          onRefreshRewindPoints={isGameMaster && wsSessionId ? loadAuthoritativeRewindPoints : null}
          onRewindRequest={isGameMaster && wsSessionId ? handleAuthoritativeRewind : null}
          onRewindPreview={isGameMaster && wsSessionId ? previewAuthoritativeRewind : null}
        />
      }
      topTools={
        <Toolbar 
          // Grid controls - all from useBattleRoom hook
          showGrid={showGrid}
          snapToGrid={snapToGrid}
          gridSize={gridSize}
          gridOpacity={gridOpacity}
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          onGridToggle={handleGridToggle}
          onSnapToggle={handleSnapToggle}
          onGridSizeChange={handleGridSizeChange}
          onGridOpacityChange={handleGridOpacityChange}
          onGridWidthChange={handleGridWidthChange}
          onGridHeightChange={handleGridHeightChange}
          
          // Zoom controls - from useBattleRoom hook
          zoom={zoom}
          onZoomIn={handleViewportZoomIn}
          onZoomOut={handleViewportZoomOut}
          onZoomReset={handleViewportZoomReset}
          showTokenLabels={showTokenLabels}
          onToggleTokenLabels={handleToggleTokenLabels}
          
          // Session controls - from useBattleRoom hook
          onSaveSession={() => handleSaveCurrentSession(false)}
          onLoadSession={handleLoadSessionIntoRoom}
          
          // Layer controls - from state manager
          layers={layers}
          activeLayer={normalizedActiveLayer}
          onActiveLayerChange={handleActiveLayerChange}
          onLayerVisibilityChange={updateLayerVisibility}
          onLayerLockChange={updateLayerLocked}
          
          // Asset management - reconnected handlers
          onAssetUpload={handleAssetUpload}
          onOpenAssetBrowser={handleOpenAssetBrowser}
          onOpenSceneManager={() => setSceneManagerOpen(true)}
          sceneManagerAvailable={Boolean(isGameMaster && wsSessionId)}
          
          // Tools - reconnected functionality
          measurementActive={measurementActive}
          onToggleMeasurement={handleToggleMeasurement}
          sharedPingActive={sharedPingActive}
          sharedPingAvailable={sharedPingAvailable}
          onToggleSharedPing={handleToggleSharedPing}
          onOpenMultiattackTargeting={handleOpenMultiattackTargeting}
          movementAvailable={Boolean(isWebSocketConnected && wsSessionId)}
          movementMode={movementMode}
          movementIntent={movementIntent}
          combatActive={Boolean(CombatLogManager.combatStarted)}
          onMovementModeChange={setMovementMode}
          onMovementIntentChange={setMovementIntent}

          // Fog of War controls (GM-only UI in Toolbar)
          isGameMaster={isGameMaster}
          fogOfWar={fogOfWar}
          onFogToggle={toggleFogEnabled}
          onFogOpacityChange={setFogOpacity}
          onFogIgnoreGMToggle={toggleFogIgnoreGM}
          onFogRevealAll={revealAllFog}
          onFogCoverAll={coverAllFog}
          // date_of_change: 2025-09-24 - Fog draw mode controls
          fogEditMode={fogEditMode}
          onFogModeChange={setFogEditMode}
          // GAP-003: shape palette + undo — date_of_change: 2026-05-20
          fogShape={fogShape}
          onFogShapeChange={handleFogShapeChange}
          onFogUndo={undoLastFogOperation}
          canUndoFog={(fogOfWar?.operations?.length ?? 0) > 0}
          fogControlsOpen={fogControlsOpen}
          onToggleFogControls={handleToggleFogControls}
          onCloseFogControls={handleCloseFogControls}
          visionControlsOpen={visionControlsOpen}
          visionPreviewEnabled={isPlayerVisionPreview}
          onToggleVisionControls={() => setVisionControlsOpen((current) => !current)}

          // GAP-002: Drawing layer controls \u2014 date_of_change: 2026-05-20
          drawingEnabled={drawingEnabled}
          onDrawingToggle={() => setDrawingEnabled((prev) => !prev)}
          drawingTool={drawingTool}
          onDrawingToolChange={setDrawingTool}
          drawingColor={drawingColor}
          onDrawingColorChange={setDrawingColor}
          drawingLineWidth={drawingLineWidth}
          onDrawingLineWidthChange={setDrawingLineWidth}
          onDrawingUndo={undoDrawing}
          onDrawingClear={clearDrawings}
          canUndoDrawing={drawings.length > 0}
        />
      }
    >

      {/* Main Stage Area */}
      {/* @date_of_change: 2025-10-05 - Added grey background so tokens outside map are visible */}
      <div id="stage-container" className="stage-container battle-map-viewport" ref={stageContainerRef} style={{ flex: 1, position: 'relative', backgroundColor: '#2a2a2a' }}>
        <RealtimeStatusNotice
          sessionStatus={wsSessionStatus}
          pendingMutationCount={pendingMutationCount + pendingAuthoritativeProjectionCount}
          notice={realtimeNotice}
          onDismiss={() => setRealtimeNotice(null)}
        />
        {isPlayerVisionPreview ? (
          <div className="br-vision-status br-vision-status--preview" role="status" aria-live="polite">
            <span>
              <strong>Player preview</strong>
              {playerVisionPreviewPending
                ? ' · Updating…'
                : ` · ${visionPreview?.visibility?.polygons?.length || 0} sight ${visionPreview?.visibility?.polygons?.length === 1 ? 'source' : 'sources'}`}
            </span>
            <button type="button" onClick={() => handlePlayerVisionPreviewChange(false)}>Exit preview</button>
          </div>
        ) : !isGameMaster && visibilityProjection?.mode === 'basic_los' && (
          <div className="br-vision-status" role="status">Basic line of sight is active. Static Fog remains a separate visibility limit.</div>
        )}
        {movementFeedback && <div className="br-movement-status" role="status" aria-live="polite">{movementFeedback}</div>}
        <BattleRoomInterruptions
          state={authoritativeCombatState}
          tokens={viewerVisibleTokens}
          automaticRolls={isGameMaster ? diceSettings.gmAutomaticRolls : diceSettings.playerAutomaticRolls}
        />
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {mapAttentionAnnouncement}
        </div>
        {isGameMaster && selectedSceneAssetId && (
          <SceneAssetInspector
            anchorRef={stageContainerRef}
            asset={sceneAssets.find((entry) => entry.assetInstanceId === selectedSceneAssetId)}
            onChange={(patch) => handleSceneAssetChange(selectedSceneAssetId, patch)}
            onDelete={() => handleSceneAssetDelete(selectedSceneAssetId)}
            onClose={() => setSelectedSceneAssetId(null)}
          />
        )}
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          onMouseDown={handleStageMouseDown}
          onMouseMove={(e) => { handleStageMouseMove(e); handleStageMouseMoveForStatusBar(e); }}
          onMouseUp={handleStageMouseUp}
          onDblClick={handleStageDblClick}
          // Fog painting must use the same authoritative operation path for a
          // one-finger touch as it does for a mouse gesture. date_of_change: 2026-09-26
          onTouchStart={handleStageMouseDown}
          onTouchMove={handleStageMouseMove}
          onTouchEnd={handleStageMouseUp}
          onDblTap={handleStageDblClick}
          // @date_of_change: 2025-10-07 - Enable pointer-centered wheel zoom on Stage
          onWheel={handleStageWheel}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePosition.x}
          y={stagePosition.y}
        >
          {/* @date_of_change: 2025-10-05 - Background Layer for stage visibility */}
          {/* Grey background so tokens outside map boundaries remain visible to GM and players */}
          <Layer listening={false}>
            <Rect
              x={-10000}
              y={-10000}
              width={20000}
              height={20000}
              fill="#2a2a2a"
            />
          </Layer>

          {/* Map Layer - Background maps and static assets */}
          {layers?.map?.visible && (
            <Layer className="map-layer">
              {/* @date_of_change: 2025-11-06 - Switch to MapArea for map manipulation (rule_reference: AGENTS.md modularization)
                  Previous inline <Image>/<Transformer> implementation retained below as comment for reference. */}
              {mapImageUrl && (
                <>
                  {/**
                   * DEPRECATED INLINE MAP CODE
                   * <Image ... />
                   * <Transformer ... />
                   */}
                  <MapArea
                    mapImage={mapImageUrl}
                    getAssetDraggable={getAssetDraggableInLayer}
                    layer="map"
                    isSelected={Boolean(mapState.isSelected && normalizedActiveLayer === 'map' && !layers?.map?.locked)}
                    onSelect={() => {
                      if (Date.now() < mapAttentionSuppressUntilRef.current) return;
                      if (normalizedActiveLayer === 'map' && !layers?.map?.locked) {
                        setMapState(prev => ({ ...prev, isSelected: true }));
                      }
                    }}
                    onChange={({ x, y, width, height }) => {
                      try {
                        setMapState(prev => ({
                          ...prev,
                          position: { x, y },
                          size: { width, height }
                        }));
                      } catch (err) {
                        console.warn('[BattleRoom] MapArea onChange failed:', err);
                      }
                    }}
                    onDragEnd={(e) => {
                      try {
                        const pos = e?.target?.position ? e.target.position() : { x: e?.target?.x?.(), y: e?.target?.y?.() };
                        if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
                          setMapState(prev => ({ ...prev, position: { x: pos.x, y: pos.y } }));
                        }
                      } catch (err) {
                        console.warn('[BattleRoom] MapArea onDragEnd failed:', err);
                      }
                    }}
                    onContextMenu={(e) => {
                      try {
                        e?.evt?.preventDefault?.();
                        e?.evt?.stopPropagation?.();
                        e.cancelBubble = true;
                        const stage = e?.target?.getStage?.();
                        const pointer = stage?.getPointerPosition?.();
                        handleContextMenu(e, 'map', pointer, 'map', 'map-background');
                      } catch (err) {
                        console.warn('[BattleRoom] MapArea onContextMenu failed:', err);
                      }
                    }}
                    mapPosition={mapState.position}
                    mapSize={mapState.size}
                  />
                </>
              )}
            </Layer>
          )}

          {/* Grid Layer - Grid overlay */}
          {showGrid && (
            <Layer className="grid-layer">
              <Grid
                // date_of_change: 2025-09-24 - FIX: Pass correct stage dimension prop names
                stageWidth={stageSize.width}
                stageHeight={stageSize.height}
                mapWidth={mapState.size.width}
                mapHeight={mapState.size.height}
                gridSize={gridSize}
                // @date_of_change: 2025-10-13 - Pass gridType to renderer
                gridType={gridType}
                gridOpacity={gridOpacity}
                gridWidth={gridWidth}
                gridHeight={gridHeight}
              />
            </Layer>
          )}

          {/* Token Layer Background - Map images for token layer */}
          {layers?.token?.visible && mapImages?.token && tokenLayerMapImage && (
            <Layer className="token-layer-background">
              <Image
                id="token-layer-map"
                image={tokenLayerMapImage}
                width={mapState.size.width}
                height={mapState.size.height}
                x={mapState.position.x}
                y={mapState.position.y}
                draggable={normalizedActiveLayer === 'token' && !layers?.token?.locked}
                onClick={(e) => {
                  if (Date.now() < mapAttentionSuppressUntilRef.current) return;
                  console.log('[BattleRoom] Token layer map clicked');
                  handleMapClick(e);
                  setMapState(prev => ({ ...prev, isSelected: true }));
                }}
                onContextMenu={(e) => {
                  e.evt.preventDefault();
                  e?.evt?.stopPropagation?.();
                  e.cancelBubble = true;
                  const stage = e.target.getStage();
                  const pointer = stage.getPointerPosition();
                  console.log('[BattleRoom] Token layer map right-clicked');
                  handleContextMenu(e, 'token', pointer, 'map', 'token-layer-map');
                }}
                onDragEnd={(e) => {
                  const newPos = e.target.position();
                  setMapState(prev => ({ ...prev, position: { x: newPos.x, y: newPos.y } }));
                }}
              />
            </Layer>
          )}

          {/* Token Layer - Characters and monsters */}
          {layers?.token?.visible && (
            <Layer className="token-layer">
              <Group x={mapState.position.x} y={mapState.position.y}>
                {sceneAssets
                  .filter((asset) => asset.layer === 'token')
                  .map((asset) => (
                    <SceneAssetNode
                      key={asset.assetInstanceId}
                      asset={asset}
                      editable={isGameMaster && normalizedActiveLayer === 'token' && !layers?.token?.locked}
                      selected={selectedSceneAssetId === asset.assetInstanceId}
                      revealHidden={isGameMaster}
                      onSelect={(assetInstanceId) => {
                        setSelectedSceneAssetId(assetInstanceId);
                        setSelectedTokenId(null);
                      }}
                      onChange={(patch) => handleSceneAssetChange(asset.assetInstanceId, patch)}
                      onContextMenu={() => setSelectedSceneAssetId(asset.assetInstanceId)}
                    />
                  ))}
              </Group>
              {tokenLayerPresentation.orderedTokens
                .filter((token) => !tokenLayerPresentation.foregroundTokenIds.includes(token.id))
                .map(renderSceneToken)}
              {tokenLayerPresentation.badges.map(({ token, cell, slotIndex, slotCount }) => (
                <CoLocatedTokenBadge
                  key={`co-located-token-badge:${token.id}`}
                  token={token}
                  cell={cell}
                  slotIndex={slotIndex}
                  slotCount={slotCount}
                  gridSize={gridSize}
                  targetingEnabled={!!((targetingEnabled && armedAction) || multiattackSequence)}
                  isTargetSelected={selectedTargets.includes(token.id) || multiattackTarget?.id === token.id}
                  onActivate={(tokenId, event) => {
                    if ((targetingEnabled && armedAction) || multiattackSequence) {
                      handleToggleTarget(tokenId, event?.evt || event);
                    } else {
                      logTokenSelection(event, tokenId);
                    }
                  }}
                  onMouseEnter={handleTokenMouseEnter}
                  onMouseLeave={handleTokenMouseLeave}
                />
              ))}
              {tokenLayerPresentation.orderedTokens
                .filter((token) => tokenLayerPresentation.foregroundTokenIds.includes(token.id))
                .map(renderSceneToken)}
            </Layer>
          )}

          {/* Gamemaster Layer Background - GM-only map images */}
          {isGameMasterPresentation && layers?.gamemaster?.visible && mapImages?.gamemaster && gamemasterLayerMapImage && (
            <Layer
              className="gamemaster-layer-background"
              listening={normalizedActiveLayer === 'gamemaster'}
            >
              <Image
                id="gamemaster-layer-map"
                image={gamemasterLayerMapImage}
                width={mapState.size.width}
                height={mapState.size.height}
                x={mapState.position.x}
                y={mapState.position.y}
                draggable={normalizedActiveLayer === 'gamemaster' && !layers?.gamemaster?.locked}
                onClick={(e) => {
                  if (Date.now() < mapAttentionSuppressUntilRef.current) return;
                  console.log('[BattleRoom] Gamemaster layer map clicked');
                  handleMapClick(e);
                  setMapState(prev => ({ ...prev, isSelected: true }));
                }}
                onContextMenu={(e) => {
                  e.evt.preventDefault();
                  e?.evt?.stopPropagation?.();
                  e.cancelBubble = true;
                  const stage = e.target.getStage();
                  const pointer = stage.getPointerPosition();
                  console.log('[BattleRoom] Gamemaster layer map right-clicked');
                  handleContextMenu(e, 'gamemaster', pointer, 'map', 'gamemaster-layer-map');
                }}
                onDragEnd={(e) => {
                  const newPos = e.target.position();
                  setMapState(prev => ({ ...prev, position: { x: newPos.x, y: newPos.y } }));
                }}
              />
            </Layer>
          )}

          {/* Gamemaster Layer - GM-only hidden tokens */}
          {isGameMasterPresentation && layers?.gamemaster?.visible && (
            <Layer
              className="gamemaster-layer"
              listening={normalizedActiveLayer === 'gamemaster'}
            >
              <Group x={mapState.position.x} y={mapState.position.y}>
                {sceneAssets
                  .filter((asset) => asset.layer === 'gamemaster')
                  .map((asset) => (
                    <SceneAssetNode
                      key={asset.assetInstanceId}
                      asset={asset}
                      editable={normalizedActiveLayer === 'gamemaster' && !layers?.gamemaster?.locked}
                      selected={selectedSceneAssetId === asset.assetInstanceId}
                      revealHidden
                      onSelect={(assetInstanceId) => {
                        setSelectedSceneAssetId(assetInstanceId);
                        setSelectedTokenId(null);
                      }}
                      onChange={(patch) => handleSceneAssetChange(asset.assetInstanceId, patch)}
                      onContextMenu={() => setSelectedSceneAssetId(asset.assetInstanceId)}
                    />
                  ))}
              </Group>
              {gameMasterLayerPresentation.orderedTokens
                .filter((token) => !gameMasterLayerPresentation.foregroundTokenIds.includes(token.id))
                .map(renderSceneToken)}
              {gameMasterLayerPresentation.badges.map(({ token, cell, slotIndex, slotCount }) => (
                <CoLocatedTokenBadge
                  key={`co-located-gm-token-badge:${token.id}`}
                  token={token}
                  cell={cell}
                  slotIndex={slotIndex}
                  slotCount={slotCount}
                  gridSize={gridSize}
                  targetingEnabled={!!((targetingEnabled && armedAction) || multiattackSequence)}
                  isTargetSelected={selectedTargets.includes(token.id) || multiattackTarget?.id === token.id}
                  onActivate={(tokenId, event) => {
                    if ((targetingEnabled && armedAction) || multiattackSequence) {
                      handleToggleTarget(tokenId, event?.evt || event);
                    } else {
                      logTokenSelection(event, tokenId);
                    }
                  }}
                  onMouseEnter={handleTokenMouseEnter}
                  onMouseLeave={handleTokenMouseLeave}
                />
              ))}
              {gameMasterLayerPresentation.orderedTokens
                .filter((token) => gameMasterLayerPresentation.foregroundTokenIds.includes(token.id))
                .map(renderSceneToken)}
            </Layer>
          )}

          {isGameMasterPresentation && (
            <VisionBarrierLayer
              bounds={{ x: mapState.position.x, y: mapState.position.y, width: mapState.size.width, height: mapState.size.height }}
              walls={visionWalls}
              editing={visionControlsOpen && visionEditing}
              selectedWallId={selectedWallId}
              onSelect={setSelectedWallId}
              onCreate={(geometry) => upsertVisionWall(geometry)}
              onEndpointMove={(wall, endpoint, point) => upsertVisionWall({ ...wall, [endpoint]: point })}
            />
          )}

          {/* Fog of War Layer - renders above map/grid/tokens; below AoE overlay for visibility */}
            {/* date_of_change: 2026-04-28 - Correct GM sees-through logic:
                - Fog always renders when enabled (GM and players both see it)
                - GM Sees Through = On (ignoreGM=true): fog renders at slider opacity so GM peeks through semi-transparently
                - GM Sees Through = Off (ignoreGM=false): GM sees fog at full opacity (1.0), same as players
                - Players always see fog at full opacity (1.0) regardless of GM setting
                - The opacity slider exclusively controls how much the GM sees through when sees-through is On */}
            {fogOfWar?.enabled && (
              <FogOfWarMask
                enabled={fogOfWar.enabled}
                mapPosition={mapState.position}
                mapSize={mapState.size}
                operations={fogOfWar.operations || []}
                opacity={isGameMasterPresentation && fogOfWar.ignoreGM ? (fogOfWar.opacity ?? 0.85) : 1}
              />
            )}

          <VisibilityMaskLayer
            bounds={{ x: mapState.position.x, y: mapState.position.y, width: mapState.size.width, height: mapState.size.height }}
            visibility={isPlayerVisionPreview ? visionPreview?.visibility : visibilityProjection}
            isGameMaster={isGameMasterPresentation}
          />

          <SharedPingLayer
            pings={sharedPings}
            origin={mapState.position}
          />

          {/* Selection Overlay - shows marquee selection rectangle during drag */}
          {isSelecting && selectionRect && (
            <Layer listening={false} className="selection-overlay-layer">
              <Rect
                x={selectionRect.x}
                y={selectionRect.y}
                width={selectionRect.width}
                height={selectionRect.height}
                fill="rgba(196,157,92,0.16)"
                stroke="#e4c783"
                strokeWidth={1}
                dash={[4, 3]}
                opacity={0.9}
              />
            </Layer>
          )}

          {/* Fog Area Selection Preview - Visual feedback during drag (GM only) */}
          {isGameMaster && fogDragPreview && fogEditMode !== 'none' && (
            <Layer className="fog-selection-preview-layer" listening={false}>
              <Rect
                x={fogDragPreview.x}
                y={fogDragPreview.y}
                width={fogDragPreview.width}
                height={fogDragPreview.height}
                fill="transparent"
                stroke={fogEditMode === 'reveal' ? '#48bb78' : '#fc8181'}
                strokeWidth={2}
                dash={[5, 5]}
                listening={false}
                opacity={0.8}
              />
            </Layer>
          )}

          {/* GAP-003: Polygon / lasso live preview — date_of_change: 2026-05-20 */}
          {isGameMaster && fogPolyPreview && fogEditMode !== 'none' && fogPolyPreview.points.length >= 2 && (
            <Layer className="fog-poly-preview-layer" listening={false}>
              <KonvaLine
                points={fogPolyPreview.points}
                closed={false}
                stroke={fogEditMode === 'reveal' ? '#48bb78' : '#fc8181'}
                strokeWidth={2}
                dash={[5, 5]}
                fill={fogEditMode === 'reveal' ? 'rgba(72,187,120,0.12)' : 'rgba(252,129,129,0.12)'}
                listening={false}
                opacity={0.85}
                perfectDrawEnabled={false}
              />
              {/* Closing line back to first point (polygon only) */}
              {fogShape === 'polygon' && fogPolyPreview.closingPoint && fogPolyPreview.points.length >= 4 && (
                <KonvaLine
                  points={[
                    fogPolyPreview.points[fogPolyPreview.points.length - 2],
                    fogPolyPreview.points[fogPolyPreview.points.length - 1],
                    fogPolyPreview.closingPoint.x,
                    fogPolyPreview.closingPoint.y
                  ]}
                  stroke={fogEditMode === 'reveal' ? '#48bb78' : '#fc8181'}
                  strokeWidth={1.5}
                  dash={[3, 4]}
                  listening={false}
                  opacity={0.5}
                  perfectDrawEnabled={false}
                />
              )}
            </Layer>
          )}

          {/* @date_of_change 2025-09-15 - AOE TARGETING OVERLAY */}
          {/* AoE Targeting Layer - Visual overlay for area of effect spells */}
          <Layer className="aoe-targeting-layer">
            <AoETargetingOverlay
              targetPoint={aoeTargeting.targetPoint || aoeTargeting.previewPoint}
              areaOfEffect={aoeTargeting.areaOfEffect}
              gridSize={gridSize}
              // @date_of_change: 2025-10-13 - Use explicit grid context
              feetPerCell={feetPerCell}
              gridType={gridType}
              isVisible={aoeTargeting.enabled}
              direction={aoeTargeting.direction}
              targetMode={aoeTargeting.targetMode}
              isPreview={!aoeTargeting.targetPoint && !!aoeTargeting.previewPoint}
              casterToken={aoeTargeting.caster}
            />
          </Layer>

          {measurementActive && measureStartCell && measureEndCell && (
            <Layer className="measure-overlay-layer" listening={false}>
              <Group x={mapState.position.x} y={mapState.position.y} listening={false}>
                <MeasureOverlay
                  active={measurementActive}
                  startCell={measureStartCell}
                  endCell={measureEndCell}
                  gridSize={gridSize}
                  feetPerCell={feetPerCell}
                  gridType={gridType}
                />
              </Group>
            </Layer>
          )}

        </Stage>
        <div className="sr-only" role="status">{visionConfig.mode === 'basic_los' ? 'Basic dynamic vision is active.' : 'Dynamic vision is off.'}</div>
        {isGameMaster && (
          <VisionBarrierControls
            open={visionControlsOpen}
            vision={visionConfig}
            walls={visionWalls}
            selectedWall={selectedVisionWall}
            editing={visionEditing}
            onEditingChange={setVisionEditing}
            onVisionChange={updateVisionConfig}
            onUpdateWall={upsertVisionWall}
            onDeleteWall={deleteVisionWall}
            previewEnabled={isPlayerVisionPreview}
            previewPending={playerVisionPreviewPending}
            previewOriginCount={visionPreview?.visibility?.polygons?.length || 0}
            previewAvailable={Boolean(wsSessionId && typeof requestBattleRoom === 'function')}
            onPreviewChange={handlePlayerVisionPreviewChange}
            onClose={() => { setVisionControlsOpen(false); setVisionEditing(false); }}
          />
        )}

        {/* GAP-002: Drawing canvas overlay \u2014 absolute HTML canvas above Konva stage, below UI overlays.
            z-20 keeps it above the Konva stage canvas (z-0) but below measurement (z-24) and context menus.
            pointer-events are managed by DrawingCanvas itself via the `enabled` prop.
            date_of_change: 2026-05-20 */}
        {/* GAP-004: Minimap — collapsible, bottom-right corner
             date_of_change: 2026-05-20 */}
        <div className="battle-stage-minimap">
          <button
            type="button"
            className="battle-stage-minimap__toggle"
            onClick={() => setMinimapVisible((v) => !v)}
            title={minimapVisible ? 'Hide minimap' : 'Show minimap'}
            aria-label={minimapVisible ? 'Hide minimap' : 'Show minimap'}
            aria-expanded={minimapVisible}
          >
            {minimapVisible ? 'Hide map' : 'Map'}
          </button>
          <Minimap
            mapImage={mapImageUrl || null}
            tokens={viewerVisibleTokens}
            viewport={{
              x: -stagePosition.x / stageScale,
              y: -stagePosition.y / stageScale,
              width: stageSize.width / stageScale,
              height: stageSize.height / stageScale
            }}
            mapOrigin={mapState?.position || { x: 0, y: 0 }}
            mapSize={mapState?.size?.width > 0 ? mapState.size : { width: 1600, height: 1200 }}
            zoom={stageScale}
            onViewportChange={({ x, y }) => {
              commitStageViewport({
                nextPosition: {
                  x: -x * stageScale,
                  y: -y * stageScale
                }
              });
            }}
            showTokens={true}
            showGrid={showGrid}
            gridSize={gridSize}
            isVisible={minimapVisible}
          />
        </div>

        {isGameMaster && (
          <DrawingCanvas
            enabled={drawingEnabled && fogEditMode === 'none' && !measurementActive && !aoeTargeting.enabled}
            color={drawingColor}
            lineWidth={drawingLineWidth}
            mapRef={stageContainerRef}
            zoom={stageScale}
            mapPosition={stagePosition}
            onDrawingComplete={addDrawing}
            drawings={drawings}
            snapToGrid={snapToGrid}
            gridSize={gridSize}
            drawingTool={drawingTool}
          />
        )}

        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            zIndex: 24,
            pointerEvents: measurementActive && fogEditMode === 'none' && !aoeTargeting.enabled ? 'auto' : 'none',
            cursor: measurementActive && fogEditMode === 'none' && !aoeTargeting.enabled ? 'crosshair' : 'default'
          }}
          onMouseDown={handleMeasurementPointerDown}
          onMouseMove={handleMeasurementPointerMove}
          onMouseUp={handleMeasurementPointerUp}
          onMouseLeave={handleMeasurementPointerUp}
        />

        {/* Context Menu */}
        {contextMenu.isVisible && (
          <LayerContextMenu
            x={contextMenu.position?.x || 0}
            y={contextMenu.position?.y || 0}
            visible={contextMenu.isVisible}
            currentLayer={normalizeBattleLayerId(contextMenu.assetLayer, contextMenu.assetLayer)}
            assetType={contextMenu.assetType}
            assetId={contextMenu.assetId}
            onLayerChange={(newLayer) => {
              const normalizedNewLayer = normalizeBattleLayerId(newLayer, newLayer);
              const normalizedCurrentLayer = normalizeBattleLayerId(contextMenu.assetLayer, contextMenu.assetLayer);
              console.log(`[BattleRoom] Moving asset ${contextMenu.assetId} from ${normalizedCurrentLayer} to ${normalizedNewLayer}`);

              if (contextMenu.assetType === 'map') {
                // @date_of_change 2025-09-18 - Implement map layer reassignment
                const currentMapImage = mapImages[normalizedCurrentLayer];
                if (currentMapImage) {
                  // Move map image from current layer to new layer
                  setMapImages(prev => ({
                    ...prev,
                    [normalizedCurrentLayer]: null, // Clear from old layer
                    [normalizedNewLayer]: currentMapImage // Add to new layer
                  }));
                  console.log(`[BattleRoom] Moved map from ${normalizedCurrentLayer} layer to ${normalizedNewLayer} layer`);
                }
              } else if (contextMenu.assetType === 'token' && contextMenu.assetId) {
                // @date_of_change: 2025-10-08 - Apply to multi-selected tokens if menu opened on a selected token
                const ids = (selectedTokenIds && selectedTokenIds.length > 1 && selectedTokenIds.includes(contextMenu.assetId))
                  ? selectedTokenIds
                  : [contextMenu.assetId];
                ids.forEach(id => handleTokenLayerChange(id, normalizedNewLayer));
              }

              handleContextMenuClose();
            }}
            onDelete={() => {
              console.log(`[BattleRoom] Deleting asset ${contextMenu.assetId} from ${contextMenu.assetLayer}`);
              if (contextMenu.assetType === 'token' && contextMenu.assetId) {
                // @date_of_change: 2025-10-08 - Apply delete to multi-selected tokens when relevant
                const ids = (selectedTokenIds && selectedTokenIds.length > 1 && selectedTokenIds.includes(contextMenu.assetId))
                  ? selectedTokenIds
                  : [contextMenu.assetId];
                ids.forEach(id => handleTokenDelete(id));
              } else if (contextMenu.assetType === 'map') {
                // @date_of_change 2025-09-18 - Add map deletion support
                handleMapDelete();
                setMapState(prev => ({ ...prev, isSelected: false }));
              }
              handleContextMenuClose();
            }}
            onProperties={(tokenId) => {
              console.log(`[BattleRoom] Opening properties for token ${tokenId}`);
              handleTokenPropertiesOpen(tokenId, contextMenu.position);
              handleContextMenuClose();
            }}
            // Role/ownership-gated Properties visibility
            canOpenProperties={(() => {
              if (contextMenu.assetType !== 'token') return false;
              const t = tokens.find(tok => tok.id === contextMenu.assetId);
              return isGameMaster || (t ? !!canControlToken(t.id) : false);
            })()}
            // Ownership management (GM only section will render inside the menu)
            // @date_of_change: 2025-10-05 - Pass ownership controls and player list
            isGameMaster={isGameMaster}
            connectedPlayers={connectedPlayers}
            getTokenControlInfo={getTokenControlInfo}
            onAssignOwnership={handleAssignOwnership}
            onUnassignOwnership={handleUnassignOwnership}
            onClose={handleContextMenuClose}
          />
        )}

        {/* Token Properties Popup */}
        {tokenPropertiesPopup.visible && (() => {
          const token = tokens.find(t => t.id === tokenPropertiesPopup.tokenId);
          return token ? (
            <TokenPropertiesPopup
              token={token}
              visible={tokenPropertiesPopup.visible}
              position={tokenPropertiesPopup.position}
              onClose={handleTokenPropertiesClose}
              onUpdate={handleTokenPropertiesUpdate}
              onTokenSizeChange={handleTokenSizeChange}
              onTogglePin={handleTokenPropertiesTogglePin}
              isGameMaster={isGameMaster}
            />
          ) : null;
        })()}

        {/* Targeting Popup - Shows for both regular targeting and multiattack targeting */}
        {(!multiattackTargetingPopup.isPinned && (multiattackTargetingPopup.visible || (targetingEnabled && armedAction))) && (
          <MultiattackTargetingPopup
            visible={true}
            position={multiattackTargetingPopup.position}
            onPositionChange={handleTargetingPositionChange}
            isPinned={multiattackTargetingPopup.isPinned}
            onClose={() => {
              setMultiattackTargetingPopup(prev => ({ ...prev, visible: false }));
              // @date_of_change 2025-09-10 - CRITICAL FIX: Don't auto-cancel on popup close
              // This was causing multiattackTarget to be cleared during execution
              // Popup close should not cancel targeting state
            }}
            onTogglePin={() => setMultiattackTargetingPopup(prev => ({
              ...prev,
              isPinned: !prev.isPinned,
              visible: true
            }))}
            // Current combatant and action data
            currentCombatant={visibleCurrentCombatant}
            availableActions={visibleCurrentCombatantActions}
            // Targeting state - critical props for execution
            selectedTargets={selectedTargets}
            multiattackTarget={multiattackTarget}
            targetingEnabled={targetingEnabled}
            armedAction={armedAction}
            tokens={isGameMasterPresentation ? tokens : viewerVisibleTokens} // @date_of_change 2026-06-23 - Resolve GM target names without exposing hidden tokens to players
            // @date_of_change 2026-07-11 - Grid/token context so the popup can preview
            // advantage and Sneak Attack adjudication with the engine derivation.
            // Players get viewer-visible tokens only (no hidden-token leakage).
            combatState={{
              tokens: isGameMasterPresentation ? tokens : viewerVisibleTokens,
              gridSize,
              gridType,
              feetPerCell,
              round: CombatLogManager.currentRound || 1,
              turn: CombatLogManager.currentTurn || 0
            }}
            // Targeting callbacks - critical for attack execution
            onArmAction={setArmedAction}
            onConfirmArmed={handleConfirmTargets}
            onCancelArmed={handleCancelTargets}
            onTargetSelect={(tokenId) => handleToggleTarget(tokenId)}
            onTargetDeselect={(tokenId) => handleToggleTarget(tokenId)}
            // Multiattack state
            multiattackSequence={multiattackSequence?.componentActions || []}
            isInMultiattackMode={!!multiattackSequence}
            // Multiattack callbacks
            onStartMultiattack={handleOpenMultiattackTargeting}
            onAddToMultiattack={(action) => console.log('Add to multiattack:', action)}
            onExecuteMultiattack={handleConfirmTargets}
            onClearMultiattack={() => setMultiattackSequence(null)}
            // @date_of_change 2026-07-12 - Inline manual-roll step: when a manual attack/damage
            // roll is pending while this panel is open, show the roll entry fields right here
            // instead of the separate centered ManualRollModal / native window.prompt.
            pendingManualRoll={manualModal.visible ? manualModal : null}
            onManualRollConfirm={(vals) => {
              const req = manualCurrentRef.current;
              if (req && typeof req.resolve === 'function') req.resolve(vals);
              manualCurrentRef.current = null;
              setManualModal({ visible: false, title: '', prompt: '', numDice: 1, dieSize: 20 });
              setTimeout(showNextManual, 0);
            }}
            onManualRollCancel={() => {
              const req = manualCurrentRef.current;
              if (req && typeof req.resolve === 'function') req.resolve([]);
              manualCurrentRef.current = null;
              setManualModal({ visible: false, title: '', prompt: '', numDice: 1, dieSize: 20 });
              setTimeout(showNextManual, 0);
            }}
          />
        )}

        {/* Asset Browser */}
        {isAssetBrowserOpen && (
          <AssetBrowser
            isOpen={isAssetBrowserOpen}
            surface="battle"
            onClose={() => setIsAssetBrowserOpen(false)}
            onAddToken={handleAssetBrowserAddToken}
            activeCampaignId={activeCampaignId}
          />
        )}

        {/* Manual Dice Modal - used when auto-rolls are disabled.
            @date_of_change 2026-07-12 - Suppressed while the Action Targeting popup is open and
            handling this same request inline (see pendingManualRoll above), so the player isn't
            shown two competing roll-entry UIs for one request. */}
        <ManualRollModal
          visible={manualModal.visible && !(!multiattackTargetingPopup.isPinned && (multiattackTargetingPopup.visible || (targetingEnabled && armedAction)))}
          title={manualModal.title}
          prompt={manualModal.prompt}
          numDice={manualModal.numDice}
          dieSize={manualModal.dieSize}
          onCancel={() => {
            const req = manualCurrentRef.current;
            if (req && typeof req.resolve === 'function') req.resolve([]);
            manualCurrentRef.current = null;
            setManualModal({ visible: false, title: '', prompt: '', numDice: 1, dieSize: 20 });
            // display next queued request if any
            setTimeout(showNextManual, 0);
          }}
          onConfirm={(vals) => {
            const req = manualCurrentRef.current;
            if (req && typeof req.resolve === 'function') req.resolve(vals);
            manualCurrentRef.current = null;
            setManualModal({ visible: false, title: '', prompt: '', numDice: 1, dieSize: 20 });
            setTimeout(showNextManual, 0);
          }}
        />

        {/* GM Choice Modal - used for damage-type selection and other finite choices */}
        <ChoiceModal
          visible={choiceModal.visible}
          title={choiceModal.title}
          prompt={choiceModal.prompt}
          options={choiceModal.options}
          onCancel={() => {
            const req = choiceCurrentRef.current;
            if (req && typeof req.resolve === 'function') req.resolve(null);
            choiceCurrentRef.current = null;
            setChoiceModal({ visible: false, title: '', prompt: '', options: [] });
            setTimeout(showNextChoice, 0);
          }}
          onConfirm={(value) => {
            const req = choiceCurrentRef.current;
            if (req && typeof req.resolve === 'function') req.resolve(value);
            choiceCurrentRef.current = null;
            setChoiceModal({ visible: false, title: '', prompt: '', options: [] });
            setTimeout(showNextChoice, 0);
          }}
        />

        {/* Exact narrative/feature intent. This is deliberately separate from
            dice and target resolution: description-only character features
            are handed to GMA without pretending that VCS resolved mechanics.
            date_of_change: 2026-07-18 */}
        <InteractionIntentModal
          {...interactionIntentModal}
          onCancel={() => resolveInteractionIntent(null)}
          onConfirm={resolveInteractionIntent}
        />

        {/* @date_of_change 2025-09-20 - Global drag-and-drop overlay for Roll20-style UX */}
        {dragOverlay && (
          <div className="fixed inset-0 bg-blue-500 bg-opacity-20 border-4 border-dashed border-blue-400 z-50 flex items-center justify-center pointer-events-none">
            <div className="bg-blue-600 text-white px-6 py-4 rounded-lg text-xl font-bold shadow-lg">
              📁 Drop images here to add to {normalizedActiveLayer === 'map' ? 'Map Layer' : 'Token Layer'}
            </div>
          </div>
        )}
        {/* GAP-011: StatusBar — mounted below stage container
             date_of_change: 2026-05-20 */}
        <StatusBar
          currentTool={normalizedActiveLayer === 'map' ? 'pan' : 'select'}
          mousePosition={mousePosition}
          zoom={stageScale}
          selectedTokens={selectedTokenId ? [selectedTokenId] : []}
          selectedDrawings={[]}
          gridSize={gridSize}
          snapToGrid={snapToGrid}
          isConnected={isWebSocketConnected}
          lastSaved={null}
          hasUnsavedChanges={false}
          onSave={() => handleSaveCurrentSession(false)}
          className=""
        />
      </div>

      <SceneLifecycleDialog
        key={`${wsSessionId || 'local'}:${activeSceneId || 'scene'}`}
        visible={sceneManagerOpen}
        sessionId={wsSessionId || ''}
        revision={Number(stateRevision) || 0}
        onRequestClose={() => setSceneManagerOpen(false)}
      />
        </BattleRoomLayout>
      </div>
    </div>
  );
};

// Error Boundary wrapper for BattleRoom
export const WrappedBattleRoom = (props) => (
  <ErrorBoundary
    FallbackComponent={({ error, resetErrorBoundary }) => (
      <div className="error-fallback">
        <h2>Something went wrong in Battle Room</h2>
        <pre>{error?.message}</pre>
        <button onClick={resetErrorBoundary}>Try again</button>
      </div>
    )}
  >
    <BattleRoomBase {...props} />
  </ErrorBoundary>
);

// Default export for BattleRoom with ErrorBoundary wrapper
export default WrappedBattleRoom;
